// // src/emailConfig.ts
// export interface TriggerConfig {
//   subject: string;
//   title: string;
//   logoUrl: string;
//   bodyLines: string[];      // supports {{placeholders}}
//   cta?: { text: string; url: string };
//   footer: string;
// }

import { bR } from "@fullcalendar/core/internal-common";
import { BROKERAGE_FIRM_MESSAGES } from "./messages";

/**
 * This file defines **static** `triggerConfig` entries only (no HTTP at import time).
 *
 * At **send** time, every trigger goes through `sendTriggerEmails` (`emailService.ts`), which resolves
 * `organizationId` (`authCookies.ts` / params / JWT) and loads branding via **`fetchPublicEmailTemplateByOrganizationId`**
 * (re-exported below from `components/emailtempleateapicalls.tsx`), then `generateEmailHTML` in
 * `emailTemplate.ts` merges that branding with the matching row from `triggerConfig` here.
 *
 * Global notification recipient lists (e.g. order/job copies) use **`fetchPublicOrderJobNotificationEmails`**
 * (`GET …/public/settings/general/bykey?organization_id=…&key=order_job.notification_emails`), also re-exported below.
 *
 * Footer always shows Facebook, Instagram, and LinkedIn icons; URLs for those three may come from
 * template `social_links` / `email_social_links_json` when present (`emailTemplate.ts`).
 */
export {
  fetchPublicEmailTemplateByOrganizationId,
  fetchPublicEmailTemplateByUserId,
  fetchPublicGeneralSettingByKey,
  fetchPublicOrderJobNotificationEmails,
  parsePublicSettingNotificationEmails,
  ORDER_JOB_NOTIFICATION_EMAILS_SETTING_KEY,
} from "components/emailtempleateapicalls";

/** Supplier-side email trigger license type: use team template only when explicitly SIGNAGE_TEAM_MEMBER. */
export function resolveSignageSupplierEmailLicenseType(
  licenseType: unknown
): "SIGNAGE_FIRM" | "SIGNAGE_TEAM_MEMBER" {
  const s = String(licenseType || "").trim().toUpperCase();
  if (s === "SIGNAGE_TEAM_MEMBER") return "SIGNAGE_TEAM_MEMBER";
  return "SIGNAGE_FIRM";
}

export interface TriggerConfig {
  subject: string | Record<string,string>; // Can be string or Record<string,string> for license-type-specific subjects
  title: string;
  logoUrl: string;
  bodyLines: string[] | Record<string,string[]>;
   headerBgColor?: string;    
  bodyHtml?: string | Record<string,string>; // Custom HTML body (supports {{placeholders}})
  cta?: { text: string; url: string } | Record<string, { text: string; url: string }>; // Can be object or Record for license-type-specific CTAs
  footer: string;
}

export const triggerConfig: Record<string, TriggerConfig> = {
  // "User Registration": {
  //   subject: " Welcome to Rea!",
  //   title:   "Thanks for signing up!",
  //   logoUrl: "https://i.ibb.co/nXS3cC6/logo.png",  // your ibb.co link
  //   headerBgColor: "#393a3d",       
  //   bodyLines: {
  //     SIGNAGE_FIRM: [
  //       "Hi {{userName}},",
  //       "You’ve successfully created a brokerage team member under your brokerage firm."
  //     ],
  //     BROKERAGE_FIRM: [
  //       "Hi {{userName}},",
  //       "Your signage firm has successfully created a team member under your brokerage."
  //     ],
  //     BROKERAGE_TEAM_MEMBER: [
  //       "Hi {{userName}},",
  //       "Your account has been created successfully.",
  //       "Click below to start exploring:"
  //     ],
  //     AGENT: [
  //       "Hello {{userName}},",
  //       "Your agent account is now active.",
  //       "Log in to begin managing properties:"
  //     ],
  //     SIGNAGE_TEAM_MEMBER: [
  //       "Welcome {{userName}},",
  //       "Your signage team member account has been set up.",
  //       "Check out your dashboard to get started:"
  //     ],
  //   },
  //   cta:    { text: "Go to Dashboard", url: "https://app.example.com/dashboard" },
  //   footer: " RealEsy Inc. All rights reserved."
  // },
  //  "License Registration": {
  //   subject: " Welcome to Rea!",
  //   title:   "Thanks for signing up!",
  
  //   headerBgColor: "#393a3d",     logoUrl: "https://i.ibb.co/nXS3cC6/logo.png",  // your ibb.co link    
  //   bodyLines: {
  //     SIGNAGE_FIRM: [
  //       "Hi {{userName}},",
  //       "You've successfully created a brokerage team member under your brokerage firm."
  //     ],
  //     BROKERAGE_FIRM: [
  //       "Hi {{userName}},",
  //       "Your signage firm has successfully created a team member under your brokerage."
  //     ],
  //     BROKERAGE_TEAM_MEMBER: [
  //       "Hi {{userName}},",
  //       "Your account has been created successfully.",
  //       "Click below to start exploring:"
  //     ],
  //     AGENT: [
  //       "Hello {{userName}},",
  //       "Your agent account is now active.",
  //       "Log in to begin managing properties:"
  //     ],
  //     SIGNAGE_TEAM_MEMBER: [
  //       "Welcome {{userName}},",
  //       "Your signage team member account has been set up.",
  //       "Check out your dashboard to get started:"
  //     ],
  //   },
  //   cta:    { text: "Go to Dashboard", url: "https://app.example.com/dashboard" },
  //   footer: " RealEsy Inc. All rights reserved."
  // },
  "Signage New Team member Activation": {
    subject: {
      SIGNAGE_FIRM: "New Team Member Added - {{teamMemberName}} ",
      SIGNAGE_TEAM_MEMBER: "Your RealEsy Access Is Ready",
      BROKERAGE_FIRM: "New Team Member Activated in Your Brokerage Account",
      BROKERAGE_TEAM_MEMBER: "Welcome to Realesy – Your Team Access Is Ready",
    },
    title:   "Team Member Activation",
    logoUrl: "", // Logo is imported directly in emailTemplate.ts
    headerBgColor: "#ffffff",
    bodyLines: [], // Using bodyHtml instead
    bodyHtml: {
      SIGNAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{adminName}},
        </h2>
        <p class="email-paragraph">
          You've successfully added <strong>{{teamMemberName}}</strong> to your signage company account in RealEsy.
        </p>
        
        <h3 class="email-heading-h3">
          Team Member Details
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Name</td>
              <td class="email-order-details-value-bold-first">{{teamMemberName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Email</td>
              <td class="email-order-details-value-link">{{email}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Role</td>
              <td class="email-order-details-value-bold">{{role}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Added On</td>
              <td class="email-order-details-value-bold">{{addedOn}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          They now have access based on the permissions you've assigned.
        </p>
      `,
      SIGNAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          You've been added to your signage company's RealEsy account as a team member. Your account is now active.
        </p>
        
        <h3 class="email-heading-h3">
         Your Access Details
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Company</td>
              <td class="email-order-details-value-bold-first">{{companyName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Email/Login</td>
              <td class="email-order-details-value-link">{{email}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Temporary Password</td>
              <td class="email-order-details-value-bold">{{password}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Role</td>
              <td class="email-order-details-value-bold">{{role}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          If you need help, please contact your signage admin directly.
        </p>
      `,
      BROKERAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          A new team member has been successfully activated under your brokerage account.
        </p>
        
        <h3 class="email-heading-h3">
           Team Member Details
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Name</td>
              <td class="email-order-details-value-bold-first">{{teamMemberName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Email</td>
              <td class="email-order-details-value-link">{{email}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Role</td>
              <td class="email-order-details-value-bold">{{role}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Office associated</td>
              <td class="email-order-details-value-bold">{{officeName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Added On</td>
              <td class="email-order-details-value-bold">{{addedOn}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          You can review their profile, assign tasks, or manage permissions from your dashboard.
        </p>
      `,
      BROKERAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          Welcome to Realesy — you've been added to the <strong>{{brokerageName}}</strong> team.
        </p>
        <p class="email-paragraph">
          Your account is now active, and you can log in to access your dashboard, view signage orders, and manage your tasks based on your role.
        </p>
        
        <h3 class="email-heading-h3">
           Your Account Details
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Name</td>
              <td class="email-order-details-value-bold-first">{{teamMemberName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Role</td>
              <td class="email-order-details-value-bold">{{role}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Brokerage</td>
              <td class="email-order-details-value-bold">{{brokerageName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Office associated</td>
              <td class="email-order-details-value-bold">{{officeName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Login URL</td>
              <td class="email-order-details-value-link">{{loginUrl}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          Need help getting started? Visit our Help Center or contact support@realesy.com.
        </p>
        <p class="email-paragraph-small">
          We're glad you're here!
        </p>
      `,
    },
    cta: {
      // SIGNAGE_FIRM: { text: "Manage Team", url: "{{manageTeamUrl}}" },
      // SIGNAGE_TEAM_MEMBER: { text: "Log In to RealEsy", url: "{{loginUrl}}" },
      // BROKERAGE_FIRM: { text: "Manage Team", url: "{{manageTeamUrl}}" },
      // BROKERAGE_TEAM_MEMBER: { text: "Log In to RealEsy", url: "{{loginUrl}}" },
      SIGNAGE_FIRM: { text: "Manage Team", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      SIGNAGE_TEAM_MEMBER: { text: "Log In to RealEsy", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      BROKERAGE_FIRM: { text: "Manage Team", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      BROKERAGE_TEAM_MEMBER: { text: " Log In to RealEsy", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
    },
    footer: "RealEsy. All rights reserved."
  },
  "Signage Password changed": {
    subject: {
      SIGNAGE_FIRM: "Your RealEsy Password Has Been Changed",
      SIGNAGE_TEAM_MEMBER: "Your RealEsy Password Has Been Changed",
      BROKERAGE_FIRM: "Your Realesy Password Has Been Updated",
      BROKERAGE_TEAM_MEMBER: "Your Realesy Password Has Been Updated",
    },
    title: "Password Changed",
    logoUrl: "", // Logo is imported directly in emailTemplate.ts
    headerBgColor: "#ffffff",
    bodyLines: [], // Using bodyHtml instead
    bodyHtml: {
      SIGNAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          Your RealEsy password was successfully changed. If you made this change, no further action is required.
        </p>
        
        <h3 class="email-heading-h3">
           Your New Password
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">New password</td>
              <td class="email-order-details-value-bold-first">{{newPassword}}</td>
            </tr>
          </table>
        </div>
        
        <h3 class="email-heading-h3">
          Security Tip:
        </h3>
        <p class="email-paragraph-small">
          If this wasn't you, we recommend resetting your password immediately using the link below:
        </p>
      `,
      SIGNAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          Your RealEsy password was successfully changed. If you made this change, no further action is required.
        </p>
        
        <h3 class="email-heading-h3">
           Your New Password
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">New password</td>
              <td class="email-order-details-value-bold-first">{{newPassword}}</td>
            </tr>
          </table>
        </div>
        
        <h3 class="email-heading-h3">
         Security Tip:
        </h3>
        <p class="email-paragraph-small">
          If this wasn't you, we recommend resetting your password immediately using the link below:
        </p>
      `,
      BROKERAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          This is a confirmation that your Realesy password has been successfully updated.
        </p>
        <p class="email-paragraph">
          If you made this change, no further action is needed.
        </p>
        
        <h3 class="email-heading-h3">
         Security Tip:
        </h3>
        <p class="email-paragraph-small">
          If you didn't update your password, please reset it immediately using the link below and notify your admin if needed.
        </p>
      `,
      BROKERAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          This is a confirmation that your Realesy password has been successfully updated.
        </p>
        <p class="email-paragraph">
          If you made this change, no further action is needed.
        </p>
        
        <h3 class="email-heading-h3">
           Security Tip:
        </h3>
        <p class="email-paragraph-small">
          If you didn't update your password, please reset it immediately using the link below and notify your admin if needed.
        </p>
      `,
    },
    cta: {
      // SIGNAGE_FIRM: { text: "Reset Password Again", url: "{{resetPasswordUrl}}" },
      // SIGNAGE_TEAM_MEMBER: { text: "Reset Password Again", url: "{{resetPasswordUrl}}" },
      // BROKERAGE_FIRM: { text: "Reset Password Again", url: "{{resetPasswordUrl}}" },
      // BROKERAGE_TEAM_MEMBER: { text: "Reset Password Again", url: "{{resetPasswordUrl}}" },
           SIGNAGE_FIRM: { text: "Reset Password Again", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      SIGNAGE_TEAM_MEMBER: { text: "Reset Password Again", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      BROKERAGE_FIRM: { text: "Reset Password Again", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      BROKERAGE_TEAM_MEMBER: { text: "Reset Password Again", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
    },
    footer: "RealEsy. All rights reserved."
  },
  "Signage Warehouse Assigned": {
    subject: {
      SIGNAGE_FIRM: "Warehouse Access Assigned – {{teamMemberName}}",
      SIGNAGE_TEAM_MEMBER: "You've Been Assigned to a Warehouse",
    },
    title: "Warehouse Assigned",
    logoUrl: "", // Logo is imported directly in emailTemplate.ts
    headerBgColor: "#ffffff",
    bodyLines: [], // Using bodyHtml instead
    bodyHtml: {
      SIGNAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{adminFirstName}},
        </h2>
        <p class="email-paragraph">
          You've successfully assigned warehouse access to <strong>{{teamMemberName}}</strong>.
        </p>
        
        <h3 class="email-heading-h3">
           Assignment Details
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Team Member</td>
              <td class="email-order-details-value-bold-first">{{teamMemberName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Email</td>
              <td class="email-order-details-value-link">{{teamMemberEmail}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Assigned Warehouse</td>
              <td class="email-order-details-value-bold">{{warehouseName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Assigned On</td>
              <td class="email-order-details-value-bold">{{assignedOn}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          You can manage access settings anytime in your RealEsy portal.
        </p>
      `,
      SIGNAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          You've been assigned to <strong>{{warehouseName}}</strong> by your signage admin. You now have access to inventory and job-related actions for this location.
        </p>
        
        <h3 class="email-heading-h3">
           Access Granted
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Warehouse</td>
              <td class="email-order-details-value-bold-first">{{warehouseName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Assigned By</td>
              <td class="email-order-details-value-bold">{{adminName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Date</td>
              <td class="email-order-details-value-bold">{{assignedOn}}</td>
            </tr>
          </table>
        </div>
      `,
    },
    // cta: { text: "Manage Warehouse Access", url: "{{manageWarehouseUrl}}" }, // URL will be set dynamically
    cta : { text: "Manage Warehouse Access", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` }, // URL will be set dynamically
    footer: "RealEsy. All rights reserved."
  },
  // "New Warehouse created": {
  //   subject: {
  //     SIGNAGE_FIRM: " New Warehouse Created – {{warehouseName}}",
  //     SIGNAGE_TEAM_MEMBER: " New Warehouse Created – {{warehouseName}}",
  //   },
  //   title: "New Warehouse Created",
  //   logoUrl: "", // Logo is imported directly in emailTemplate.ts
  //   headerBgColor: "#ffffff",
  //   bodyLines: [], // Using bodyHtml instead
  //   bodyHtml: {
  //     SIGNAGE_FIRM: `
  //       <h2 class="email-heading-h2">
  //         Hi {{adminFirstName}},
  //       </h2>
  //       <p class="email-paragraph">
  //         A new warehouse <strong>{{warehouseName}}</strong> has been successfully created in your RealEsy account.
  //       </p>
        
  //       <h3 class="email-heading-h3">
  //          Warehouse Details
  //       </h3>
        
  //       <div class="email-order-details-card">
  //         <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
  //           <tr>
  //             <td class="email-order-details-field-first">Field</td>
  //             <td class="email-order-details-value-first">Info</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Warehouse Name</td>
  //             <td class="email-order-details-value-bold">{{warehouseName}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Address</td>
  //             <td class="email-order-details-value">{{warehouseAddress}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Status</td>
  //             <td class="email-order-details-value-bold">{{warehouseStatus}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Default Warehouse</td>
  //             <td class="email-order-details-value-bold">{{isDefault}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Created On</td>
  //             <td class="email-order-details-value-bold">{{createdOn}}</td>
  //           </tr>
  //         </table>
  //       </div>
        
  //       <p class="email-paragraph-small">
  //         You can manage this warehouse and assign team members to it from your RealEsy portal.
  //       </p>
  //     `,
  //     SIGNAGE_TEAM_MEMBER: `
  //       <h2 class="email-heading-h2">
  //         Hi {{adminFirstName}},
  //       </h2>
  //       <p class="email-paragraph">
  //         A new warehouse <strong>{{warehouseName}}</strong> has been successfully created in your RealEsy account.
  //       </p>
        
  //       <h3 class="email-heading-h3">
  //          Warehouse Details
  //       </h3>
        
  //       <div class="email-order-details-card">
  //         <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
  //           <tr>
  //             <td class="email-order-details-field-first">Field</td>
  //             <td class="email-order-details-value-first">Info</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Warehouse Name</td>
  //             <td class="email-order-details-value-bold">{{warehouseName}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Address</td>
  //             <td class="email-order-details-value">{{warehouseAddress}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Status</td>
  //             <td class="email-order-details-value-bold">{{warehouseStatus}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Default Warehouse</td>
  //             <td class="email-order-details-value-bold">{{isDefault}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Created On</td>
  //             <td class="email-order-details-value-bold">{{createdOn}}</td>
  //           </tr>
  //         </table>
  //       </div>
        
  //       <p class="email-paragraph-small">
  //         You can manage this warehouse and assign team members to it from your RealEsy portal.
  //       </p>
  //     `,
  //   },
  //   cta: { text: "Manage Warehouses", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}/signage/warehouses` },
  //   footer: "RealEsy. All rights reserved."
  // },
  // "Signage Agent license activated": {
  //   subject: {
  //     SIGNAGE_FIRM: "Agent License Activated – {{agentName}}",
  //     SIGNAGE_TEAM_MEMBER: "Agent License Activated – {{agentName}}",
  //     BROKERAGE_FIRM: "Agent License Activated",
  //     BROKERAGE_TEAM_MEMBER: "Agent License Activated",
  //     AGENT: " Your Realesy Agent License Is Now Active",
  //     BROKERAGE_AGENT: " Your Realesy Agent License Is Now Active",
  //   },
  //   title: "Agent License Activated",
  //   logoUrl: "", // Logo is imported directly in emailTemplate.ts
  //   headerBgColor: "#ffffff",
  //   bodyLines: [], // Using bodyHtml instead
  //   bodyHtml: {
  //     SIGNAGE_FIRM: `
  //       <h2 class="email-heading-h2">
  //         Hi {{firstName}},
  //       </h2>
  //       <p class="email-paragraph">
  //         An agent has activated their RealEsy license and is now eligible to place signage orders with your company (if linked).
  //       </p>
        
  //       <h3 class="email-heading-h3">
  //          Agent Activation Summary
  //       </h3>
        
  //       <div class="email-order-details-card">
  //         <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
  //           <tr>
  //             <td class="email-order-details-field-first">Field</td>
  //             <td class="email-order-details-value-bold-first">Info</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Agent Name</td>
  //             <td class="email-order-details-value-bold">{{agentName}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Email</td>
  //             <td class="email-order-details-value-link">{{agentEmail}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Activated On</td>
  //             <td class="email-order-details-value-bold">{{activatedOn}}</td>
  //           </tr>
  //         </table>
  //       </div>
  //     `,
  //     SIGNAGE_TEAM_MEMBER: `
  //       <h2 class="email-heading-h2">
  //         Hi {{firstName}},
  //       </h2>
  //       <p class="email-paragraph">
  //         An agent has activated their RealEsy license and is now eligible to place signage orders with your company (if linked).
  //       </p>
        
  //       <h3 class="email-heading-h3">
  //          Agent Activation Summary
  //       </h3>
        
  //       <div class="email-order-details-card">
  //         <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
  //           <tr>
  //             <td class="email-order-details-field-first">Field</td>
  //             <td class="email-order-details-value-first">Info</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Agent Name</td>
  //             <td class="email-order-details-value-bold">{{agentName}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Email</td>
  //             <td class="email-order-details-value-link">{{agentEmail}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Activated On</td>
  //             <td class="email-order-details-value-bold">{{activatedOn}}</td>
  //           </tr>
  //         </table>
  //       </div>
  //     `,
  //     BROKERAGE_FIRM: `
  //       <h2 class="email-heading-h2">
  //         Hi {{firstName}},
  //       </h2>
  //       <p class="email-paragraph">
  //         An agent under your brokerage has just activated their Realesy license.
  //       </p>
        
  //       <h3 class="email-heading-h3">
  //          Agent Details
  //       </h3>
        
  //       <div class="email-order-details-card">
  //         <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
  //           <tr>
  //             <td class="email-order-details-field-first">Name</td>
  //             <td class="email-order-details-value-bold-first">{{agentName}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Email</td>
  //             <td class="email-order-details-value-link">{{agentEmail}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Activated On</td>
  //             <td class="email-order-details-value-bold">{{activatedOn}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Office</td>
  //             <td class="email-order-details-value-bold">{{officeName}}</td>
  //           </tr>
  //         </table>
  //       </div>
        
  //       <p class="email-paragraph-small">
  //         You can now view their activity and assist them as needed via your dashboard.
  //       </p>
  //     `,
  //     BROKERAGE_TEAM_MEMBER: `
  //       <h2 class="email-heading-h2">
  //         Hi {{firstName}},
  //       </h2>
  //       <p class="email-paragraph">
  //         An agent under your brokerage has just activated their Realesy license.
  //       </p>
        
  //       <h3 class="email-heading-h3">
  //          Agent Details
  //       </h3>
        
  //       <div class="email-order-details-card">
  //         <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
  //           <tr>
  //             <td class="email-order-details-field-first">Name</td>
  //             <td class="email-order-details-value-bold-first">{{agentName}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Email</td>
  //             <td class="email-order-details-value-link">{{agentEmail}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Activated On</td>
  //             <td class="email-order-details-value-bold">{{activatedOn}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Office</td>
  //             <td class="email-order-details-value-bold">{{officeName}}</td>
  //           </tr>
  //         </table>
  //       </div>
        
  //       <p class="email-paragraph-small">
  //         You can now view their activity and assist them as needed via your dashboard.
  //       </p>
  //     `,
  //     AGENT: `
  //       <h2 class="email-heading-h2">
  //         Hi {{firstName}},
  //       </h2>
  //       <p class="email-paragraph">
  //         Welcome to RealEsy! Your agent license has been successfully activated — you now have full access to place signage orders, manage installations, and track your listings.
  //       </p>
        
  //       <h3 class="email-heading-h3">
  //          Your Account Summary
  //       </h3>
        
  //       <div class="email-order-details-card">
  //         <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
  //           <tr>
  //             <td class="email-order-details-field-first">Field</td>
  //             <td class="email-order-details-value-first">Value</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Agent Name</td>
  //             <td class="email-order-details-value-bold">{{agentName}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Email/Login</td>
  //             <td class="email-order-details-value-link">{{agentEmail}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Activated On</td>
  //             <td class="email-order-details-value-bold">{{activatedOn}}</td>
  //           </tr>
  //         </table>
  //       </div>
        
  //       <p class="email-paragraph-small">
  //         If you need help linking a signage vendor, your brokerage or vendor support team can assist.
  //       </p>
  //     `,
  //     BROKERAGE_AGENT: `
  //       <h2 class="email-heading-h2">
  //         Hi {{firstName}},
  //       </h2>
  //       <p class="email-paragraph">
  //         Your Realesy Agent license is now active! You can start placing sign requests, tracking installations, and managing your listing assets right away.
  //       </p>
        
  //       <h3 class="email-heading-h3">
  //          Account Details
  //       </h3>
        
  //       <div class="email-order-details-card">
  //         <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
  //           <tr>
  //             <td class="email-order-details-field-first">Agent Name</td>
  //             <td class="email-order-details-value-bold-first">{{agentName}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Brokerage</td>
  //             <td class="email-order-details-value-bold">{{brokerageName}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Login URL</td>
  //             <td class="email-order-details-value-link">{{loginUrl}}</td>
  //           </tr>
  //         </table>
  //       </div>
        
  //       <p class="email-paragraph-small">
  //         Need help getting started? Visit your dashboard or reach out to your brokerage office for support.
  //       </p>
  //     `,
  //   },
  //   cta: {
  //     BROKERAGE_FIRM: { text: "View Agent Details", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}/signage/agents` },
  //     BROKERAGE_TEAM_MEMBER: { text: "View Agent Details", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}/signage/agents` },
  //     AGENT: { text: "Get Started with My Orders", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}/agent/orders` },
  //   },
  //   footer: "RealEsy. All rights reserved."
  // },
  // "Signage Agent license deactivated": {
  //   subject: {
  //     SIGNAGE_FIRM: "️ Agent License Deactivated",
  //     SIGNAGE_TEAM_MEMBER: "️ Agent License Deactivated",
  //     BROKERAGE_FIRM: "️ Agent License Deactivated",
  //     BROKERAGE_TEAM_MEMBER: "️ Agent License Deactivated",
  //     AGENT: "️ Your RealEsy License Has Been Deactivated",
  //     BROKERAGE_AGENT: "️ Your RealEsy License Has Been Deactivated",
  //   },
  //   title: "Agent License Deactivated",
  //   logoUrl: "", // Logo is imported directly in emailTemplate.ts
  //   headerBgColor: "#ffffff",
  //   bodyLines: [], // Using bodyHtml instead
  //   bodyHtml: {
  //     SIGNAGE_FIRM: `
  //       <h2 class="email-heading-h2">
  //         Hi {{adminFirstName}},
  //       </h2>
  //       <p class="email-paragraph">
  //         An agent's RealEsy license has been deactivated. They will no longer be able to place signage orders until the license is reactivated.
  //       </p>
        
  //       <h3 class="email-heading-h3">
  //          Agent Details
  //       </h3>
        
  //       <div class="email-order-details-card">
  //         <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
  //           <tr>
  //             <td class="email-order-details-field-first">Name</td>
  //             <td class="email-order-details-value-bold-first">{{agentName}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Email</td>
  //             <td class="email-order-details-value-link">{{agentEmail}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Deactivated On</td>
  //             <td class="email-order-details-value-bold">{{deactivatedOn}}</td>
  //           </tr>
  //         </table>
  //       </div>
  //     `,
  //     SIGNAGE_TEAM_MEMBER: `
  //       <h2 class="email-heading-h2">
  //         Hi {{firstName}},
  //       </h2>
  //       <p class="email-paragraph">
  //         An agent's RealEsy license has been deactivated. They will no longer be able to place signage orders until the license is reactivated.
  //       </p>
        
  //       <h3 class="email-heading-h3">
  //          Agent Details
  //       </h3>
        
  //       <div class="email-order-details-card">
  //         <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
  //           <tr>
  //             <td class="email-order-details-field-first">Name</td>
  //             <td class="email-order-details-value-bold-first">{{agentName}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Email</td>
  //             <td class="email-order-details-value-link">{{agentEmail}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Deactivated On</td>
  //             <td class="email-order-details-value-bold">{{deactivatedOn}}</td>
  //           </tr>
  //         </table>
  //       </div>
  //     `,
  //     BROKERAGE_FIRM: `
  //       <h2 class="email-heading-h2">
  //         Hi {{firstName}},
  //       </h2>
  //       <p class="email-paragraph">
  //         An agent's RealEsy license has been deactivated. They will no longer be able to place signage orders until the license is reactivated.
  //       </p>
        
  //       <h3 class="email-heading-h3">
  //          Agent Details
  //       </h3>
        
  //       <div class="email-order-details-card">
  //         <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
  //           <tr>
  //             <td class="email-order-details-field-first">Name</td>
  //             <td class="email-order-details-value-bold-first">{{agentName}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Email</td>
  //             <td class="email-order-details-value-link">{{agentEmail}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Deactivated On</td>
  //             <td class="email-order-details-value-bold">{{deactivatedOn}}</td>
  //           </tr>
  //         </table>
  //       </div>
  //     `,
  //     BROKERAGE_TEAM_MEMBER: `
  //       <h2 class="email-heading-h2">
  //         Hi {{firstName}},
  //       </h2>
  //       <p class="email-paragraph">
  //         An agent's RealEsy license has been deactivated. They will no longer be able to place signage orders until the license is reactivated.
  //       </p>
        
  //       <h3 class="email-heading-h3">
  //          Agent Details
  //       </h3>
        
  //       <div class="email-order-details-card">
  //         <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
  //           <tr>
  //             <td class="email-order-details-field-first">Name</td>
  //             <td class="email-order-details-value-bold-first">{{agentName}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Email</td>
  //             <td class="email-order-details-value-link">{{agentEmail}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Deactivated On</td>
  //             <td class="email-order-details-value-bold">{{deactivatedOn}}</td>
  //           </tr>
  //         </table>
  //       </div>
  //     `,
  //     AGENT: `
  //       <h2 class="email-heading-h2">
  //         Hi {{firstName}},
  //       </h2>
  //       <p class="email-paragraph">
  //         Your RealEsy agent license has been deactivated. You will no longer be able to place new signage orders until your license is reactivated by your signage admin.
  //       </p>
        
  //       <h3 class="email-heading-h3">
  //         Deactivation Details
  //       </h3>
        
  //       <div class="email-order-details-card">
  //         <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
  //           <tr>
  //             <td class="email-order-details-field-first">Agent Name</td>
  //             <td class="email-order-details-value-bold-first">{{agentName}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Email/Login</td>
  //             <td class="email-order-details-value-link">{{agentEmail}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Deactivated On</td>
  //             <td class="email-order-details-value-bold">{{deactivatedOn}}</td>
  //           </tr>
  //         </table>
  //       </div>
        
  //       <p class="email-paragraph-small">
  //         If you believe this was done in error, please contact your signage admin or brokerage support team.
  //       </p>
  //     `,
  //     BROKERAGE_AGENT: `
  //       <h2 class="email-heading-h2">
  //         Hi {{firstName}},
  //       </h2>
  //       <p class="email-paragraph">
  //         Your RealEsy agent license has been deactivated. You will no longer be able to place new signage orders until your license is reactivated.
  //       </p>
        
  //       <h3 class="email-heading-h3">
  //         Deactivation Details
  //       </h3>
        
  //       <div class="email-order-details-card">
  //         <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
  //           <tr>
  //             <td class="email-order-details-field-first">Agent Name</td>
  //             <td class="email-order-details-value-bold-first">{{agentName}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Email/Login</td>
  //             <td class="email-order-details-value-link">{{agentEmail}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Deactivated On</td>
  //             <td class="email-order-details-value-bold">{{deactivatedOn}}</td>
  //           </tr>
  //         </table>
  //       </div>
        
  //       <p class="email-paragraph-small">
  //         If you believe this was done in error, please contact your brokerage support team.
  //       </p>
  //     `,
  //   },
  //   footer: "RealEsy. All rights reserved."
  // },
  "Signage Sales order": {
    subject: {
      BROKERAGE_FIRM: "Order Created on Your Behalf – {{orderId}}",
      BROKERAGE_TEAM_MEMBER: "Order Created on Your Behalf – {{orderId}}",
      AGENT: "Signage Order Created for You – {{orderId}}",
      BROKERAGE_AGENT: "Signage Order Created for You – {{orderId}}",
    },
    title: "Sales Order Created",
    logoUrl: "", // Logo is imported directly in emailTemplate.ts
    headerBgColor: "#ffffff",
    bodyLines: [], // Using bodyHtml instead
    bodyHtml: {
      SIGNAGE_FIRM: `
        <p class="email-paragraph">
          Hi {{First Name}},
        </p>
        <p class="email-paragraph">
          Sales order {{Order ID}} has been Created. Please review the latest details below:
        </p>
        <h3 class="email-heading-h3">
          Order Summary
        </h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{Order Number}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Updated By</td>
              <td class="email-order-details-value-bold">{{Signage Admin Name}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Update Date</td>
              <td class="email-order-details-value-bold">{{Update Date}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Changes Made</td>
              <td class="email-order-details-value-bold">{{Changes Made}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent (if any)</td>
              <td class="email-order-details-value-bold">{{Agent Name}}</td>
            </tr>
          </table>
        </div>
        <p class="email-paragraph">
          <a href="{{View Updated Order}}" class="email-button"> View Order</a>
        </p>
      `,
      SIGNAGE_TEAM_MEMBER: `
        <p class="email-paragraph">
          Hi {{First Name}},
        </p>
        <p class="email-paragraph">
          Sales order {{Order ID}} has been created. Please review the latest details below:
        </p>
        <h3 class="email-heading-h3">
           Updated Order Summary
        </h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{Order Number}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Updated By</td>
              <td class="email-order-details-value-bold">{{Signage Admin Name}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Update Date</td>
              <td class="email-order-details-value-bold">{{Update Date}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Changes Made</td>
              <td class="email-order-details-value-bold">{{Changes Made}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent (if any)</td>
              <td class="email-order-details-value-bold">{{Agent Name}}</td>
            </tr>
          </table>
        </div>
        <p class="email-paragraph">
          <a href="{{View Updated Order}}" class="email-button"> View Order</a>
        </p>
      `,
      BROKERAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          An order has been created on your behalf by <span style="font-weight: bold;">{{signageCompanyName}}</span>., based on a phone call, email, or offline request.
        </p>
        
        <h3 class="email-heading-h3">
          Order Details
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Entered By</td>
              <td class="email-order-details-value">{{enteredBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Order Method</td>
              <td class="email-order-details-value">{{orderMethod}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent</td>
              <td class="email-order-details-value">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Requested Date</td>
              <td class="email-order-details-value">{{requestedDate}}</td>
            </tr>
          </table>
        </div>      
        {{lineItems}}
        
      
      `,
          //  {{orderTotalsHtml}}

        // <p class="email-paragraph-small">
        //   You can track the status and progress of this order directly from your RealEsy portal.
        // </p>
         BROKERAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          An order has been created on your behalf by <span style="font-weight: bold;">{{signageCompanyName}}</span>., based on a phone call, email, or offline request.
        </p>
        
        <h3 class="email-heading-h3">
          Order Details
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Entered By</td>
              <td class="email-order-details-value">{{enteredBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Order Method</td>
              <td class="email-order-details-value">{{orderMethod}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent</td>
              <td class="email-order-details-value">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Requested Date</td>
              <td class="email-order-details-value">{{requestedDate}}</td>
            </tr>
          </table>
        </div>        
        {{lineItems}}
        
       
      `,

      //  <p class="email-paragraph-small">
      //     You can track the status and progress of this order directly from your RealEsy portal.
      //   </p>
      AGENT: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          {{signageCompanyName}} has created a signage order on your behalf following your request via {{orderMethod}}.
        </p>
        
        <h3 class="email-heading-h3">
         Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Scheduled By</td>
              <td class="email-order-details-value">{{scheduledBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Requested Date</td>
              <td class="email-order-details-value">{{requestedDate}}</td>
            </tr>
          </table>
        </div>
        
        {{lineItems}}
        
        <p class="email-paragraph-small">
          You will receive updates as the order progresses.
        </p>
      `,
       BROKERAGE_AGENT: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          {{signageCompanyName}} has created a signage order on your behalf following your request via {{orderMethod}}.
        </p>
        
        <h3 class="email-heading-h3">
         Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Scheduled By</td>
              <td class="email-order-details-value">{{scheduledBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Requested Date</td>
              <td class="email-order-details-value">{{requestedDate}}</td>
            </tr>
          </table>
        </div>
        {{lineItems}}
        
        <p class="email-paragraph-small">
          You will receive updates as the order progresses.
        </p>
      `,
    },
    cta: {
      // BROKERAGE_FIRM: { text: "View Order", url: "{{viewOrderUrl}}" },
      // AGENT: { text: "Track My Orders", url: "{{trackOrdersUrl}}" },
      // BROKERAGE_AGENT: { text: "Track My Orders", url: "{{trackOrdersUrl}}" },
      //  BROKERAGE_FIRM: { text: "View Order", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}`},
      // AGENT: { text: "Track My Orders", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      // BROKERAGE_AGENT: { text: "Track My Orders", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      // BROKERAGE_TEAM_MEMBER: { text: "View Order", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}`},
    },
    footer: "RealEsy. All rights reserved."
  },
  "Signage Payment applied for order": {
    subject: "Payment Received for Sales Order {{orderId}}",
    title: "Payment Applied for Order",
    logoUrl: "", // Logo is imported directly in emailTemplate.ts
    headerBgColor: "#ffffff",
    bodyLines: [], // Using bodyHtml instead
    bodyHtml: `
      <h2 class="email-heading-h2">
        Hi {{firstName}},
      </h2>
      <p class="email-paragraph">
        We've applied a payment to the following sales order:
      </p>
      
      <h3 class="email-heading-h3">
         Order Summary
      </h3>
      
      <div class="email-order-details-card">
        <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
            <td class="email-order-details-field-first">Order ID</td>
            <td class="email-order-details-value-first">{{orderId}}</td>
          </tr>
          <tr>
            <td class="email-order-details-field">Agent Name</td>
            <td class="email-order-details-value">{{agentName}}</td>
          </tr>
          <tr>
            <td class="email-order-details-field">Brokerage Firm</td>
            <td class="email-order-details-value">{{brokerageFirm}}</td>
          </tr>
          <tr>
            <td class="email-order-details-field">Payment Applied By</td>
            <td class="email-order-details-value">{{paymentAppliedBy}}</td>
          </tr>
          <tr>
            <td class="email-order-details-field">Payment Amount</td>
            <td class="email-order-details-value">{{paymentAmount}}</td>
          </tr>
          <tr>
            <td class="email-order-details-field">Payment Method</td>
            <td class="email-order-details-value">{{paymentMethod}}</td>
          </tr>
         
        </table>
      </div>
    `,
    //  <tr>
    //         <td class="email-order-details-field">Payment Date</td>
    //         <td class="email-order-details-value">{{paymentDate}}</td>
    //       </tr>
    //  <tr>
    //         <td class="email-order-details-field">Current Balance</td>
    //         <td class="email-order-details-value">{{currentBalance}}</td>
    //       </tr>
    // cta: { text: "View Order Details", url: "{{viewOrderUrl}}" },
    footer: "RealEsy. All rights reserved."
  },
  "Public Signage Payment applied for order": {
    subject: "Payment Received for Sales Order {{orderId}}",
    title: "Payment Applied for Order",
    logoUrl: "", // Logo is imported directly in emailTemplate.ts
    headerBgColor: "#ffffff",
    bodyLines: [], // Using bodyHtml instead
    bodyHtml: `
      <h2 class="email-heading-h2">
        Hi {{firstName}},
      </h2>
      <p class="email-paragraph">
        We've applied a payment to the following sales order:
      </p>
      
      <h3 class="email-heading-h3">
         Order Summary
      </h3>
      
      <div class="email-order-details-card">
        <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
            <td class="email-order-details-field-first">Order ID</td>
            <td class="email-order-details-value-first">{{orderId}}</td>
          </tr>
          <tr>
            <td class="email-order-details-field">Agent Name</td>
            <td class="email-order-details-value">{{agentName}}</td>
          </tr>
          <tr>
            <td class="email-order-details-field">Brokerage Firm</td>
            <td class="email-order-details-value">{{brokerageFirm}}</td>
          </tr>
          <tr>
            <td class="email-order-details-field">Payment Applied By</td>
            <td class="email-order-details-value">{{paymentAppliedBy}}</td>
          </tr>
          <tr>
            <td class="email-order-details-field">Payment Amount</td>
            <td class="email-order-details-value">{{paymentAmount}}</td>
          </tr>
          <tr>
            <td class="email-order-details-field">Payment Method</td>
            <td class="email-order-details-value">{{paymentMethod}}</td>
          </tr>
         
        </table>
      </div>
    `,
    //  <tr>
    //         <td class="email-order-details-field">Payment Date</td>
    //         <td class="email-order-details-value">{{paymentDate}}</td>
    //       </tr>
    //  <tr>
    //         <td class="email-order-details-field">Current Balance</td>
    //         <td class="email-order-details-value">{{currentBalance}}</td>
    //       </tr>
    // cta: { text: "View Order Details", url: "{{viewOrderUrl}}" },
    footer: "RealEsy. All rights reserved."
  },
    // <p class="email-paragraph-small">
    //     You can view the updated invoice or download receipts from your RealEsy portal.
    //   </p>
  
  "Signage Sales order status update": {
    subject: {
      BROKERAGE_FIRM: "{{statusSubject}}",
      BROKERAGE_TEAM_MEMBER: "{{statusSubject}}",
      BROKERAGE_AGENT: "{{statusSubject}}",
      AGENT: "{{statusSubject}}",
    },
    title: "Order Shipped",
    logoUrl: "", // Logo is imported directly in emailTemplate.ts
    headerBgColor: "#ffffff",
    bodyLines: [], // Using bodyHtml instead
    bodyHtml: {
      BROKERAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          The signage {{statusSubject}}.
        </p>
        
        <h3 class="email-heading-h3">
          Shipping Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Shipped By</td>
              <td class="email-order-details-value">{{shippedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Shipped On</td>
              <td class="email-order-details-value">{{shippedOn}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Shipping Address</td>
              <td class="email-order-details-value">{{shippingAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Tracking Number</td>
              <td class="email-order-details-value tracking">{{trackingNumberHtml}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent</td>
              <td class="email-order-details-value">{{agentName}}</td>
            </tr>
          </table>
        </div>
        
        {{lineItems}}
      `,
        // by {{signageCompanyName}}.
        // <p class="email-paragraph-small">
        //   You can monitor the status and delivery confirmation in your RealEsy portal.
        // </p>
        // as shipped by {{signageCompanyName}}
         BROKERAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          The signage order {{orderId}} has been marked.
        </p>
        
        <h3 class="email-heading-h3">
           Shipping Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Shipped By</td>
              <td class="email-order-details-value">{{shippedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Shipped On</td>
              <td class="email-order-details-value">{{shippedOn}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Shipping Address</td>
              <td class="email-order-details-value">{{shippingAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Tracking Number</td>
              <td class="email-order-details-value tracking" >{{trackingNumberHtml}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent</td>
              <td class="email-order-details-value">{{agentName}}</td>
            </tr>
          </table>
        </div>
        
        {{lineItems}}
      `,

        
        // <p class="email-paragraph-small">
        //   You can monitor the status and delivery confirmation in your RealEsy portal.
        // </p>
        // shipped by {{signageCompanyName}}.
        BROKERAGE_AGENT: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          Your signage order {{orderId}} has been marked.
        </p>
        
        <h3 class="email-heading-h3">
           Shipping Info
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Shipping Date</td>
              <td class="email-order-details-value">{{shippedOn}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Location</td>
              <td class="email-order-details-value">{{shippingAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Supplier</td>
              <td class="email-order-details-value">{{signageCompanyName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Tracking</td>
              <td class="email-order-details-value tracking">{{trackingNumberHtml}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          You will be notified once installation is complete.
        </p>
      `,
      // shipped by {{signageCompanyName}}.
      AGENT: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          Your signage order {{orderId}} has been marked.
        </p>
        
        <h3 class="email-heading-h3">
           Shipping Info
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Shipping Date</td>
              <td class="email-order-details-value">{{shippedOn}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Location</td>
              <td class="email-order-details-value">{{shippingAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Supplier</td>
              <td class="email-order-details-value">{{signageCompanyName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Tracking</td>
              <td class="email-order-details-value tracking">{{trackingNumberHtml}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          You will be notified once installation is complete.
        </p>
      `,
    },
    cta: {
      // BROKERAGE_FIRM: { text: "View Order",url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      // BROKERAGE_TEAM_MEMBER: { text: "View Order",url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}`  },
      // AGENT: { text: "Track My Orders",url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}`     },
      // BROKERAGE_AGENT: { text: "Track My Orders",url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}`  },
    },
    footer: "RealEsy. All rights reserved."
  },
  "Signage Sales order ready for pickup update": {
    subject: {
      BROKERAGE_FIRM: "{{statusSubject}}",
      BROKERAGE_TEAM_MEMBER: "{{statusSubject}}",
      BROKERAGE_AGENT: "{{statusSubject}}",
      AGENT: "{{statusSubject}}",
    },
    title: "Order Ready for Pickup",
    logoUrl: "",
    headerBgColor: "#ffffff",
    bodyLines: [],
    bodyHtml: {
      BROKERAGE_FIRM: `
        <h2 class="email-heading-h2">Hi {{firstName}},</h2>
        <p class="email-paragraph">
          Your order {{orderId}} is now ready for pickup at {{signageCompanyName}}.
        </p>
        <h3 class="email-heading-h3">Pickup Details</h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Ready Since</td>
              <td class="email-order-details-value">{{readySince}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Vendor</td>
              <td class="email-order-details-value">{{vendorName}}</td>
            </tr>
          </table>
        </div>
        {{lineItems}}
        <p class="email-paragraph-small">
          Please arrange pickup at your convenience. If you need assistance or want to coordinate timing, feel free to contact us.
        </p>
      `,
      //  <tr>
      //         <td class="email-order-details-field">Pickup Location</td>
      //         <td class="email-order-details-value">{{pickupLocation}}</td>
      //       </tr>
      //         <tr>
      //         <td class="email-order-details-field">Contact Person</td>
      //         <td class="email-order-details-value">{{contactPerson}}</td>
      //       </tr>
            
      
      BROKERAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">Hi {{firstName}},</h2>
        <p class="email-paragraph">
          Your order {{orderId}} is now ready for pickup at {{signageCompanyName}}.
        </p>
        <h3 class="email-heading-h3">Pickup Details</h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Ready Since</td>
              <td class="email-order-details-value">{{readySince}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Vendor</td>
              <td class="email-order-details-value">{{vendorName}}</td>
            </tr>
          </table>
        </div>
        {{lineItems}}
        <p class="email-paragraph-small">
          Please arrange pickup at your convenience. If you need assistance or want to coordinate timing, feel free to contact us.
        </p>
      `,
        // <tr>
        //       <td class="email-order-details-field">Pickup Location</td>
        //       <td class="email-order-details-value">{{pickupLocation}}</td>
        //     </tr>
        //       <tr>
        //       <td class="email-order-details-field">Contact Person</td>
        //       <td class="email-order-details-value">{{contactPerson}}</td>
        //     </tr>
      BROKERAGE_AGENT: `
        <h2 class="email-heading-h2">Hi {{firstName}},</h2>
        <p class="email-paragraph">
          Your order {{orderId}} is now ready for pickup at {{signageCompanyName}}.
        </p>
        <h3 class="email-heading-h3">Pickup Details</h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Ready Since</td>
              <td class="email-order-details-value">{{readySince}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Vendor</td>
              <td class="email-order-details-value">{{vendorName}}</td>
            </tr>
          </table>
        </div>
        {{lineItems}}
        <p class="email-paragraph-small">
          Please arrange pickup at your convenience. If you need assistance or want to coordinate timing, feel free to contact us.
        </p>
      `,

      // <tr>
      //         <td class="email-order-details-field">Contact Person</td>
      //         <td class="email-order-details-value">{{contactPerson}}</td>
      //       </tr>

      //    <tr>
      //         <td class="email-order-details-field">Pickup Location</td>
      //         <td class="email-order-details-value">{{pickupLocation}}</td>
      //       </tr>
      AGENT: `
        <h2 class="email-heading-h2">Hi {{firstName}},</h2>
        <p class="email-paragraph">
          Your order {{orderId}} is now ready for pickup at {{signageCompanyName}}.
        </p>
        <h3 class="email-heading-h3">Pickup Details</h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Ready Since</td>
              <td class="email-order-details-value">{{readySince}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Vendor</td>
              <td class="email-order-details-value">{{vendorName}}</td>
            </tr>
          </table>
        </div>
        {{lineItems}}
        <p class="email-paragraph-small">
          Please arrange pickup at your convenience. If you need assistance or want to coordinate timing, feel free to contact us.
        </p>
      `,
    },
    //    <tr>
    //           <td class="email-order-details-field">Pickup Location</td>
    //           <td class="email-order-details-value">{{pickupLocation}}</td>
    //         </tr>

    //  <tr>
    //           <td class="email-order-details-field">Contact Person</td>
    //           <td class="email-order-details-value">{{contactPerson}}</td>
    //         </tr>
    footer: "RealEsy. All rights reserved."
  },
  "Signage Sales order shipped with tracking": {
    subject: "Order {{orderId}} Shipped – Tracking Details Included",
    title: "Order Shipped",
    logoUrl: "",
    headerBgColor: "#ffffff",
    bodyLines: [],
    bodyHtml: `
      <h2 class="email-heading-h2">Hi {{firstName}},</h2>
      <p class="email-paragraph">
        Your order {{orderId}} has been shipped by {{vendorName}}. Shipping details are provided below.
      </p>
      <h3 class="email-heading-h3">Shipment Details</h3>
      <div class="email-order-details-card">
        <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
            <td class="email-order-details-field-first">Order ID</td>
            <td class="email-order-details-value-first">{{orderId}}</td>
          </tr>
          <tr>
            <td class="email-order-details-field">Carrier Name</td>
            <td class="email-order-details-value">{{carrierName}}</td>
          </tr>
          <tr>
            <td class="email-order-details-field">Tracking Number</td>
            <td class="email-order-details-value tracking">{{trackingNumberHtml}}</td>
          </tr>
          <tr>
            <td class="email-order-details-field">Shipped On</td>
            <td class="email-order-details-value">{{shippedOn}}</td>
          </tr>
          <tr>
            <td class="email-order-details-field">Ship To</td>
            <td class="email-order-details-value">{{shippingAddress}}</td>
          </tr>
        </table>
      </div>
      {{lineItems}}
      <p class="email-paragraph-small">
        Please track your shipment using the tracking number provided above.
      </p>
    `,
      // <tr>
      //       <td class="email-order-details-field">Items Shipped</td>
      //       <td class="email-order-details-value">{{productsIncluded}}</td>
      //     </tr>
    footer: "RealEsy. All rights reserved."
  },
  "Signage Sales order shipped manual": {
    subject: "Order {{orderId}} Shipped",
    title: "Order Shipped",
    logoUrl: "",
    headerBgColor: "#ffffff",
    bodyLines: [],
    bodyHtml: `
      <h2 class="email-heading-h2">Hi {{firstName}},</h2>
      <p class="email-paragraph">
        Your order {{orderId}} has been shipped by {{vendorName}}.
      </p>
      <h3 class="email-heading-h3"> Shipment Details</h3>
      <div class="email-order-details-card">
        <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
            <td class="email-order-details-field-first">Order ID</td>
            <td class="email-order-details-value-first">{{orderId}}</td>
          </tr>
          <tr>
            <td class="email-order-details-field">Carrier Name</td>
            <td class="email-order-details-value">{{carrierName}}</td>
          </tr>
          <tr>
            <td class="email-order-details-field">Shipped On</td>
            <td class="email-order-details-value">{{shippedOn}}</td>
          </tr>
          <tr>
            <td class="email-order-details-field">Delivery Method</td>
            <td class="email-order-details-value">{{deliveryMethod}}</td>
          </tr>
          <tr>
            <td class="email-order-details-field">Ship To</td>
            <td class="email-order-details-value">{{shippingAddress}}</td>
          </tr>
        </table>
      </div>
      {{lineItems}}
      <p class="email-paragraph-small">
        This shipment was processed without a tracking number. If you need delivery updates, please contact {{vendorName}} directly.
      </p>
    `,

      // <tr>
      //       <td class="email-order-details-field">Items Shipped</td>
      //       <td class="email-order-details-value">{{productsIncluded}}</td>
      //     </tr>
    footer: "RealEsy. All rights reserved."
  },
    // <p class="email-paragraph-small">
    //       Assigned To: <strong>{{installerName}}</strong> ({{installerEmail}})
    //     </p>
  "Signage Installation order created": {
    subject: {
      SIGNAGE_FIRM: "Installation Order Created – {{orderId}}",
      SIGNAGE_TEAM_MEMBER: "Installation Order Created – {{orderId}}",
      BROKERAGE_FIRM: "New Installation Order Created – {{orderId}}",
      BROKERAGE_TEAM_MEMBER: "New Installation Order Created – {{orderId}}",
      AGENT: "New Installation Order Created – {{orderId}}",
      BROKERAGE_AGENT: "New Installation Order Created – {{orderId}}",
    },
    title: "Installation Order Created",
    logoUrl: "", // Logo is imported directly in emailTemplate.ts
    headerBgColor: "#ffffff",
    bodyLines: [], // Using bodyHtml instead
    bodyHtml: {
      SIGNAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          You have successfully created Installation Order {{orderId}} for {{customerName}}.
        </p>
        <p class="email-paragraph-small">
          All parties have been notified. Be sure to update the scheduling and progress as the job proceeds.
        </p>
      `,
      SIGNAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          You have successfully created Installation Order {{orderId}} for {{customerName}}.
        </p>
        <p class="email-paragraph-small">
          All parties have been notified. Be sure to update the scheduling and progress as the job proceeds.
        </p>
      `,
      BROKERAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          A new installation order {{orderId}} has been created by {{signageCompanyName}}.
        </p>
        
        <h3 class="email-heading-h3">
          Installation Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Scheduled Date</td>
              <td class="email-order-details-value">{{scheduledDate}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent</td>
              <td class="email-order-details-value">{{agentName}}</td>
            </tr>
          </table>
        </div>
        
        <h3 class="email-line-items-heading">
           Line Items
        </h3>
        
        <div class="email-line-items-container">
          {{lineItems}}
        </div>
      `,
      BROKERAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          A new installation order {{orderId}} has been created by {{signageCompanyName}}.
        </p>
        
        <h3 class="email-heading-h3">
           Installation Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Scheduled Date</td>
              <td class="email-order-details-value">{{scheduledDate}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent</td>
              <td class="email-order-details-value">{{agentName}}</td>
            </tr>
          </table>
        </div>
        
        <h3 class="email-line-items-heading">
           Line Items
        </h3>
        
        <div class="email-line-items-container">
          {{lineItems}}
        </div>
      `,
       BROKERAGE_AGENT: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          A new installation order {{orderId}} has been created by {{signageCompanyName}}.
        </p>
        
        <h3 class="email-heading-h3">
           Installation Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Scheduled Date</td>
              <td class="email-order-details-value-bold">{{scheduledDate}}</td>
            </tr>
            
          </table>
        </div>
        
        <h3 class="email-line-items-heading">
           Line Items
        </h3>
        
        <div class="email-line-items-container">
          {{lineItems}}
        </div>
      `,
      AGENT: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          A new installation order {{orderId}} has been created by {{signageCompanyName}}.
        </p>
        
        <h3 class="email-heading-h3">
           Installation Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Scheduled Date</td>
              <td class="email-order-details-value-bold">{{scheduledDate}}</td>
            </tr>
           
          </table>
        </div>
        
        <h3 class="email-line-items-heading">
           Line Items
        </h3>
        
        <div class="email-line-items-container">
          {{lineItems}}
        </div>
      `,
    },
    cta: {
      // SIGNAGE_FIRM: { text: "Manage Installation Orders", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      // SIGNAGE_TEAM_MEMBER: { text: "Manage Installation Orders", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      // BROKERAGE_FIRM: { text: "View Installation Order", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      // BROKERAGE_TEAM_MEMBER: { text: "View Installation Order", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      // BROKERAGE_AGENT: { text: "View Installation Order", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      // AGENT: { text: "View Installation Order", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
    },
    footer: "RealEsy. All rights reserved."
  },
  "Signage Payment applied for Installation Job": {
    subject: {
      SIGNAGE_FIRM: "Payment Received for Installation Order {{orderId}}",
      SIGNAGE_TEAM_MEMBER: "Payment Received for Installation Order {{orderId}}",
      BROKERAGE_FIRM: "Payment Received for Installation Order {{orderId}}",
      BROKERAGE_TEAM_MEMBER: "Payment Received for Installation Order {{orderId}}",
      AGENT: "Payment Received for Your Installation Order {{orderId}}",
      BROKERAGE_AGENT: "Payment Received for Your Installation Order {{orderId}}",
    },
    title: "Payment Applied for Installation Order",
    logoUrl: "", // Logo is imported directly in emailTemplate.ts
    headerBgColor: "#ffffff",
    bodyLines: [], // Using bodyHtml instead
    bodyHtml: {
      SIGNAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          A payment has been successfully applied to the following installation order:
        </p>
        
        <h3 class="email-heading-h3">
           Installation Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent Name</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Brokerage Firm</td>
              <td class="email-order-details-value-bold">{{brokerageFirm}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Applied By</td>
              <td class="email-order-details-value-bold">{{paymentAppliedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Amount</td>
              <td class="email-order-details-value-bold">{{paymentAmount}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Method</td>
              <td class="email-order-details-value-bold">{{paymentMethod}}</td>
            </tr>
           
          </table>
        </div>
        
      `,
//  <tr>
//               <td class="email-order-details-field">Payment Date</td>
//               <td class="email-order-details-value-bold">{{paymentDate}}</td>
//             </tr>
      //  <tr>
      //         <td class="email-order-details-field">Current Balance</td>
      //         <td class="email-order-details-value-bold">{{currentBalance}}</td>
      //       </tr>
      SIGNAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          A payment has been successfully applied to the following installation order:
        </p>
        
        <h3 class="email-heading-h3">
           Installation Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent Name</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Brokerage Firm</td>
              <td class="email-order-details-value-bold">{{brokerageFirm}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Applied By</td>
              <td class="email-order-details-value-bold">{{paymentAppliedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Amount</td>
              <td class="email-order-details-value-bold">{{paymentAmount}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Method</td>
              <td class="email-order-details-value-bold">{{paymentMethod}}</td>
            </tr>
           
          </table>
        </div>
      `,
        // <tr>
        //       <td class="email-order-details-field">Payment Date</td>
        //       <td class="email-order-details-value-bold">{{paymentDate}}</td>
        //     </tr>

      //  <tr>
      //         <td class="email-order-details-field">Current Balance</td>
      //         <td class="email-order-details-value-bold">{{currentBalance}}</td>
      //       </tr>
      BROKERAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          A payment has been successfully applied to the following installation order:
        </p>
        
        <h3 class="email-heading-h3">
           Installation Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent Name</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Brokerage Firm</td>
              <td class="email-order-details-value-bold">{{brokerageFirm}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Applied By</td>
              <td class="email-order-details-value-bold">{{paymentAppliedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Amount</td>
              <td class="email-order-details-value-bold">{{paymentAmount}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Method</td>
              <td class="email-order-details-value-bold">{{paymentMethod}}</td>
            </tr>
          
          </table>
        </div>
      `,

      //  <tr>
      //         <td class="email-order-details-field">Payment Date</td>
      //         <td class="email-order-details-value-bold">{{paymentDate}}</td>
      //       </tr>
        // <tr>
        //       <td class="email-order-details-field">Current Balance</td>
        //       <td class="email-order-details-value-bold">{{currentBalance}}</td>
        //     </tr>
      BROKERAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          A payment has been successfully applied to the following installation order:
        </p>
        
        <h3 class="email-heading-h3">
           Installation Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent Name</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Brokerage Firm</td>
              <td class="email-order-details-value-bold">{{brokerageFirm}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Applied By</td>
              <td class="email-order-details-value-bold">{{paymentAppliedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Amount</td>
              <td class="email-order-details-value-bold">{{paymentAmount}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Method</td>
              <td class="email-order-details-value-bold">{{paymentMethod}}</td>
            </tr>
          </table>
        </div>
      `,
      //  <tr>
      //         <td class="email-order-details-field">Payment Date</td>
      //         <td class="email-order-details-value-bold">{{paymentDate}}</td>
      //       </tr>
      // <tr>
      //         <td class="email-order-details-field">Current Balance</td>
      //         <td class="email-order-details-value-bold">{{currentBalance}}</td>
      //       </tr>
           
      AGENT: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          A payment has been successfully applied to your installation order:
        </p>
        
        <h3 class="email-heading-h3">
           Installation Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent Name</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Brokerage Firm</td>
              <td class="email-order-details-value-bold">{{brokerageFirm}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Applied By</td>
              <td class="email-order-details-value-bold">{{paymentAppliedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Amount</td>
              <td class="email-order-details-value-bold">{{paymentAmount}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Method</td>
              <td class="email-order-details-value-bold">{{paymentMethod}}</td>
            </tr>
          </table>
        </div>
        
      `,

        //  <tr>
        //       <td class="email-order-details-field">Payment Date</td>
        //       <td class="email-order-details-value-bold">{{paymentDate}}</td>
        //     </tr>
      //  <tr>
      //         <td class="email-order-details-field">Current Balance</td>
      //         <td class="email-order-details-value-bold">{{currentBalance}}</td>
      //       </tr>
      BROKERAGE_AGENT: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          A payment has been successfully applied to your installation order:
        </p>
        
        <h3 class="email-heading-h3">
           Installation Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent Name</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Brokerage Firm</td>
              <td class="email-order-details-value-bold">{{brokerageFirm}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Applied By</td>
              <td class="email-order-details-value-bold">{{paymentAppliedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Amount</td>
              <td class="email-order-details-value-bold">{{paymentAmount}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Method</td>
              <td class="email-order-details-value-bold">{{paymentMethod}}</td>
            </tr>
          </table>
        </div>
      `,
        // <tr>
        //       <td class="email-order-details-field">Payment Date</td>
        //       <td class="email-order-details-value-bold">{{paymentDate}}</td>
        //     </tr>
      //  <tr>
      //         <td class="email-order-details-field">Current Balance</td>
      //         <td class="email-order-details-value-bold">{{currentBalance}}</td>
      //       </tr>
    },
    // cta: { text: "View Installation Order", url: "{{viewOrderUrl}}" },
    footer: "RealEsy. All rights reserved."
  },
  "Signage Payment applied for removal Job": {
    subject: {
      SIGNAGE_FIRM: "Payment Received for Removal Order {{orderId}}",
      SIGNAGE_TEAM_MEMBER: "Payment Received for Removal Order {{orderId}}",
      BROKERAGE_FIRM: "Payment Received for Removal Order {{orderId}}",
      BROKERAGE_TEAM_MEMBER: "Payment Received for Removal Order {{orderId}}",
      AGENT: "Payment Received for Your Removal Order {{orderId}}",
      BROKERAGE_AGENT: "Payment Received for Your Removal Order {{orderId}}",
    },
    title: "Payment Applied for Removal Order",
    logoUrl: "", // Logo is imported directly in emailTemplate.ts
    headerBgColor: "#ffffff",
    bodyLines: [], // Using bodyHtml instead
    bodyHtml: {
      SIGNAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          A payment has been successfully applied to the following removal order:
        </p>
        
        <h3 class="email-heading-h3">
          Removal Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent Name</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Brokerage Firm</td>
              <td class="email-order-details-value-bold">{{brokerageFirm}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Applied By</td>
              <td class="email-order-details-value-bold">{{paymentAppliedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Amount</td>
              <td class="email-order-details-value-bold">{{paymentAmount}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Method</td>
              <td class="email-order-details-value-bold">{{paymentMethod}}</td>
            </tr>
          </table>
        </div>
        
      `,
      //  <tr>
      //         <td class="email-order-details-field">Current Balance</td>
      //         <td class="email-order-details-value-bold">{{currentBalance}}</td>
      //       </tr>
           
      SIGNAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          A payment has been successfully applied to the following removal order:
        </p>
        
        <h3 class="email-heading-h3">
          Removal Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent Name</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Brokerage Firm</td>
              <td class="email-order-details-value-bold">{{brokerageFirm}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Applied By</td>
              <td class="email-order-details-value-bold">{{paymentAppliedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Amount</td>
              <td class="email-order-details-value-bold">{{paymentAmount}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Method</td>
              <td class="email-order-details-value-bold">{{paymentMethod}}</td>
            </tr>
         
          </table>
        </div>
      `,
      BROKERAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          A payment has been successfully applied to the following removal order:
        </p>
        
        <h3 class="email-heading-h3">
          Removal Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent Name</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Brokerage Firm</td>
              <td class="email-order-details-value-bold">{{brokerageFirm}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Applied By</td>
              <td class="email-order-details-value-bold">{{paymentAppliedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Amount</td>
              <td class="email-order-details-value-bold">{{paymentAmount}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Method</td>
              <td class="email-order-details-value-bold">{{paymentMethod}}</td>
            </tr>
          
          </table>
        </div>
      `,
      BROKERAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          A payment has been successfully applied to the following removal order:
        </p>
        
        <h3 class="email-heading-h3">
          Removal Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent Name</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Brokerage Firm</td>
              <td class="email-order-details-value-bold">{{brokerageFirm}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Applied By</td>
              <td class="email-order-details-value-bold">{{paymentAppliedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Amount</td>
              <td class="email-order-details-value-bold">{{paymentAmount}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Method</td>
              <td class="email-order-details-value-bold">{{paymentMethod}}</td>
            </tr>
           
          </table>
        </div>
      `,
      AGENT: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          A payment has been successfully applied to your removal order:
        </p>
        
        <h3 class="email-heading-h3">
          Removal Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent Name</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Brokerage Firm</td>
              <td class="email-order-details-value-bold">{{brokerageFirm}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Applied By</td>
              <td class="email-order-details-value-bold">{{paymentAppliedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Amount</td>
              <td class="email-order-details-value-bold">{{paymentAmount}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Method</td>
              <td class="email-order-details-value-bold">{{paymentMethod}}</td>
            </tr>

          </table>
        </div>
      `,
      BROKERAGE_AGENT: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          A payment has been successfully applied to your removal order:
        </p>
        
        <h3 class="email-heading-h3">
          Removal Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent Name</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Brokerage Firm</td>
              <td class="email-order-details-value-bold">{{brokerageFirm}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Applied By</td>
              <td class="email-order-details-value-bold">{{paymentAppliedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Amount</td>
              <td class="email-order-details-value-bold">{{paymentAmount}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Method</td>
              <td class="email-order-details-value-bold">{{paymentMethod}}</td>
            </tr>
           
          </table>
        </div>
      `,
    },
    // cta: { text: "View Removal Order", url: "{{viewOrderUrl}}" },
    footer: "RealEsy. All rights reserved."
  },
  "Public Signage Payment applied for Installation Job": {
    subject: {
      SIGNAGE_FIRM: "Payment Received for Installation Order {{orderId}}",
      SIGNAGE_TEAM_MEMBER: "Payment Received for Installation Order {{orderId}}",
      BROKERAGE_FIRM: "Payment Received for Installation Order {{orderId}}",
      BROKERAGE_TEAM_MEMBER: "Payment Received for Installation Order {{orderId}}",
      AGENT: "Payment Received for Your Installation Order {{orderId}}",
      BROKERAGE_AGENT: "Payment Received for Your Installation Order {{orderId}}",
    },
    title: "Payment Applied for Installation Order",
    logoUrl: "", // Logo is imported directly in emailTemplate.ts
    headerBgColor: "#ffffff",
    bodyLines: [], // Using bodyHtml instead
    bodyHtml: {
      SIGNAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          A payment has been successfully applied to the following installation order:
        </p>
        
        <h3 class="email-heading-h3">
           Installation Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent Name</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Brokerage Firm</td>
              <td class="email-order-details-value-bold">{{brokerageFirm}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Applied By</td>
              <td class="email-order-details-value-bold">{{paymentAppliedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Amount</td>
              <td class="email-order-details-value-bold">{{paymentAmount}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Method</td>
              <td class="email-order-details-value-bold">{{paymentMethod}}</td>
            </tr>
           
          </table>
        </div>
        
      `,
//  <tr>
//               <td class="email-order-details-field">Payment Date</td>
//               <td class="email-order-details-value-bold">{{paymentDate}}</td>
//             </tr>
      //  <tr>
      //         <td class="email-order-details-field">Current Balance</td>
      //         <td class="email-order-details-value-bold">{{currentBalance}}</td>
      //       </tr>
      SIGNAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          A payment has been successfully applied to the following installation order:
        </p>
        
        <h3 class="email-heading-h3">
           Installation Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent Name</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Brokerage Firm</td>
              <td class="email-order-details-value-bold">{{brokerageFirm}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Applied By</td>
              <td class="email-order-details-value-bold">{{paymentAppliedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Amount</td>
              <td class="email-order-details-value-bold">{{paymentAmount}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Method</td>
              <td class="email-order-details-value-bold">{{paymentMethod}}</td>
            </tr>
           
          </table>
        </div>
      `,
        // <tr>
        //       <td class="email-order-details-field">Payment Date</td>
        //       <td class="email-order-details-value-bold">{{paymentDate}}</td>
        //     </tr>

      //  <tr>
      //         <td class="email-order-details-field">Current Balance</td>
      //         <td class="email-order-details-value-bold">{{currentBalance}}</td>
      //       </tr>
      BROKERAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          A payment has been successfully applied to the following installation order:
        </p>
        
        <h3 class="email-heading-h3">
           Installation Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent Name</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Brokerage Firm</td>
              <td class="email-order-details-value-bold">{{brokerageFirm}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Applied By</td>
              <td class="email-order-details-value-bold">{{paymentAppliedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Amount</td>
              <td class="email-order-details-value-bold">{{paymentAmount}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Method</td>
              <td class="email-order-details-value-bold">{{paymentMethod}}</td>
            </tr>
          
          </table>
        </div>
      `,

      //  <tr>
      //         <td class="email-order-details-field">Payment Date</td>
      //         <td class="email-order-details-value-bold">{{paymentDate}}</td>
      //       </tr>
        // <tr>
        //       <td class="email-order-details-field">Current Balance</td>
        //       <td class="email-order-details-value-bold">{{currentBalance}}</td>
        //     </tr>
      BROKERAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          A payment has been successfully applied to the following installation order:
        </p>
        
        <h3 class="email-heading-h3">
           Installation Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent Name</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Brokerage Firm</td>
              <td class="email-order-details-value-bold">{{brokerageFirm}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Applied By</td>
              <td class="email-order-details-value-bold">{{paymentAppliedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Amount</td>
              <td class="email-order-details-value-bold">{{paymentAmount}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Method</td>
              <td class="email-order-details-value-bold">{{paymentMethod}}</td>
            </tr>
          </table>
        </div>
      `,
      //  <tr>
      //         <td class="email-order-details-field">Payment Date</td>
      //         <td class="email-order-details-value-bold">{{paymentDate}}</td>
      //       </tr>
      // <tr>
      //         <td class="email-order-details-field">Current Balance</td>
      //         <td class="email-order-details-value-bold">{{currentBalance}}</td>
      //       </tr>
           
      AGENT: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          A payment has been successfully applied to your installation order:
        </p>
        
        <h3 class="email-heading-h3">
           Installation Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent Name</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Brokerage Firm</td>
              <td class="email-order-details-value-bold">{{brokerageFirm}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Applied By</td>
              <td class="email-order-details-value-bold">{{paymentAppliedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Amount</td>
              <td class="email-order-details-value-bold">{{paymentAmount}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Method</td>
              <td class="email-order-details-value-bold">{{paymentMethod}}</td>
            </tr>
          </table>
        </div>
        
      `,

        //  <tr>
        //       <td class="email-order-details-field">Payment Date</td>
        //       <td class="email-order-details-value-bold">{{paymentDate}}</td>
        //     </tr>
      //  <tr>
      //         <td class="email-order-details-field">Current Balance</td>
      //         <td class="email-order-details-value-bold">{{currentBalance}}</td>
      //       </tr>
      BROKERAGE_AGENT: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          A payment has been successfully applied to your installation order:
        </p>
        
        <h3 class="email-heading-h3">
           Installation Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent Name</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Brokerage Firm</td>
              <td class="email-order-details-value-bold">{{brokerageFirm}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Applied By</td>
              <td class="email-order-details-value-bold">{{paymentAppliedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Amount</td>
              <td class="email-order-details-value-bold">{{paymentAmount}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Method</td>
              <td class="email-order-details-value-bold">{{paymentMethod}}</td>
            </tr>
          </table>
        </div>
      `,
        // <tr>
        //       <td class="email-order-details-field">Payment Date</td>
        //       <td class="email-order-details-value-bold">{{paymentDate}}</td>
        //     </tr>
      //  <tr>
      //         <td class="email-order-details-field">Current Balance</td>
      //         <td class="email-order-details-value-bold">{{currentBalance}}</td>
      //       </tr>
    },
    // cta: { text: "View Installation Order", url: "{{viewOrderUrl}}" },
    footer: "RealEsy. All rights reserved."
  },
  "Public Signage Payment applied for removal Job": {
    subject: {
      SIGNAGE_FIRM: "Payment Received for Removal Order {{orderId}}",
      SIGNAGE_TEAM_MEMBER: "Payment Received for Removal Order {{orderId}}",
      BROKERAGE_FIRM: "Payment Received for Removal Order {{orderId}}",
      BROKERAGE_TEAM_MEMBER: "Payment Received for Removal Order {{orderId}}",
      AGENT: "Payment Received for Your Removal Order {{orderId}}",
      BROKERAGE_AGENT: "Payment Received for Your Removal Order {{orderId}}",
    },
    title: "Payment Applied for Removal Order",
    logoUrl: "", // Logo is imported directly in emailTemplate.ts
    headerBgColor: "#ffffff",
    bodyLines: [], // Using bodyHtml instead
    bodyHtml: {
      SIGNAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          A payment has been successfully applied to the following removal order:
        </p>
        
        <h3 class="email-heading-h3">
          Removal Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent Name</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Brokerage Firm</td>
              <td class="email-order-details-value-bold">{{brokerageFirm}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Applied By</td>
              <td class="email-order-details-value-bold">{{paymentAppliedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Amount</td>
              <td class="email-order-details-value-bold">{{paymentAmount}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Method</td>
              <td class="email-order-details-value-bold">{{paymentMethod}}</td>
            </tr>
          </table>
        </div>
        
      `,
      //  <tr>
      //         <td class="email-order-details-field">Current Balance</td>
      //         <td class="email-order-details-value-bold">{{currentBalance}}</td>
      //       </tr>
           
      SIGNAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          A payment has been successfully applied to the following removal order:
        </p>
        
        <h3 class="email-heading-h3">
          Removal Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent Name</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Brokerage Firm</td>
              <td class="email-order-details-value-bold">{{brokerageFirm}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Applied By</td>
              <td class="email-order-details-value-bold">{{paymentAppliedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Amount</td>
              <td class="email-order-details-value-bold">{{paymentAmount}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Method</td>
              <td class="email-order-details-value-bold">{{paymentMethod}}</td>
            </tr>
         
          </table>
        </div>
      `,
      BROKERAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          A payment has been successfully applied to the following removal order:
        </p>
        
        <h3 class="email-heading-h3">
          Removal Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent Name</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Brokerage Firm</td>
              <td class="email-order-details-value-bold">{{brokerageFirm}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Applied By</td>
              <td class="email-order-details-value-bold">{{paymentAppliedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Amount</td>
              <td class="email-order-details-value-bold">{{paymentAmount}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Method</td>
              <td class="email-order-details-value-bold">{{paymentMethod}}</td>
            </tr>
          
          </table>
        </div>
      `,
      BROKERAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          A payment has been successfully applied to the following removal order:
        </p>
        
        <h3 class="email-heading-h3">
          Removal Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent Name</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Brokerage Firm</td>
              <td class="email-order-details-value-bold">{{brokerageFirm}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Applied By</td>
              <td class="email-order-details-value-bold">{{paymentAppliedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Amount</td>
              <td class="email-order-details-value-bold">{{paymentAmount}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Method</td>
              <td class="email-order-details-value-bold">{{paymentMethod}}</td>
            </tr>
           
          </table>
        </div>
      `,
      AGENT: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          A payment has been successfully applied to your removal order:
        </p>
        
        <h3 class="email-heading-h3">
          Removal Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent Name</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Brokerage Firm</td>
              <td class="email-order-details-value-bold">{{brokerageFirm}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Applied By</td>
              <td class="email-order-details-value-bold">{{paymentAppliedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Amount</td>
              <td class="email-order-details-value-bold">{{paymentAmount}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Method</td>
              <td class="email-order-details-value-bold">{{paymentMethod}}</td>
            </tr>

          </table>
        </div>
      `,
      BROKERAGE_AGENT: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          A payment has been successfully applied to your removal order:
        </p>
        
        <h3 class="email-heading-h3">
          Removal Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent Name</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Brokerage Firm</td>
              <td class="email-order-details-value-bold">{{brokerageFirm}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Applied By</td>
              <td class="email-order-details-value-bold">{{paymentAppliedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Amount</td>
              <td class="email-order-details-value-bold">{{paymentAmount}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Payment Method</td>
              <td class="email-order-details-value-bold">{{paymentMethod}}</td>
            </tr>
           
          </table>
        </div>
      `,
    },
    // cta: { text: "View Removal Order", url: "{{viewOrderUrl}}" },
    footer: "RealEsy. All rights reserved."
  },
  "Signage Installation Status change": {
    subject: {
      SIGNAGE_FIRM: " Installation Order {{orderId}} Status Updated to \"{{newStatus}}\"",
      SIGNAGE_TEAM_MEMBER: " Installation Order {{orderId}} Status Updated to \"{{newStatus}}\"",
      BROKERAGE_FIRM: " Installation Order {{orderId}} Status Updated to \"{{newStatus}}\"",
      BROKERAGE_TEAM_MEMBER: " Installation Order {{orderId}} Status Updated to \"{{newStatus}}\"",
      AGENT: " Installation Order {{orderId}} Status Updated to \"{{newStatus}}\"",
      BROKERAGE_AGENT: " Installation Order {{orderId}} Status Updated to \"{{newStatus}}\"",
    },
    title: "Installation Order Status Updated",
    logoUrl: "", // Logo is imported directly in emailTemplate.ts
    headerBgColor: "#ffffff",
    bodyLines: [], // Using bodyHtml instead
    bodyHtml: {
      SIGNAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          The status of installation order {{orderId}} has been updated in RealEsy.
        </p>
        
        <h3 class="email-heading-h3">
           Installation Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">New Status</td>
              <td class="email-order-details-value-bold">{{newStatus}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent Name</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Brokerage Firm</td>
              <td class="email-order-details-value-bold">{{brokerageFirm}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Updated By</td>
              <td class="email-order-details-value-bold">{{updatedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Update Time</td>
              <td class="email-order-details-value-bold">{{updateTime}}</td>
            </tr>
            
          </table>
        </div>
      `,
         
        // <p class="email-paragraph-small">
        //   You can view the latest details or take next steps from your RealEsy portal.
        // </p>
      SIGNAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          The status of installation order {{orderId}} has been updated in RealEsy.
        </p>
        
        <h3 class="email-heading-h3">
           Installation Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">New Status</td>
              <td class="email-order-details-value-bold">{{newStatus}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent Name</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Brokerage Firm</td>
              <td class="email-order-details-value-bold">{{brokerageFirm}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Updated By</td>
              <td class="email-order-details-value-bold">{{updatedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Update Time</td>
              <td class="email-order-details-value-bold>{{updateTime}}</td>
            </tr>
           
          </table>
        </div>
      `,
      BROKERAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          The installation order below has been updated to the following status:
        </p>
        
        <h3 class="email-heading-h3">
           Installation Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">New Status</td>
              <td class="email-order-details-value-bold">{{newStatus}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent Name</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Brokerage Firm</td>
              <td class="email-order-details-value-bold">{{brokerageFirm}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Updated By</td>
              <td class="email-order-details-value-bold">{{updatedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Update Time</td>
              <td class="email-order-details-value-bold">{{updateTime}}</td>
            </tr>
           
          </table>
        </div>
      `,
      BROKERAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          The installation order below has been updated to the following status:
        </p>
        
        <h3 class="email-heading-h3">
           Installation Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">New Status</td>
              <td class="email-order-details-value-bold">{{newStatus}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent Name</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Brokerage Firm</td>
              <td class="email-order-details-value-bold">{{brokerageFirm}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Updated By</td>
              <td class="email-order-details-value-bold">{{updatedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Update Time</td>
              <td class="email-order-details-value-bold">{{updateTime}}</td>
            </tr>
           
          </table>
        </div>
      `,
      AGENT: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          The installation order below has been updated to the following status:
        </p>
        
        <h3 class="email-heading-h3">
           Installation Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">New Status</td>
              <td class="email-order-details-value-bold">{{newStatus}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent Name</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Brokerage Firm</td>
              <td class="email-order-details-value-bold">{{brokerageFirm}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Updated By</td>
              <td class="email-order-details-value-bold">{{updatedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Update Time</td>
              <td class="email-order-details-value-bold">{{updateTime}}</td>
            </tr>
          </table>
        </div>
      `,
    },
    // cta: { text: "View Installation Order", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
    footer: "RealEsy. All rights reserved."
  },
  //  <tr>
  //             <td class="email-order-details-field">Assigned To</td>
  //             <td class="email-order-details-value-bold">{{installerName}} ({{installerEmail}})</td>
  //           </tr>
  "Signage Installation order Canceled": {
    subject: {
      SIGNAGE_FIRM: "Your Signage Installation Order {{orderId}} has been canceled",
      SIGNAGE_TEAM_MEMBER: "Your Signage Installation Order {{orderId}} has been canceled",
      BROKERAGE_FIRM: "Your Signage Installation Order {{orderId}} has been canceled",
      BROKERAGE_TEAM_MEMBER: "Your Signage Installation Order {{orderId}} has been canceled",
      AGENT: "Your Signage Installation Order {{orderId}} has been canceled",
      BROKERAGE_AGENT: "Your Signage Installation Order {{orderId}} has been canceled",
    },
    title: "Installation Order Canceled",
    logoUrl: "", // Logo is imported directly in emailTemplate.ts
    headerBgColor: "#ffffff",
    bodyLines: [], // Using bodyHtml instead
    bodyHtml: {
      SIGNAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          Installation Order {{orderId}} was canceled by {{canceledBy}}.
        </p>
        
        <h3 class="email-heading-h3">
           Canceled Order Details
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Canceled On</td>
              <td class="email-order-details-value-bold">{{canceledOn}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Canceled By</td>
              <td class="email-order-details-value-bold">{{canceledBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Reason</td>
              <td class="email-order-details-value-bold">{{reason}}</td>
            </tr>
            
          </table>
        </div>
      `,
      SIGNAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          Installation Order {{orderId}} was canceled by {{canceledBy}}.
        </p>
        
        <h3 class="email-heading-h3">
           Canceled Order Details
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Canceled On</td>
              <td class="email-order-details-value-bold">{{canceledOn}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Canceled By</td>
              <td class="email-order-details-value-bold">{{canceledBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Reason</td>
              <td class="email-order-details-value-bold">{{reason}}</td>
            </tr>
            
          </table>
        </div>
      `,
      BROKERAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          Installation Order {{orderId}} has been canceled by {{signageCompanyName}}. Below is a summary of the cancellation:
        </p>
        
        <h3 class="email-heading-h3">
           Canceled Order Details
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Canceled On</td>
              <td class="email-order-details-value-bold">{{canceledOn}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Canceled By</td>
              <td class="email-order-details-value-bold">{{canceledBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Reason</td>
              <td class="email-order-details-value-bold">{{reason}}</td>
            </tr>
          </table>
        </div>
      `,

        // <p class="email-paragraph-small">
        //   You can view the order history from your RealEsy portal.
        // </p>
      BROKERAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          Installation Order {{orderId}} has been canceled by {{signageCompanyName}}. Below is a summary of the cancellation:
        </p>
        
        <h3 class="email-heading-h3">
           Canceled Order Details
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Canceled On</td>
              <td class="email-order-details-value-bold">{{canceledOn}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Canceled By</td>
              <td class="email-order-details-value-bold">{{canceledBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Reason</td>
              <td class="email-order-details-value-bold">{{reason}}</td>
            </tr>
          </table>
        </div>
      `,

      // <p class="email-paragraph-small">
      //     You can view the order history from your RealEsy portal.
      //   </p>
        BROKERAGE_AGENT: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          Your installation order {{orderId}} has been canceled by {{signageCompanyName}}. See the details below:
        </p>
        
        <h3 class="email-heading-h3">
          Canceled Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Canceled On</td>
              <td class="email-order-details-value-bold">{{canceledOn}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Reason</td>
              <td class="email-order-details-value-bold">{{reason}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          You can submit a new request at any time.
        </p>
      `,
      AGENT: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          Your installation order {{orderId}} has been canceled by {{signageCompanyName}}. See the details below:
        </p>
        
        <h3 class="email-heading-h3">
          Canceled Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Canceled On</td>
              <td class="email-order-details-value-bold">{{canceledOn}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Reason</td>
              <td class="email-order-details-value-bold">{{reason}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          You can submit a new request at any time.
        </p>
      `,
    },
    cta: {
      // SIGNAGE_FIRM: { text: "View Order History", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      // SIGNAGE_TEAM_MEMBER: { text: "View Order History", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      // BROKERAGE_FIRM: { text: "View Order History", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      // BROKERAGE_TEAM_MEMBER: { text: "View Order History", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      // AGENT: { text: "Create New Order", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}`},
      // BROKERAGE_AGENT: { text: "Create New Order", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}`},
    },
    footer: "RealEsy. All rights reserved."
  },
  "Signage Removal request created": {
    subject: {
      SIGNAGE_FIRM: "Removal Job has been Created – {{orderId}}",
      SIGNAGE_TEAM_MEMBER: "Removal Job has been Created – {{orderId}}",
      BROKERAGE_FIRM: "Removal Job has been Created – {{orderId}}",
      BROKERAGE_TEAM_MEMBER: "Removal Job has been Created – {{orderId}}",
      AGENT: "Removal Job has been Created – {{orderId}}",
      BROKERAGE_AGENT: "Removal Job has been Created – {{orderId}}",
    },
    title: "Removal Request Created",
    logoUrl: "", // Logo is imported directly in emailTemplate.ts
    headerBgColor: "#ffffff",
    bodyLines: [], // Using bodyHtml instead
    bodyHtml: {
      SIGNAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          New signage removal request {{orderId}} received from {{customerName}}.
        </p>
       
        <p class="email-paragraph-small">
          All parties have been notified. Be sure to update the scheduling and progress as the job proceeds.
        </p>
      `,
      SIGNAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          New signage removal request {{orderId}} received from {{customerName}}.
        </p>
      
        <p class="email-paragraph-small">
          All parties have been notified. Be sure to update the scheduling and progress as the job proceeds.
        </p>
      `,
      BROKERAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
           Your signage removal request {{orderId}} has been created.
        </p>
        
        <h3 class="email-heading-h3">
         Request Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Submitted By</td>
              <td class="email-order-details-value-bold">{{submittedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Location</td>
              <td class="email-order-details-value-bold">{{location}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Assigned To</td>
              <td class="email-order-details-value-bold">{{signageCompanyName}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          You'll be notified once the removal is scheduled and completed.
        </p>
      `,
      // and assigned to {{signageCompanyName}}.
      BROKERAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          Your signage removal request {{orderId}} has been created.
        </p>
        
        <h3 class="email-heading-h3">
         Request Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Submitted By</td>
              <td class="email-order-details-value-bold">{{submittedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Location</td>
              <td class="email-order-details-value-bold">{{location}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Assigned To</td>
              <td class="email-order-details-value-bold">{{signageCompanyName}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          You'll be notified once the removal is scheduled and completed.
        </p>
      `,
      // and assigned to {{signageCompanyName}}.
        BROKERAGE_AGENT: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          Your request to remove signage at {{location}} has been created.
        </p>
        
        <h3 class="email-heading-h3">
          Request Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Submitted On</td>
              <td class="email-order-details-value-bold">{{submittedOn}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Location</td>
              <td class="email-order-details-value-bold">{{location}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Supplier</td>
              <td class="email-order-details-value-bold">{{signageCompanyName}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          You'll be notified when removal is scheduled and completed.
        </p>
      `,
      // and assigned to {{signageCompanyName}}.
      AGENT: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          Your signage removal request {{orderId}} has been created.
        </p>
        
        <h3 class="email-heading-h3">
          Request Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Submitted On</td>
              <td class="email-order-details-value-bold">{{submittedOn}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Location</td>
              <td class="email-order-details-value-bold">{{location}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Supplier</td>
              <td class="email-order-details-value-bold">{{signageCompanyName}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          You'll be notified when removal is scheduled and completed.
        </p>
      `,
    },
    cta: {
      // SIGNAGE_FIRM: { text: "View Order History", url: "{{viewOrderUrl}}" },
      // SIGNAGE_FIRM: { text: "View Order History", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}`},
      // SIGNAGE_TEAM_MEMBER: { text: "View Order History", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}`},
      // BROKERAGE_FIRM: { text: "Track Removals", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}`},
      // BROKERAGE_TEAM_MEMBER: { text: "Track Removals", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}`},
      // AGENT: { text: "Track My Orders", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}`},
      // BROKERAGE_AGENT: { text: "Track My Orders", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}`},
    },
    footer: "RealEsy. All rights reserved."
  },
  "Signage Removal order Canceled": {
    subject: {
      SIGNAGE_FIRM: "Your Signage Removal Order {{orderId}} has been canceled",
      SIGNAGE_TEAM_MEMBER: "Your Signage Removal Order {{orderId}} has been canceled",
      BROKERAGE_FIRM: "Your Signage Removal Order {{orderId}} has been canceled",
      BROKERAGE_TEAM_MEMBER: "Your Signage Removal Order {{orderId}} has been canceled",
      AGENT: "Your Signage Removal Order {{orderId}} has been canceled",
      BROKERAGE_AGENT: "Your Signage Removal Order {{orderId}} has been canceled",
    },
    title: "Removal Order Canceled",
    logoUrl: "", // Logo is imported directly in emailTemplate.ts
    headerBgColor: "#ffffff",
    bodyLines: [], // Using bodyHtml instead
    bodyHtml: {
      SIGNAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          Removal order {{orderId}} was canceled by {{canceledBy}}.
        </p>
        
        <h3 class="email-heading-h3">
           Canceled Order Details
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Canceled On</td>
              <td class="email-order-details-value-bold">{{canceledOn}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Canceled By</td>
              <td class="email-order-details-value-bold">{{canceledBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Reason</td>
              <td class="email-order-details-value-bold">{{reason}}</td>
            </tr>
           
          </table>
        </div>
      `,
      SIGNAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          Removal order {{orderId}} was canceled by {{canceledBy}}.
        </p>
        
        <h3 class="email-heading-h3">
           Canceled Order Details
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Canceled On</td>
              <td class="email-order-details-value-bold">{{canceledOn}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Canceled By</td>
              <td class="email-order-details-value-bold">{{canceledBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Reason</td>
              <td class="email-order-details-value-bold">{{reason}}</td>
            </tr>
            
          </table>
        </div>
      `,
      BROKERAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          The signage removal order {{orderId}} has been canceled by {{signageCompanyName}}. Please see the details below:
        </p>
        
        <h3 class="email-heading-h3">
           Canceled Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Canceled On</td>
              <td class="email-order-details-value-bold">{{canceledOn}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Canceled By</td>
              <td class="email-order-details-value-bold">{{canceledBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent (if any)</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Reason</td>
              <td class="email-order-details-value-bold">{{reason}}</td>
            </tr>
          </table>
        </div>
      `,

      //  <p class="email-paragraph-small">
      //     You can view the order history from your RealEsy portal.
      //   </p>
      BROKERAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          The signage removal order {{orderId}} has been canceled by {{signageCompanyName}}. Please see the details below:
        </p>
        
        <h3 class="email-heading-h3">
          Canceled Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Canceled On</td>
              <td class="email-order-details-value-bold">{{canceledOn}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Canceled By</td>
              <td class="email-order-details-value-bold">{{canceledBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent (if any)</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Reason</td>
              <td class="email-order-details-value-bold">{{reason}}</td>
            </tr>
          </table>
        </div>
      `,

        //  <p class="email-paragraph-small">
        //   You can view the order history from your RealEsy portal.
        // </p>
        BROKERAGE_AGENT: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          Your signage removal order {{orderId}} has been canceled by {{signageCompanyName}}.
        </p>
        
        <h3 class="email-heading-h3">
          Canceled Order Details
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Canceled On</td>
              <td class="email-order-details-value-bold">{{canceledOn}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Reason</td>
              <td class="email-order-details-value-bold">{{reason}}</td>
            </tr>
          </table>
        </div>
      `,

      //  <p class="email-paragraph-small">
      //     If this was unintentional, you can submit a new request from your RealEsy portal.
      //   </p>
      AGENT: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          Your signage removal order {{orderId}} has been canceled by {{signageCompanyName}}.
        </p>
        
        <h3 class="email-heading-h3">
          Canceled Order Details
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Canceled On</td>
              <td class="email-order-details-value-bold">{{canceledOn}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Reason</td>
              <td class="email-order-details-value-bold">{{reason}}</td>
            </tr>
          </table>
        </div>
      `,

      // <p class="email-paragraph-small">
      //     If this was unintentional, you can submit a new request from your RealEsy portal.
      //   </p>
    },
    cta: {
      // SIGNAGE_FIRM: { text: "View Order History", url: "{{viewOrderUrl}}" },
      // SIGNAGE_TEAM_MEMBER: { text: "View Order History", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}`},
      // BROKERAGE_FIRM: { text: "View Order", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}`},
      // BROKERAGE_TEAM_MEMBER: { text: "View Order", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}`},
      // AGENT: { text: "Submit New Removal Request", url: "{{createOrderUrl}}" },
      // AGENT: { text: "Submit New Removal Request", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      // BROKERAGE_AGENT: { text: "Submit New Removal Request", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
    },
    footer: "RealEsy. All rights reserved."
  },
  "Signage Removal order Voided": {
    subject: {
      SIGNAGE_FIRM: "Removal Order {{orderId}} Marked as Voided",
      SIGNAGE_TEAM_MEMBER: "Removal Order {{orderId}} Marked as Voided",
      BROKERAGE_FIRM: "Removal Order {{orderId}} Marked as Voided by {{signageCompanyName}}",
      BROKERAGE_TEAM_MEMBER: "Removal Order {{orderId}} Marked as Voided by {{signageCompanyName}}",
      AGENT: "Your Removal Order {{orderId}} Was Voided",
      BROKERAGE_AGENT: "Your Removal Order {{orderId}} Was Voided",
    },
    title: "Removal Order Voided",
    logoUrl: "", // Logo is imported directly in emailTemplate.ts
    headerBgColor: "#ffffff",
    bodyLines: [], // Using bodyHtml instead
    bodyHtml: {
      SIGNAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          Removal order {{orderId}} has been marked as voided. This order is no longer active.
        </p>
        
        <h3 class="email-heading-h3">
           Voided Order Details
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Voided On</td>
              <td class="email-order-details-value-bold">{{voidedOn}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Voided By</td>
              <td class="email-order-details-value-bold">{{voidedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent (if any)</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Reason Provided</td>
              <td class="email-order-details-value-bold">{{reason}}</td>
            </tr>
          
          </table>
        </div>
        
        <p class="email-paragraph-small">
          If this was unintentional, you can submit a new request from your RealEsy portal.
        </p>
      `,
      SIGNAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          Removal order {{orderId}} has been marked as voided. This order is no longer active.
        </p>
        
        <h3 class="email-heading-h3">
          Voided Order Details
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Voided On</td>
              <td class="email-order-details-value-bold">{{voidedOn}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Voided By</td>
              <td class="email-order-details-value-bold">{{voidedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent (if any)</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Reason Provided</td>
              <td class="email-order-details-value-bold">{{reason}}</td>
            </tr>
          
          </table>
        </div>
        
        <p class="email-paragraph-small">
          If this was unintentional, you can submit a new request from your RealEsy portal.
        </p>
      `,
      BROKERAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
         Your signage removal order {{orderId}} has been voided. This means it has been canceled and will not be processed.
        </p>
        
        <h3 class="email-heading-h3">
          Voided Order Details
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Voided On</td>
              <td class="email-order-details-value-bold">{{voidedOn}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Voided By</td>
              <td class="email-order-details-value-bold">{{voidedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent (if any)</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Reason Provided</td>
              <td class="email-order-details-value-bold">{{reason}}</td>
            </tr>
            
          </table>
        </div>
      `,

        // <p class="email-paragraph-small">
        //   If needed, you can submit a new request from your RealEsy portal.
        // </p>
      BROKERAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          Your signage removal order {{orderId}} has been voided. This means it has been canceled and will not be processed.
        </p>
        
        <h3 class="email-heading-h3">
          Voided Order Details
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Voided On</td>
              <td class="email-order-details-value-bold">{{voidedOn}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Voided By</td>
              <td class="email-order-details-value-bold">{{voidedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent (if any)</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Reason Provided</td>
              <td class="email-order-details-value-bold">{{reason}}</td>
            </tr>
           
          </table>
        </div>
      `,

      //  <p class="email-paragraph-small">
      //     If needed, you can submit a new request from your RealEsy portal.
      //   </p>
         BROKERAGE_AGENT: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          Your signage removal order {{orderId}} has been voided. This means it has been canceled and will not be processed.
        </p>
        
        <h3 class="email-heading-h3">
          Voided Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Voided On</td>
              <td class="email-order-details-value-bold">{{voidedOn}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Reason</td>
              <td class="email-order-details-value-bold">{{reason}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          If needed, you may submit a new request.
        </p>
      `,
      AGENT: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          Your signage removal order {{orderId}} has been voided. This means it has been canceled and will not be processed.
        </p>
        
        <h3 class="email-heading-h3">
          Voided Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Voided On</td>
              <td class="email-order-details-value-bold">{{voidedOn}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Reason</td>
              <td class="email-order-details-value-bold">{{reason}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          If needed, you may submit a new request.
        </p>
      `,
    },
    cta: {
      // BROKERAGE_FIRM: { text: "Submit New Request", url: "{{createOrderUrl}}" },
      // BROKERAGE_FIRM: { text: "Submit New Request", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      // BROKERAGE_TEAM_MEMBER: { text: "Submit New Request", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      // AGENT: { text: "Create New Removal Order", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      // BROKERAGE_AGENT: { text: "Create New Removal Order", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
    },
    footer: "RealEsy. All rights reserved."
  },
  "Signage Removal Status change": {
    subject: {
      SIGNAGE_FIRM: " Removal Order {{orderId}} Status Updated to \"{{newStatus}}\"",
      SIGNAGE_TEAM_MEMBER: " Removal Order {{orderId}} Status Updated to \"{{newStatus}}\"",
      BROKERAGE_FIRM: " Removal Order {{orderId}} Status Updated to \"{{newStatus}}\"",
      BROKERAGE_TEAM_MEMBER: " Removal Order {{orderId}} Status Updated to \"{{newStatus}}\"",
      BROKERAGE_AGENT: "Removal Order {{orderId}} Status Updated to \"{{newStatus}}\"",
      AGENT: "Removal Order {{orderId}} Status Updated to \"{{newStatus}}\"",
    },
    title: "Removal Order Status Updated",
    logoUrl: "", // Logo is imported directly in emailTemplate.ts
    headerBgColor: "#ffffff",
    bodyLines: [], // Using bodyHtml instead
    bodyHtml: {
      SIGNAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          The status of the following removal order has been updated in RealEsy:
        </p>
        
        <h3 class="email-heading-h3">
          Removal Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">New Status</td>
              <td class="email-order-details-value-bold">{{newStatus}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent Name</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Brokerage Firm</td>
              <td class="email-order-details-value-bold">{{brokerageFirm}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Updated By</td>
              <td class="email-order-details-value-bold">{{updatedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Update Time</td>
              <td class="email-order-details-value-bold">{{updateTime}}</td>
            </tr>
           
          </table>
        </div>
      `,

              
        // <p class="email-paragraph-small">
        //   You can view the full order details and next steps in your RealEsy portal.
        // </p>
      SIGNAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          The status of the following removal order has been updated in RealEsy:
        </p>
        
        <h3 class="email-heading-h3">
          Removal Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">New Status</td>
              <td class="email-order-details-value-bold">{{newStatus}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent Name</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Brokerage Firm</td>
              <td class="email-order-details-value-bold">{{brokerageFirm}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Updated By</td>
              <td class="email-order-details-value-bold">{{updatedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Update Time</td>
              <td class="email-order-details-value-bold">{{updateTime}}</td>
            </tr>
           
          </table>
        </div>
      `,
      BROKERAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          The status of the following removal order has been updated in RealEsy:
        </p>
        
        <h3 class="email-heading-h3">
          Removal Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">New Status</td>
              <td class="email-order-details-value-bold">{{newStatus}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent Name</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Brokerage Firm</td>
              <td class="email-order-details-value-bold">{{brokerageFirm}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Updated By</td>
              <td class="email-order-details-value-bold">{{updatedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Update Time</td>
              <td class="email-order-details-value-bold">{{updateTime}}</td>
            </tr>
            
          </table>
        </div>
      `,
      BROKERAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          The status of the following removal order has been updated in RealEsy:
        </p>
        
        <h3 class="email-heading-h3">
          Removal Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">New Status</td>
              <td class="email-order-details-value-bold">{{newStatus}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent Name</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Brokerage Firm</td>
              <td class="email-order-details-value-bold">{{brokerageFirm}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Updated By</td>
              <td class="email-order-details-value-bold">{{updatedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Update Time</td>
              <td class="email-order-details-value-bold">{{updateTime}}</td>
            </tr>
           
          </table>
        </div>
      `,
        BROKERAGE_AGENT: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          The status of the following removal order has been updated in RealEsy:
        </p>
        
        <h3 class="email-heading-h3">
          Removal Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">New Status</td>
              <td class="email-order-details-value-bold">{{newStatus}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent Name</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Brokerage Firm</td>
              <td class="email-order-details-value-bold">{{brokerageFirm}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Updated By</td>
              <td class="email-order-details-value-bold">{{updatedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Update Time</td>
              <td class="email-order-details-value-bold">{{updateTime}}</td>
            </tr>
          </table>
        </div>
      `,
      AGENT: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          The status of the following removal order has been updated in RealEsy:
        </p>
        
        <h3 class="email-heading-h3">
          Removal Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">New Status</td>
              <td class="email-order-details-value-bold">{{newStatus}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent Name</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Brokerage Firm</td>
              <td class="email-order-details-value-bold">{{brokerageFirm}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Updated By</td>
              <td class="email-order-details-value-bold">{{updatedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Update Time</td>
              <td class="email-order-details-value-bold">{{updateTime}}</td>
            </tr>
          </table>
        </div>
      `,
    },
    cta: {
      // BROKERAGE_FIRM: { text: "View Removal Order", url: "{{viewOrderUrl}}" },
      // BROKERAGE_FIRM: { text: "View Removal Order",url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      // BROKERAGE_TEAM_MEMBER: { text: "View Removal Order",url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      // AGENT: { text: "View Removal Order",url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      // BROKERAGE_AGENT: { text: "View Removal Order",url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
    },
    footer: "RealEsy. All rights reserved."
  },
  // "Signage New Office created": {
  //   subject: {
  //     BROKERAGE_FIRM: " New Office Added to Your Realesy Account",
  //     BROKERAGE_TEAM_MEMBER: " You've Been Assigned to an Office in Realesy",
  //   },
  //   title: "New Office Created",
  //   logoUrl: "", // Logo is imported directly in emailTemplate.ts
  //   headerBgColor: "#ffffff",
  //   bodyLines: [], // Using bodyHtml instead
  //   bodyHtml: {
  //     BROKERAGE_FIRM: `
  //       <h2 class="email-heading-h2">
  //         Hi {{firstName}},
  //       </h2>
  //       <p class="email-paragraph">
  //         A new office has been successfully created under your brokerage account.
  //       </p>
        
  //       <h3 class="email-heading-h3">
  //          Office Details
  //       </h3>
        
  //       <div class="email-order-details-card">
  //         <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
  //           <tr>
  //             <td class="email-order-details-field-first">Office Name</td>
  //             <td class="email-order-details-value-bold-first">{{officeName}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Created By</td>
  //             <td class="email-order-details-value-bold">{{createdBy}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Created On</td>
  //             <td class="email-order-details-value-bold">{{createdOn}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Location</td>
  //             <td class="email-order-details-value-bold">{{location}}</td>
  //           </tr>
  //         </table>
  //       </div>
        
  //       <p class="email-paragraph-small">
  //         You can now manage agents, assign orders, and monitor activity from this office in your dashboard.
  //       </p>
  //     `,
  //     BROKERAGE_TEAM_MEMBER: `
  //       <h2 class="email-heading-h2">
  //         Hi {{firstName}},
  //       </h2>
  //       <p class="email-paragraph">
  //         You've been assigned to <strong>{{officeName}}</strong> under your brokerage account in Realesy.
  //       </p>
  //       <p class="email-paragraph">
  //         This office assignment allows you to place orders, manage listings, or collaborate with teammates tied to this location.
  //       </p>
        
  //       <h3 class="email-heading-h3">
  //          Details
  //       </h3>
        
  //       <div class="email-order-details-card">
  //         <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
  //           <tr>
  //             <td class="email-order-details-field-first">Office Name</td>
  //             <td class="email-order-details-value-bold-first">{{officeName}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Assigned By</td>
  //             <td class="email-order-details-value-bold">{{adminName}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Assignment Date</td>
  //             <td class="email-order-details-value-bold">{{assignmentDate}}</td>
  //           </tr>
  //         </table>
  //       </div>
        
  //       <p class="email-paragraph-small">
  //         If you believe this is incorrect, please reach out to your brokerage admin.
  //       </p>
  //     `,
  //   },
  //   cta: {
  //     BROKERAGE_FIRM: { text: "Go to Office Management", url: "{{officeManagementUrl}}" },
  //     BROKERAGE_TEAM_MEMBER: { text: "View Dashboard", url: "{{dashboardUrl}}" },
  //   },
  //   footer: "RealEsy. All rights reserved."
  // },
  // "Signage Office Assigned": {
  //   subject: {
  //     BROKERAGE_FIRM: "Team Member Assigned to Office",
  //     BROKERAGE_TEAM_MEMBER: " You've Been Assigned to an Office in Realesy",
  //   },
  //   title: "Office Assigned",
  //   logoUrl: "", // Logo is imported directly in emailTemplate.ts
  //   headerBgColor: "#ffffff",
  //   bodyLines: [], // Using bodyHtml instead
  //   bodyHtml: {
  //     BROKERAGE_FIRM: `
  //       <h2 class="email-heading-h2">
  //         Hi {{firstName}},
  //       </h2>
  //       <p class="email-paragraph">
        //         A team member has been successfully assigned to an office under your brokerage.
  //       </p>
        
  //       <h3 class="email-heading-h3">
  //          Assignment Details
  //       </h3>
        
  //       <div class="email-order-details-card">
  //         <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
  //           <tr>
  //             <td class="email-order-details-field-first">Team Member</td>
  //             <td class="email-order-details-value-bold-first">{{teamMemberName}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Assigned Office</td>
  //             <td class="email-order-details-value-bold">{{officeName}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Assigned On</td>
  //             <td class="email-order-details-value-bold">{{assignedOn}}</td>
  //           </tr>
  //         </table>
  //       </div>
        
  //       <p class="email-paragraph-small">
  //         You can view or update office assignments anytime in your team management dashboard.
  //       </p>
  //     `,
  //     BROKERAGE_TEAM_MEMBER: `
  //       <h2 class="email-heading-h2">
  //         Hi {{firstName}},
  //       </h2>
  //       <p class="email-paragraph">
  //         You've been assigned to <strong>{{officeName}}</strong> under your brokerage account in Realesy.
  //       </p>
  //       <p class="email-paragraph">
  //         This office assignment allows you to place orders, manage listings, or collaborate with teammates tied to this location.
  //       </p>
        
  //       <h3 class="email-heading-h3">
  //          Details
  //       </h3>
        
  //       <div class="email-order-details-card">
  //         <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
  //           <tr>
  //             <td class="email-order-details-field-first">Office Name</td>
  //             <td class="email-order-details-value-bold-first">{{officeName}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Assigned By</td>
  //             <td class="email-order-details-value-bold">{{adminName}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Assignment Date</td>
  //             <td class="email-order-details-value-bold">{{assignmentDate}}</td>
  //           </tr>
  //         </table>   
  //       </div>
        
  //       <p class="email-paragraph-small">
  //         If you believe this is incorrect, please reach out to your brokerage admin.
  //       </p>
  //     `,
  //   },
  //   cta: {
  //     BROKERAGE_FIRM: { text: "Manage Offices", url: "{{manageOfficesUrl}}" },
  //     BROKERAGE_TEAM_MEMBER: { text: "View Dashboard", url: "{{dashboardUrl}}" },
  //   },
  //   footer: "RealEsy. All rights reserved."
  // },
  
  // ==================== SIGNAGE ORDER EDIT TRIGGERS ====================
  
  "Signage Sales order edit": {
    subject: {
      SIGNAGE_FIRM: " Order {{Order ID}} Updated for {{Signage Company Name}}",
      SIGNAGE_TEAM_MEMBER: " Order {{Order ID}} Updated for {{Signage Company Name}}",
      BROKERAGE_FIRM: " Order {{Order ID}} Updated by {{Signage Company Name}}",
      BROKERAGE_TEAM_MEMBER: " Order {{Order ID}} Updated by {{Signage Company Name}}",
      BROKERAGE_AGENT: " Your Signage Order {{Order ID}} Was Updated",
      AGENT: " Your Signage Order {{Order ID}} Was Updated",
    },
    title: "Order Update Confirmation",
    logoUrl: "https://i.ibb.co/nXS3cC6/logo.png",
    headerBgColor: "#393a3d",
    bodyHtml: {
      SIGNAGE_FIRM: `
        <p class="email-paragraph">
          Hi {{First Name}},
        </p>
        <p class="email-paragraph">
          You have updated sales order {{Order ID}} for {{Signage Company Name}}. Please review the updated information below:
        </p>
        <h3 class="email-heading-h3">
         Updated Order Summary
        </h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{Order Number}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Updated By</td>
              <td class="email-order-details-value-bold">{{Signage Admin Name}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Update Date</td>
              <td class="email-order-details-value-bold">{{Update Date}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Changes Made</td>
              <td class="email-order-details-value-bold">{{Changes Made}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent (if any)</td>
              <td class="email-order-details-value-bold">{{Agent Name}}</td>
            </tr>
          </table>
        </div>
      `,

      //  <p class="email-paragraph">
      //     <a href="{{View Updated Order}}" class="email-button">View Updated Order</a>
      //   </p>
      SIGNAGE_TEAM_MEMBER: `
        <p class="email-paragraph">
          Hi {{First Name}},
        </p>
        <p class="email-paragraph">
          A sales order {{Order ID}} for {{Signage Company Name}} has been updated. Please review the updated information below:
        </p>
        <h3 class="email-heading-h3">
         Updated Order Summary
        </h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{Order Number}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Updated By</td>
              <td class="email-order-details-value-bold">{{Signage Admin Name}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Update Date</td>
              <td class="email-order-details-value-bold">{{Update Date}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Changes Made</td>
              <td class="email-order-details-value-bold">{{Changes Made}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent (if any)</td>
              <td class="email-order-details-value-bold">{{Agent Name}}</td>
            </tr>
          </table>
        </div>
       
      `,
      //  <p class="email-paragraph">
      //     <a href="{{View Updated Order}}" class="email-button">View Updated Order</a>
      //   </p>
      BROKERAGE_FIRM: `
        <p class="email-paragraph">
          Hi {{First Name}},
        </p>
        <p class="email-paragraph">
          Your signage order {{Order ID}} has been updated by {{Signage Company Name}}. Please review the updated information below:
        </p>
        <h3 class="email-heading-h3">
         Updated Order Summary
        </h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{Order Number}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Updated By</td>
              <td class="email-order-details-value-bold">{{Signage Admin Name}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Update Date</td>
              <td class="email-order-details-value-bold">{{Update Date}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Changes Made</td>
              <td class="email-order-details-value-bold">{{Changes Made}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent (if any)</td>
              <td class="email-order-details-value-bold">{{Agent Name}}</td>
            </tr>
           
          </table>
        </div>
       
      `,
       // <tr>
            //   <td class="email-order-details-field">Assigned To</td>
            //   <td class="email-order-details-value-bold">{{Assigned To Name}} ({{Assigned To Email}})</td>
            // </tr>
      //  <p class="email-paragraph">
      //     <a href="{{View Updated Order}}" class="email-button">View Updated Order</a>
      //   </p>
      BROKERAGE_TEAM_MEMBER: `
        <p class="email-paragraph">
          Hi {{First Name}},
        </p>
        <p class="email-paragraph">
          Your signage order {{Order ID}} has been updated by {{Signage Company Name}}. Please review the updated information below:
        </p>
        <h3 class="email-heading-h3">
         Updated Order Summary
        </h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{Order Number}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Updated By</td>
              <td class="email-order-details-value-bold">{{Signage Admin Name}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Update Date</td>
              <td class="email-order-details-value-bold">{{Update Date}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Changes Made</td>
              <td class="email-order-details-value-bold">{{Changes Made}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent (if any)</td>
              <td class="email-order-details-value-bold">{{Agent Name}}</td>
            </tr>
          
          </table>
        </div>
      
      `,
        // <tr>
        //       <td class="email-order-details-field">Assigned To</td>
        //       <td class="email-order-details-value-bold">{{Assigned To Name}} ({{Assigned To Email}})</td>
        //     </tr>
        //       <p class="email-paragraph">
        //   <a href="{{View Updated Order}}" class="email-button">View Updated Order</a>
        // </p>
       BROKERAGE_AGENT: `
        <p class="email-paragraph">
          Hi {{Agent First Name}},
        </p>
        <p class="email-paragraph">
          The signage company {{Supplier Name}} has updated your order {{Order ID}}. Please find the revised details below:
        </p>
        <h3 class="email-heading-h3">
          Order Update Snapshot
        </h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Updated On</td>
              <td class="email-order-details-value-bold-first">{{Updated On}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Changes Made</td>
              <td class="email-order-details-value-bold">{{Changes Made}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{Install Location}}</td>
            </tr>
          </table>
        </div>
      `,
      //  <p class="email-paragraph">
      //     <a href="{{View My Orders}}" class="email-button">View My Orders</a>
      //   </p>
      AGENT: `
        <p class="email-paragraph">
          Hi {{Agent First Name}},
        </p>
        <p class="email-paragraph">
          The signage company {{Supplier Name}} has updated your order {{Order ID}}. Please find the revised details below:
        </p>
        <h3 class="email-heading-h3">
          Order Update Snapshot
        </h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Updated On</td>
              <td class="email-order-details-value-bold-first">{{Updated On}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Changes Made</td>
              <td class="email-order-details-value-bold">{{Changes Made}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{Install Location}}</td>
            </tr>
          </table>
        </div>
       
      `,
      //  <p class="email-paragraph">
      //     <a href="{{View My Orders}}" class="email-button">View My Orders</a>
      //   </p>
    },
    footer: "RealEsy. All rights reserved.",
  },
  
  "Signage Installation order edit": {
    subject: {
      SIGNAGE_FIRM: " Installation Order {{Order ID}} Updated",
      SIGNAGE_TEAM_MEMBER: " Installation Order {{Order ID}} Updated",
      BROKERAGE_FIRM: " Installation Order {{Order ID}} Updated by {{Signage Company Name}}",
      BROKERAGE_TEAM_MEMBER: " Installation Order {{Order ID}} Updated by {{Signage Company Name}}",
      AGENT: "Your Installation Order {{Order ID}} Was Updated",
      BROKERAGE_AGENT: " Your Installation Order {{Order ID}} Was Updated"
    },
    title: "Installation Order Update Confirmation",
    logoUrl: "https://i.ibb.co/nXS3cC6/logo.png",
    headerBgColor: "#393a3d",
    bodyHtml: {
      SIGNAGE_FIRM: `
        <p class="email-paragraph">
          Hi {{First Name}},
        </p>
        <p class="email-paragraph">
          Installation Order {{Order ID}} has been updated. Please review the latest details below:
        </p>
        <h3 class="email-heading-h3">
          Updated Order Summary
        </h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{Order Number}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Edited On</td>
              <td class="email-order-details-value-bold">{{Edited On}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Edited By</td>
              <td class="email-order-details-value-bold">{{Signage Team Member Name}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Changes Made</td>
              <td class="email-order-details-value-bold">{{Changes Made}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{Install Location}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent (if any)</td>
              <td class="email-order-details-value-bold">{{Agent Name}}</td>
            </tr>
            
          </table>
        </div>
        <p class="email-paragraph">
          <a href="{{View Updated Order}}" class="email-button">View Updated Order</a>
        </p>
      `,

      // <tr>
            //   <td class="email-order-details-field">Assigned To</td>
            //   <td class="email-order-details-value-bold">{{Assigned To Name}} ({{Assigned To Email}})</td>
            // </tr>
      SIGNAGE_TEAM_MEMBER: `
        <p class="email-paragraph">
          Hi {{First Name}},
        </p>
        <p class="email-paragraph">
          Installation Order {{Order ID}} has been updated. Please review the latest details below:
        </p>
        <h3 class="email-heading-h3">
          Updated Order Summary
        </h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{Order Number}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Edited On</td>
              <td class="email-order-details-value-bold">{{Edited On}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Edited By</td>
              <td class="email-order-details-value-bold">{{Signage Team Member Name}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Changes Made</td>
              <td class="email-order-details-value-bold">{{Changes Made}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{Install Location}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent (if any)</td>
              <td class="email-order-details-value-bold">{{Agent Name}}</td>
            </tr>
           
          </table>
        </div>
        <p class="email-paragraph">
          <a href="{{View Updated Order}}" class="email-button">View Updated Order</a>
        </p>
      `,
       // <tr>
            //   <td class="email-order-details-field">Assigned To</td>
            //   <td class="email-order-details-value-bold">{{Assigned To Name}} ({{Assigned To Email}})</td>
            // </tr>
      BROKERAGE_FIRM: `
        <p class="email-paragraph">
          Hi {{First Name}},
        </p>
        <p class="email-paragraph">
          The signage team at {{Signage Company Name}} has made changes to Installation Order {{Order ID}}. Please review the updated information below:
        </p>
        <h3 class="email-heading-h3">
          Updated Order Summary
        </h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{Order Number}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Edited On</td>
              <td class="email-order-details-value-bold">{{Edited On}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Edited By</td>
              <td class="email-order-details-value-bold">{{Signage Team Member Name}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Changes Made</td>
              <td class="email-order-details-value-bold">{{Changes Made}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{Install Location}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent (if any)</td>
              <td class="email-order-details-value-bold">{{Agent Name}}</td>
            </tr>
           
          </table>
        </div>
       
      `,
//  <p class="email-paragraph">
//           <a href="{{View Updated Order}}" class="email-button">View Updated Order</a>
//         </p>
       // <tr>
            //   <td class="email-order-details-field">Assigned To</td>
            //   <td class="email-order-details-value-bold">{{Assigned To Name}} ({{Assigned To Email}})</td>
            // </tr>
      BROKERAGE_TEAM_MEMBER: `
        <p class="email-paragraph">
          Hi {{First Name}},
        </p>
        <p class="email-paragraph">
          The signage team at {{Signage Company Name}} has made changes to Installation Order {{Order ID}}. Please review the updated information below:
        </p>
        <h3 class="email-heading-h3">
          Updated Order Summary
        </h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{Order Number}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Edited On</td>
              <td class="email-order-details-value-bold">{{Edited On}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Edited By</td>
              <td class="email-order-details-value-bold">{{Signage Team Member Name}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Changes Made</td>
              <td class="email-order-details-value-bold">{{Changes Made}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{Install Location}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent (if any)</td>
              <td class="email-order-details-value-bold">{{Agent Name}}</td>
            </tr>
            
          </table>
        </div>
       
      `,

      //  <p class="email-paragraph">
      //     <a href="{{View Updated Order}}" class="email-button">View Updated Order</a>
      //   </p>
      // <tr>
            //   <td class="email-order-details-field">Assigned To</td>
            //   <td class="email-order-details-value-bold">{{Assigned To Name}} ({{Assigned To Email}})</td>
            // </tr>
        BROKERAGE_AGENT: `
        <p class="email-paragraph">
          Hi {{Agent First Name}},
        </p>
        <p class="email-paragraph">
          Your installation order {{Order ID}} has been updated by {{Signage Company Name}}. Below are the revised details:
        </p>
        <h3 class="email-heading-h3">
          What's Been Updated
        </h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Install Address</td>
              <td class="email-order-details-value-bold-first">{{Property Location}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Install Date</td>
              <td class="email-order-details-value-bold">{{Install Date}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Updated Notes</td>
              <td class="email-order-details-value-bold">{{Installer comments}}</td>
            </tr>
          </table>
        </div>
      
      `,
        // <p class="email-paragraph">
        //   <a href="{{Track My Orders}}" class="email-button">Track My Orders</a>
        // </p>
      AGENT: `
        <p class="email-paragraph">
          Hi {{Agent First Name}},
        </p>
        <p class="email-paragraph">
          Your installation order {{Order ID}} has been updated by {{Signage Company Name}}. Below are the revised details:
        </p>
        <h3 class="email-heading-h3">
           What's Been Updated
        </h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Install Address</td>
              <td class="email-order-details-value-bold-first">{{Property Location}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Install Date</td>
              <td class="email-order-details-value-bold">{{Install Date}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Updated Notes</td>
              <td class="email-order-details-value-bold">{{Installer comments}}</td>
            </tr>
          </table>
        </div>
       
      `,

      //  <p class="email-paragraph">
      //     <a href="{{Track My Orders}}" class="email-button">Track My Orders</a>
      //   </p>
    },
    footer: "RealEsy. All rights reserved.",
  },
  
  "Signage Removal order edit": {
    subject: {
      SIGNAGE_FIRM: " Removal Order {{Order ID}} Updated",
      SIGNAGE_TEAM_MEMBER: " Removal Order {{Order ID}} Updated",
      BROKERAGE_FIRM: " Removal Order {{Order ID}} Updated by {{Signage Company Name}}",
      BROKERAGE_TEAM_MEMBER: " Removal Order {{Order ID}} Updated by {{Signage Company Name}}",
      AGENT: " Your Signage Removal Order {{Order ID}} Was Updated",
      BROKERAGE_AGENT: " Your Signage Removal Order {{Order ID}} Was Updated"
    },
    title: "Removal Order Update Confirmation",
    logoUrl: "https://i.ibb.co/nXS3cC6/logo.png",
    headerBgColor: "#393a3d",
    bodyHtml: {
      SIGNAGE_FIRM: `
        <p class="email-paragraph">
          Hi {{First Name}},
        </p>
        <p class="email-paragraph">
          Removal order {{Order ID}} has been updated. Please see the updated details below:
        </p>
        <h3 class="email-heading-h3">
          Updated Removal Order Summary
        </h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{Order Number}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Updated On</td>
              <td class="email-order-details-value-bold">{{Updated On}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Updated By</td>
              <td class="email-order-details-value-bold">{{Signage Team Member Name}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Changes Made</td>
              <td class="email-order-details-value-bold">{{Changes Made}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent (if any)</td>
              <td class="email-order-details-value-bold">{{Agent Name}}</td>
            </tr>
          </table>
        </div>
        <p class="email-paragraph">
          <a href="{{View Updated Order}}" class="email-button">View Updated Order</a>
        </p>
      `,
      SIGNAGE_TEAM_MEMBER: `
        <p class="email-paragraph">
          Hi {{First Name}},
        </p>
        <p class="email-paragraph">
          Removal order {{Order ID}} has been updated. Please see the updated details below:
        </p>
        <h3 class="email-heading-h3">
          Updated Removal Order Summary
        </h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{Order Number}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Updated On</td>
              <td class="email-order-details-value-bold">{{Updated On}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Updated By</td>
              <td class="email-order-details-value-bold">{{Signage Team Member Name}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Changes Made</td>
              <td class="email-order-details-value-bold">{{Changes Made}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent (if any)</td>
              <td class="email-order-details-value-bold">{{Agent Name}}</td>
            </tr>
          </table>
        </div>
        <p class="email-paragraph">
          <a href="{{View Updated Order}}" class="email-button">View Updated Order</a>
        </p>
      `,
      BROKERAGE_FIRM: `
        <p class="email-paragraph">
          Hi {{First Name}},
        </p>
        <p class="email-paragraph">
          The signage company {{Signage Company Name}} has updated the removal order {{Order ID}}. Please see the updated details below:
        </p>
        <h3 class="email-heading-h3">
          Updated Removal Order Summary
        </h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{Order Number}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Updated On</td>
              <td class="email-order-details-value-bold">{{Updated On}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Updated By</td>
              <td class="email-order-details-value-bold">{{Signage Team Member Name}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Changes Made</td>
              <td class="email-order-details-value-bold">{{Changes Made}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent (if any)</td>
              <td class="email-order-details-value-bold">{{Agent Name}}</td>
            </tr>
          </table>
        </div>
       
      `,
      //  <p class="email-paragraph">
      //     <a href="{{View Updated Order}}" class="email-button">View Updated Order</a>
      //   </p>
      BROKERAGE_TEAM_MEMBER: `
        <p class="email-paragraph">
          Hi {{First Name}},
        </p>
        <p class="email-paragraph">
          The signage company {{Signage Company Name}} has updated the removal order {{Order ID}}. Please see the updated details below:
        </p>
        <h3 class="email-heading-h3">
          Updated Removal Order Summary
        </h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">{{Order Number}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Updated On</td>
              <td class="email-order-details-value-bold">{{Updated On}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Updated By</td>
              <td class="email-order-details-value-bold">{{Signage Team Member Name}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Changes Made</td>
              <td class="email-order-details-value-bold">{{Changes Made}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent (if any)</td>
              <td class="email-order-details-value-bold">{{Agent Name}}</td>
            </tr>
          </table>
        </div>
      
      `,

        // <p class="email-paragraph">
        //   <a href="{{View Updated Order}}" class="email-button">View Updated Order</a>
        // </p>
       BROKERAGE_AGENT: `
        <p class="email-paragraph">
          Hi {{Agent First Name}},
        </p>
        <p class="email-paragraph">
          Your signage removal order {{Order ID}} has been updated by {{Signage Company Name}}.
        </p>
        <h3 class="email-heading-h3">
          Updated Information
        </h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Removal Location</td>
              <td class="email-order-details-value-bold-first">{{Property Address}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Scheduled Date</td>
              <td class="email-order-details-value-bold">{{Updated Date}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Installer Notes</td>
              <td class="email-order-details-value-bold">{{Optional field}}</td>
            </tr>
          </table>
        </div>
       
      `,

        //  <p class="email-paragraph">
        //   You can track this order from your portal at any time.
        // </p>

      //  <p class="email-paragraph">
      //     <a href="{{Track My Orders}}" class="email-button">Track My Orders</a>
      //   </p>
      AGENT: `
        <p class="email-paragraph">
          Hi {{Agent First Name}},
        </p>
        <p class="email-paragraph">
          Your signage removal order {{Order ID}} has been updated by {{Signage Company Name}}.
        </p>
        <h3 class="email-heading-h3">
          Updated Information
        </h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Removal Location</td>
              <td class="email-order-details-value-bold-first">{{Property Address}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Scheduled Date</td>
              <td class="email-order-details-value-bold">{{Updated Date}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Installer Notes</td>
              <td class="email-order-details-value-bold">{{Optional field}}</td>
            </tr>
          </table>
        </div>
      `,

      //  <p class="email-paragraph">
      //     <a href="{{Track My Orders}}" class="email-button">Track My Orders</a>
      //   </p>
    },
    footer: "RealEsy. All rights reserved.",
  },

  // "Supplier Linked": {
  //   subject: {
  //     AGENT: " Supplier Linked Successfully",
  //     BROKERAGE_AGENT: " Supplier Successfully Linked to Your Brokerage",
  //     BROKERAGE_FIRM: " Supplier Successfully Linked to Your Brokerage",
  //     BROKERAGE_TEAM_MEMBER: " Supplier Successfully Linked to Your Brokerage",
  //     SIGNAGE_FIRM: " You've Been Linked as a Supplier by {{Agent or Brokerage Name}}",
  //     SIGNAGE_TEAM_MEMBER: " You've Been Linked as a Supplier by {{Agent or Brokerage Name}}",
  //   },
  //   title: "Supplier Linked",
  //   logoUrl: "",
  //   headerBgColor: "#ffffff",
  //   bodyLines: [],
  //   bodyHtml: {
  //     AGENT: `
  //       <h2 class="email-heading-h2">
  //         Hi {{Agent First Name}},
  //       </h2>
  //       <p class="email-paragraph">
  //         You've successfully linked <strong>{{Supplier Name}}</strong> as your signage provider in Realesy. You can now begin sending installation and removal orders directly to them.
  //       </p>
        
  //       <h3 class="email-heading-h3">
  //          Linked Supplier Info
  //       </h3>
        
  //       <div class="email-order-details-card">
  //         <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
  //           <tr>
  //             <td class="email-order-details-field-first">Supplier Name</td>
  //             <td class="email-order-details-value-bold-first">{{Supplier Name}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Linked On</td>
  //             <td class="email-order-details-value-bold">{{Linked On}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Contact Email</td>
  //             <td class="email-order-details-value-link">{{Supplier Contact Email}}</td>
  //           </tr>
  //         </table>
  //       </div>
        
  //       <p class="email-paragraph">
  //         <a href="{{View My Suppliers}}" class="email-button">View My Suppliers</a>
  //       </p>
  //     `,
  //      BROKERAGE_AGENT: `
  //       <h2 class="email-heading-h2">
  //         Hi {{Agent First Name}},
  //       </h2>
  //       <p class="email-paragraph">
  //         You've successfully linked <strong>{{Supplier Name}}</strong> as your signage provider in Realesy. You can now begin sending installation and removal orders directly to them.
  //       </p>
        
  //       <h3 class="email-heading-h3">
  //          Linked Supplier Info
  //       </h3>
        
  //       <div class="email-order-details-card">
  //         <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
  //           <tr>
  //             <td class="email-order-details-field-first">Supplier Name</td>
  //             <td class="email-order-details-value-bold-first">{{Supplier Name}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Linked On</td>
  //             <td class="email-order-details-value-bold">{{Linked On}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Contact Email</td>
  //             <td class="email-order-details-value-link">{{Supplier Contact Email}}</td>
  //           </tr>
  //         </table>
  //       </div>
        
  //       <p class="email-paragraph">
  //         <a href="{{View My Suppliers}}" class="email-button">View My Suppliers</a>
  //       </p>
  //     `,
  //     BROKERAGE_FIRM: `
  //       <h2 class="email-heading-h2">
  //         Hi {{First Name}},
  //       </h2>
  //       <p class="email-paragraph">
  //         You've successfully linked a supplier to your brokerage account in Realesy.
  //       </p>
  //       <p class="email-paragraph">
  //         This supplier can now be assigned to installation jobs, signage requests, and other service tasks directly from your portal.
  //       </p>
        
  //       <h3 class="email-heading-h3">
  //          Supplier Details
  //       </h3>
        
  //       <div class="email-order-details-card">
  //         <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
  //           <tr>
  //             <td class="email-order-details-field-first">Supplier Name</td>
  //             <td class="email-order-details-value-bold-first">{{Supplier Company Name}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Type</td>
  //             <td class="email-order-details-value-bold">{{Type}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Linked On</td>
  //             <td class="email-order-details-value-bold">{{Linked On}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Linked By</td>
  //             <td class="email-order-details-value-bold">{{Your Name}}</td>
  //           </tr>
  //         </table>
  //       </div>
        
  //       <p class="email-paragraph">
  //         <a href="{{Manage Suppliers}}" class="email-button">Manage Suppliers</a>
  //       </p>
        
  //       <p class="email-paragraph-small">
  //         If this link was created in error, you can unlink the supplier anytime from the Supplier Management page.
  //       </p>
  //     `,
  //     BROKERAGE_TEAM_MEMBER: `
  //       <h2 class="email-heading-h2">
  //         Hi {{First Name}},
  //       </h2>
  //       <p class="email-paragraph">
  //         You've successfully linked a supplier to your brokerage account in Realesy.
  //       </p>
  //       <p class="email-paragraph">
  //         This supplier can now be assigned to installation jobs, signage requests, and other service tasks directly from your portal.
  //       </p>
        
  //       <h3 class="email-heading-h3">
  //          Supplier Details
  //       </h3>
        
  //       <div class="email-order-details-card">
  //         <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
  //           <tr>
  //             <td class="email-order-details-field-first">Supplier Name</td>
  //             <td class="email-order-details-value-bold-first">{{Supplier Company Name}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Type</td>
  //             <td class="email-order-details-value-bold">{{Type}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Linked On</td>
  //             <td class="email-order-details-value-bold">{{Linked On}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Linked By</td>
  //             <td class="email-order-details-value-bold">{{Your Name}}</td>
  //           </tr>
  //         </table>
  //       </div>
        
  //       <p class="email-paragraph">
  //         <a href="{{Manage Suppliers}}" class="email-button">Manage Suppliers</a>
  //       </p>
        
  //       <p class="email-paragraph-small">
  //         If this link was created in error, you can unlink the supplier anytime from the Supplier Management page.
  //       </p>
  //     `,
  //     SIGNAGE_FIRM: `
  //       <h2 class="email-heading-h2">
  //         Hi {{First Name}},
  //       </h2>
  //       <p class="email-paragraph">
  //         You've been linked as a supplier by <strong>{{Agent or Brokerage Name}}</strong> in RealEsy. You can now begin receiving their installation and removal requests.
  //       </p>
        
  //       <h3 class="email-heading-h3">
  //          Link Summary
  //       </h3>
        
  //       <div class="email-order-details-card">
  //         <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
  //           <tr>
  //             <td class="email-order-details-field-first">Linked By</td>
  //             <td class="email-order-details-value-bold-first">{{Agent or Brokerage Name}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Linked On</td>
  //             <td class="email-order-details-value-bold">{{Linked On}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Order Eligibility</td>
  //             <td class="email-order-details-value-bold">Installation & Removal Jobs</td>
  //           </tr>
  //         </table>
  //       </div>
        
  //       <p class="email-paragraph">
  //         You can manage linked customers from your RealEsy portal.
  //       </p>
        
  //       <p class="email-paragraph">
  //         <a href="{{View Linked Agents/Firms}}" class="email-button">View Linked Agents/Firms</a>
  //       </p>
  //     `,
  //     SIGNAGE_TEAM_MEMBER: `
  //       <h2 class="email-heading-h2">
  //         Hi {{First Name}},
  //       </h2>
  //       <p class="email-paragraph">
  //         You've been linked as a supplier by <strong>{{Agent or Brokerage Name}}</strong> in RealEsy. You can now begin receiving their installation and removal requests.
  //       </p>
        
  //       <h3 class="email-heading-h3">
  //          Link Summary
  //       </h3>
        
  //       <div class="email-order-details-card">
  //         <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
  //           <tr>
  //             <td class="email-order-details-field-first">Linked By</td>
  //             <td class="email-order-details-value-bold-first">{{Agent or Brokerage Name}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Linked On</td>
  //             <td class="email-order-details-value-bold">{{Linked On}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Order Eligibility</td>
  //             <td class="email-order-details-value-bold">Installation & Removal Jobs</td>
  //           </tr>
  //         </table>
  //       </div>
        
  //       <p class="email-paragraph">
  //         You can manage linked customers from your RealEsy portal.
  //       </p>
        
  //       <p class="email-paragraph">
  //         <a href="{{View Linked Agents/Firms}}" class="email-button">View Linked Agents/Firms</a>
  //       </p>
  //     `,
  //   },
  //   cta: {
  //     // AGENT: { text: "View My Suppliers", url: "{{View My Suppliers}}" },
  //     // BROKERAGE_FIRM: { text: "Manage Suppliers", url: "{{Manage Suppliers}}" },
  //     // BROKERAGE_TEAM_MEMBER: { text: "Manage Suppliers", url: "{{Manage Suppliers}}" },
  //     // SIGNAGE_FIRM: { text: "View Linked Agents/Firms", url: "{{View Linked Agents/Firms}}" },
  //     // SIGNAGE_TEAM_MEMBER: { text: "View Linked Agents/Firms", url: "{{View Linked Agents/Firms}}" },
  //     // BROKERAGE_AGENT: { text: "View My Suppliers", url: "{{View My Suppliers}}" },

  //     AGENT: { text: "View My Suppliers", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
  //     BROKERAGE_FIRM: { text: "Manage Suppliers", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
  //     BROKERAGE_TEAM_MEMBER: { text: "Manage Suppliers", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
  //     SIGNAGE_FIRM: { text: "View Linked Agents/Firms", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
  //     SIGNAGE_TEAM_MEMBER: { text: "View Linked Agents/Firms", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
  //     BROKERAGE_AGENT: { text: "View My Suppliers", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
  //   },
  //   footer: "RealEsy. All rights reserved.",
  // },

  // "Supplier Unlink Request": {
  //   subject: {
  //     SIGNAGE_FIRM: " Product Unlink Requested by {{Agent or Brokerage Name}}",
  //     SIGNAGE_TEAM_MEMBER: " Product Unlink Requested by {{Agent or Brokerage Name}}",
  //   },
  //   title: "Unlink Request",
  //   logoUrl: "",
  //   headerBgColor: "#ffffff",
  //   bodyLines: [],
  //   bodyHtml: {
  //     SIGNAGE_FIRM: `
  //       <h2 class="email-heading-h2">
  //         Hi {{First Name}},
  //       </h2>
  //       <p class="email-paragraph">
  //         A request has been submitted by <strong>{{Agent or Brokerage Name}}</strong> to unlink a product from your signage inventory.
  //       </p>
        
  //       <h3 class="email-heading-h3">
  //          Unlink Request Details
  //       </h3>
        
  //       <div class="email-order-details-card">
  //         <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
  //           <tr>
  //             <td class="email-order-details-field-first">Product Name</td>
  //             <td class="email-order-details-value-bold-first">{{Product Title}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Requested By</td>
  //             <td class="email-order-details-value-bold">{{Agent or Brokerage Name}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Request Date</td>
  //             <td class="email-order-details-value-bold">{{Request Date}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Reason (if provided)</td>
  //             <td class="email-order-details-value-bold">{{Agent note, if any}}</td>
  //           </tr>
  //         </table>
  //       </div>
        
  //       <p class="email-paragraph">
  //         Please review and approve or reject the unlink request in your portal.
  //       </p>
        
  //       <p class="email-paragraph">
  //         <a href="{{Review Unlink Requests}}" class="email-button">Review Unlink Requests</a>
  //       </p>
  //     `,
  //     SIGNAGE_TEAM_MEMBER: `
  //       <h2 class="email-heading-h2">
  //         Hi {{First Name}},
  //       </h2>
  //       <p class="email-paragraph">
  //         A request has been submitted by <strong>{{Agent or Brokerage Name}}</strong> to unlink a product from your signage inventory.
  //       </p>
        
  //       <h3 class="email-heading-h3">
  //          Unlink Request Details
  //       </h3>
        
  //       <div class="email-order-details-card">
  //         <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
  //           <tr>
  //             <td class="email-order-details-field-first">Product Name</td>
  //             <td class="email-order-details-value-bold-first">{{Product Title}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Requested By</td>
  //             <td class="email-order-details-value-bold">{{Agent or Brokerage Name}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Request Date</td>
  //             <td class="email-order-details-value-bold">{{Request Date}}</td>
  //           </tr>
  //           <tr>
  //             <td class="email-order-details-field">Reason (if provided)</td>
  //             <td class="email-order-details-value-bold">{{Agent note, if any}}</td>
  //           </tr>
  //         </table>
  //       </div>
        
  //       <p class="email-paragraph">
  //         Please review and approve or reject the unlink request in your portal.
  //       </p>
        
  //       <p class="email-paragraph">
  //         <a href="{{Review Unlink Requests}}" class="email-button">Review Unlink Requests</a>
  //       </p>
  //     `,
  //   },
  //   cta: {
  //     // SIGNAGE_FIRM: { text: "Review Unlink Requests", url: "{{Review Unlink Requests}}" },
  //     // SIGNAGE_TEAM_MEMBER: { text: "Review Unlink Requests", url: "{{Review Unlink Requests}}" },
  //     SIGNAGE_TEAM_MEMBER: { text: "Review Unlink Requests", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
  //     SIGNAGE_FIRM: { text: "Review Unlink Requests", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` }
  //   },
  //   footer: "RealEsy. All rights reserved.",
  // },
  
  // "Product link requested": {
  //   subject: {
  //     SIGNAGE_FIRM: "Signage Product link requested – {{requestedBy}}",
  //     SIGNAGE_TEAM_MEMBER: " Supplier Updated Product Link Status",
  //     BROKERAGE_FIRM: " Supplier Updated Product Link Status",
  //     BROKERAGE_TEAM_MEMBER: " Supplier Updated Product Link Status",
  //     AGENT: " Product Status Updated by {{supplierName}}",
  //     BROKERAGE_AGENT: " Product Status Updated by {{supplierName}}",
  //   },
  //   title: "Product Link Requested",
  //   logoUrl: "", // Logo is imported directly in emailTemplate.ts
  //   headerBgColor: "#ffffff",
  //   bodyLines: [], // Using bodyHtml instead
  //   bodyHtml: {
  //     SIGNAGE_FIRM: `
  //       <h2 class="email-heading-h2">
  //         Hi {{firstName}},
  //       </h2>
  //       <p class="email-paragraph">
  //         A request has been submitted by {{requestedBy}} to link a product from your signage inventory.
  //       </p>
        
  //       <h3 class="email-heading-h3">
  //         <span class="email-order-details-icon"></span>
  //         Order Details
  //       </h3>
        
  //       <div class="email-order-details-card">
  //         <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
  //           <tr class="email-table-row-header">
  //             <td class="email-order-details-field-first">Field</td>
  //             <td class="email-order-details-field-first">Value</td>
  //           </tr>
  //           <tr class="email-table-row-even">
  //             <td class="email-order-details-field">Product Name</td>
  //             <td class="email-order-details-value">{{productName}}</td>
  //           </tr>
  //           <tr class="email-table-row-odd">
  //             <td class="email-order-details-field">Requested By</td>
  //             <td class="email-order-details-value">{{requestedBy}}</td>
  //           </tr>
  //           <tr class="email-table-row-even">
  //             <td class="email-order-details-field">Request Date</td>
  //             <td class="email-order-details-value">{{requestDate}}</td>
  //           </tr>
  //           <tr class="email-table-row-odd">
  //             <td class="email-order-details-field">Reason</td>
  //             <td class="email-order-details-value">{{reason}}</td>
  //           </tr>
  //         </table>
  //       </div>
        
  //       <p class="email-paragraph-small">
  //         Please review and approve or reject the Link request in your portal.
  //       </p>
  //     `,
  //     SIGNAGE_TEAM_MEMBER: `
  //       <h2 class="email-heading-h2">
  //         Hi {{firstName}},
  //       </h2>
  //       <p class="email-paragraph">
  //         A supplier has updated the status of a product linked to your signage account.
  //       </p>
        
  //       <h3 class="email-heading-h3">
  //         <span class="email-order-details-icon"></span>
  //         Product Link Status Update
  //       </h3>
        
  //       <div class="email-order-details-card">
  //         <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
  //           <tr class="email-table-row-even">
  //             <td class="email-order-details-field-first">Product Name</td>
  //             <td class="email-order-details-value-bold-first">{{productName}}</td>
  //           </tr>
  //           <tr class="email-table-row-odd">
  //             <td class="email-order-details-field">Supplier</td>
  //             <td class="email-order-details-value-bold">{{supplierName}}</td>
  //           </tr>
  //           <tr class="email-table-row-even">
  //             <td class="email-order-details-field">New Status</td>
  //             <td class="email-order-details-value-bold">{{newStatus}}</td>
  //           </tr>
  //           <tr class="email-table-row-odd">
  //             <td class="email-order-details-field">Updated On</td>
  //             <td class="email-order-details-value-bold">{{updatedOn}}</td>
  //           </tr>
  //         </table>
  //       </div>
  //     `,
  //     BROKERAGE_FIRM: `
  //       <h2 class="email-heading-h2">
  //         Hi {{firstName}},
  //       </h2>
  //       <p class="email-paragraph">
  //         A supplier has updated the status of a product linked to your brokerage account.
  //       </p>
        
  //       <h3 class="email-heading-h3">
  //         <span class="email-order-details-icon"></span>
  //         Product Link Status Update
  //       </h3>
        
  //       <div class="email-order-details-card">
  //         <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
  //           <tr class="email-table-row-even">
  //             <td class="email-order-details-field-first">Product Name</td>
  //             <td class="email-order-details-value-bold-first">{{productName}}</td>
  //           </tr>
  //           <tr class="email-table-row-odd">
  //             <td class="email-order-details-field">Supplier</td>
  //             <td class="email-order-details-value-bold">{{supplierName}}</td>
  //           </tr>
  //           <tr class="email-table-row-even">
  //             <td class="email-order-details-field">New Status</td>
  //             <td class="email-order-details-value-bold">{{newStatus}}</td>
  //           </tr>
  //           <tr class="email-table-row-odd">
  //             <td class="email-order-details-field">Updated On</td>
  //             <td class="email-order-details-value-bold">{{updatedOn}}</td>
  //           </tr>
  //         </table>
  //       </div>
  //     `,
  //     BROKERAGE_TEAM_MEMBER: `
  //       <h2 class="email-heading-h2">
  //         Hi {{firstName}},
  //       </h2>
  //       <p class="email-paragraph">
  //         A supplier has updated the status of a product linked to your brokerage account.
  //       </p>
        
  //       <h3 class="email-heading-h3">
  //         <span class="email-order-details-icon"></span>
  //         Product Link Status Update
  //       </h3>
        
  //       <div class="email-order-details-card">
  //         <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
  //           <tr class="email-table-row-even">
  //             <td class="email-order-details-field-first">Product Name</td>
  //             <td class="email-order-details-value-bold-first">{{productName}}</td>
  //           </tr>
  //           <tr class="email-table-row-odd">
  //             <td class="email-order-details-field">Supplier</td>
  //             <td class="email-order-details-value-bold">{{supplierName}}</td>
  //           </tr>
  //           <tr class="email-table-row-even">
  //             <td class="email-order-details-field">New Status</td>
  //             <td class="email-order-details-value-bold">{{newStatus}}</td>
  //           </tr>
  //           <tr class="email-table-row-odd">
  //             <td class="email-order-details-field">Updated On</td>
  //             <td class="email-order-details-value-bold">{{updatedOn}}</td>
  //           </tr>
  //         </table>
  //       </div>
  //     `,
  //     BROKERAGE_AGENT: `
  //       <h2 class="email-heading-h2">
  //         Hi {{firstName}},
  //       </h2>
  //       <p class="email-paragraph">
  //         {{supplierName}} has updated the status of one of your linked products in Realesy.
  //       </p>
        
  //       <h3 class="email-heading-h3">
  //         <span class="email-order-details-icon"></span>
  //         Product Link Status Update
  //       </h3>
        
  //       <div class="email-order-details-card">
  //         <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
  //           <tr class="email-table-row-header">
  //             <td class="email-order-details-field-first">Field Details</td>
  //             <td class="email-order-details-field-first"></td>
  //           </tr>
  //           <tr class="email-table-row-even">
  //             <td class="email-order-details-field">Product Name</td>
  //             <td class="email-order-details-value-bold">{{productName}}</td>
  //           </tr>
  //           <tr class="email-table-row-odd">
  //             <td class="email-order-details-field">New Status</td>
  //             <td class="email-order-details-value-bold">{{newStatus}}</td>
  //           </tr>
  //           <tr class="email-table-row-even">
  //             <td class="email-order-details-field">Updated On</td>
  //             <td class="email-order-details-value-bold">{{updatedOn}}</td>
  //           </tr>
  //           <tr class="email-table-row-odd">
  //             <td class="email-order-details-field">Supplier Comment</td>
  //             <td class="email-order-details-value-bold">"{{supplierComment}}"</td>
  //           </tr>
  //         </table>
  //       </div>
        
  //       <p class="email-paragraph-small">
  //         If the product was rejected or needs changes, you can revise and re-submit as needed.
  //       </p>
  //     `,
  //     AGENT: `
  //       <h2 class="email-heading-h2">
  //         Hi {{firstName}},
  //       </h2>
  //       <p class="email-paragraph">
  //         {{supplierName}} has updated the status of one of your linked products in Realesy.
  //       </p>
        
  //       <h3 class="email-heading-h3">
  //         <span class="email-order-details-icon"></span>
  //         Product Link Status Update
  //       </h3>
        
  //       <div class="email-order-details-card">
  //         <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
  //           <tr class="email-table-row-header">
  //             <td class="email-order-details-field-first">Field Details</td>
  //             <td class="email-order-details-field-first"></td>
  //           </tr>
  //           <tr class="email-table-row-even">
  //             <td class="email-order-details-field">Product Name</td>
  //             <td class="email-order-details-value-bold">{{productName}}</td>
  //           </tr>
  //           <tr class="email-table-row-odd">
  //             <td class="email-order-details-field">New Status</td>
  //             <td class="email-order-details-value-bold">{{newStatus}}</td>
  //           </tr>
  //           <tr class="email-table-row-even">
  //             <td class="email-order-details-field">Updated On</td>
  //             <td class="email-order-details-value-bold">{{updatedOn}}</td>
  //           </tr>
  //           <tr class="email-table-row-odd">
  //             <td class="email-order-details-field">Supplier Comment</td>
  //             <td class="email-order-details-value-bold">"{{supplierComment}}"</td>
  //           </tr>
  //         </table>
  //       </div>
        
  //       <p class="email-paragraph-small">
  //         If the product was rejected or needs changes, you can revise and re-submit as needed.
  //       </p>
  //     `,
  //   },
  //   cta: {
  //     // SIGNAGE_FIRM: { text: " View Order", url: "{{reviewUrl}}" },
  //     // SIGNAGE_TEAM_MEMBER: { text: " View Order", url: "{{reviewUrl}}" },
  //     // BROKERAGE_FIRM: { text: " View Order", url: "{{reviewUrl}}" },
  //     // BROKERAGE_TEAM_MEMBER: { text: " View Order", url: "{{reviewUrl}}" },
  //     // AGENT: { text: " View Order", url: "{{reviewUrl}}" },
  //     // BROKERAGE_AGENT: { text: " View Order", url: "{{reviewUrl}}" },
  //     SIGNAGE_FIRM: { text: "Review Product Link Status", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
  //     SIGNAGE_TEAM_MEMBER: { text: "Review Product Link Status", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
  //     BROKERAGE_FIRM: { text: "Review Product Link Status", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
  //     BROKERAGE_TEAM_MEMBER: { text: "Review Product Link Status", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
  //     AGENT: { text: "Review Product Status", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
  //     BROKERAGE_AGENT: { text: "Review Product Status", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
  //   },
  //   footer: "RealEsy. All rights reserved."
  // },
  // "Decline": {
  //   subject: {
  //     SIGNAGE_FIRM: "Product Link Declined – {{productName}}",
  //     SIGNAGE_TEAM_MEMBER: " Supplier Updated Product Link Status",
  //     BROKERAGE_FIRM: " Supplier Updated Product Link Status",
  //     BROKERAGE_TEAM_MEMBER: " Supplier Updated Product Link Status",
  //     AGENT: " Product Status Updated by {{supplierName}}",
  //     BROKERAGE_AGENT: " Product Status Updated by {{supplierName}}",
  //   },
  //   title: "Product Link Declined",
  //   logoUrl: "", // Logo is imported directly in emailTemplate.ts
  //   headerBgColor: "#ffffff",
  //   bodyLines: [], // Using bodyHtml instead
  //   bodyHtml: {
  //     SIGNAGE_FIRM: `
  //       <h2 class="email-heading-h2">
  //         Hi {{firstName}},
  //       </h2>
  //       <p class="email-paragraph">
  //         The product link request for <strong>{{productName}}</strong> has been declined.
  //       </p>
        
  //       <h3 class="email-heading-h3">
  //         <span class="email-order-details-icon"></span>
  //         Product Details
  //       </h3>
        
  //       <div class="email-order-details-card">
  //         <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
  //           <tr class="email-table-row-header">
  //             <td class="email-order-details-field-first">Field</td>
  //             <td class="email-order-details-field-first">Value</td>
  //           </tr>
  //           <tr class="email-table-row-even">
  //             <td class="email-order-details-field">Product Name</td>
  //             <td class="email-order-details-value">{{productName}}</td>
  //           </tr>
  //           <tr class="email-table-row-odd">
  //             <td class="email-order-details-field">SKU</td>
  //             <td class="email-order-details-value">{{sku}}</td>
  //           </tr>
  //           <tr class="email-table-row-even">
  //             <td class="email-order-details-field">Declined By</td>
  //             <td class="email-order-details-value">{{declinedBy}}</td>
  //           </tr>
  //           <tr class="email-table-row-odd">
  //             <td class="email-order-details-field">Reason</td>
  //             <td class="email-order-details-value">{{reason}}</td>
  //           </tr>
  //           <tr class="email-table-row-even">
  //             <td class="email-order-details-field">Declined On</td>
  //             <td class="email-order-details-value">{{declinedOn}}</td>
  //           </tr>
  //         </table>
  //       </div>
        
  //       <p class="email-paragraph-small">
  //         If you have any questions, please contact the supplier directly.
  //       </p>
  //     `,
  //     BROKERAGE_FIRM: `
  //       <h2 class="email-heading-h2">
  //         Hi {{firstName}},
  //       </h2>
  //       <p class="email-paragraph">
  //         A supplier has updated the status of a product linked to your brokerage account.
  //       </p>
        
  //       <h3 class="email-heading-h3">
  //         <span class="email-order-details-icon"></span>
  //         Product Link Status Update
  //       </h3>
        
  //       <div class="email-order-details-card">
  //         <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
  //           <tr class="email-table-row-even">
  //             <td class="email-order-details-field-first">Product Name</td>
  //             <td class="email-order-details-value-bold-first">{{productName}}</td>
  //           </tr>
  //           <tr class="email-table-row-odd">
  //             <td class="email-order-details-field">Supplier</td>
  //             <td class="email-order-details-value-bold">{{supplierName}}</td>
  //           </tr>
  //           <tr class="email-table-row-even">
  //             <td class="email-order-details-field">New Status</td>
  //             <td class="email-order-details-value-bold">{{newStatus}}</td>
  //           </tr>
  //           <tr class="email-table-row-odd">
  //             <td class="email-order-details-field">Updated On</td>
  //             <td class="email-order-details-value-bold">{{declinedOn}}</td>
  //           </tr>
  //         </table>
  //       </div>
  //     `,
  //     BROKERAGE_TEAM_MEMBER: `
  //       <h2 class="email-heading-h2">
  //         Hi {{firstName}},
  //       </h2>
  //       <p class="email-paragraph">
  //         A supplier has updated the status of a product linked to your brokerage account.
  //       </p>
        
  //       <h3 class="email-heading-h3">
  //         <span class="email-order-details-icon"></span>
  //         Product Link Status Update
  //       </h3>
        
  //       <div class="email-order-details-card">
  //         <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
  //           <tr class="email-table-row-even">
  //             <td class="email-order-details-field-first">Product Name</td>
  //             <td class="email-order-details-value-bold-first">{{productName}}</td>
  //           </tr>
  //           <tr class="email-table-row-odd">
  //             <td class="email-order-details-field">Supplier</td>
  //             <td class="email-order-details-value-bold">{{supplierName}}</td>
  //           </tr>
  //           <tr class="email-table-row-even">
  //             <td class="email-order-details-field">New Status</td>
  //             <td class="email-order-details-value-bold">{{newStatus}}</td>
  //           </tr>
  //           <tr class="email-table-row-odd">
  //             <td class="email-order-details-field">Updated On</td>
  //             <td class="email-order-details-value-bold">{{declinedOn}}</td>
  //           </tr>
  //         </table>
  //       </div>
  //     `,
  //     SIGNAGE_TEAM_MEMBER: `
  //       <h2 class="email-heading-h2">
  //         Hi {{firstName}},
  //       </h2>
  //       <p class="email-paragraph">
  //         A supplier has updated the status of a product linked to your signage account.
  //       </p>
        
  //       <h3 class="email-heading-h3">
  //         <span class="email-order-details-icon"></span>
  //         Product Link Status Update
  //       </h3>
        
  //       <div class="email-order-details-card">
  //         <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
  //           <tr class="email-table-row-even">
  //             <td class="email-order-details-field-first">Product Name</td>
  //             <td class="email-order-details-value-bold-first">{{productName}}</td>
  //           </tr>
  //           <tr class="email-table-row-odd">
  //             <td class="email-order-details-field">Supplier</td>
  //             <td class="email-order-details-value-bold">{{supplierName}}</td>
  //           </tr>
  //           <tr class="email-table-row-even">
  //             <td class="email-order-details-field">New Status</td>
  //             <td class="email-order-details-value-bold">{{newStatus}}</td>
  //           </tr>
  //           <tr class="email-table-row-odd">
  //             <td class="email-order-details-field">Updated On</td>
  //             <td class="email-order-details-value-bold">{{declinedOn}}</td>
  //           </tr>
  //         </table>
  //       </div>
  //     `,
  //     AGENT: `
  //       <h2 class="email-heading-h2">
  //         Hi {{firstName}},
  //       </h2>
  //       <p class="email-paragraph">
  //         {{supplierName}} has updated the status of one of your linked products in Realesy.
  //       </p>
        
  //       <h3 class="email-heading-h3">
  //         <span class="email-order-details-icon"></span>
  //         Product Link Status Update
  //       </h3>
        
  //       <div class="email-order-details-card">
  //         <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
  //           <tr class="email-table-row-header">
  //             <td class="email-order-details-field-first">Field Details</td>
  //             <td class="email-order-details-field-first"></td>
  //           </tr>
  //           <tr class="email-table-row-even">
  //             <td class="email-order-details-field">Product Name</td>
  //             <td class="email-order-details-value-bold">{{productName}}</td>
  //           </tr>
  //           <tr class="email-table-row-odd">
  //             <td class="email-order-details-field">New Status</td>
  //             <td class="email-order-details-value-bold">{{newStatus}}</td>
  //           </tr>
  //           <tr class="email-table-row-even">
  //             <td class="email-order-details-field">Updated On</td>
  //             <td class="email-order-details-value-bold">{{declinedOn}}</td>
  //           </tr>
  //           <tr class="email-table-row-odd">
  //             <td class="email-order-details-field">Supplier Comment</td>
  //             <td class="email-order-details-value-bold">"{{reason}}"</td>
  //           </tr>
  //         </table>
  //       </div>
        
  //       <p class="email-paragraph-small">
  //         If the product was rejected or needs changes, you can revise and re-submit as needed.
  //       </p>
  //     `,
  //     BROKERAGE_AGENT: `
  //       <h2 class="email-heading-h2">
  //         Hi {{firstName}},
  //       </h2>
  //       <p class="email-paragraph">
  //         {{supplierName}} has updated the status of one of your linked products in Realesy.
  //       </p>
        
  //       <h3 class="email-heading-h3">
  //         <span class="email-order-details-icon"></span>
  //         Product Link Status Update
  //       </h3>
        
  //       <div class="email-order-details-card">
  //         <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
  //           <tr class="email-table-row-header">
  //             <td class="email-order-details-field-first">Field Details</td>
  //             <td class="email-order-details-field-first"></td>
  //           </tr>
  //           <tr class="email-table-row-even">
  //             <td class="email-order-details-field">Product Name</td>
  //             <td class="email-order-details-value-bold">{{productName}}</td>
  //           </tr>
  //           <tr class="email-table-row-odd">
  //             <td class="email-order-details-field">New Status</td>
  //             <td class="email-order-details-value-bold">{{newStatus}}</td>
  //           </tr>
  //           <tr class="email-table-row-even">
  //             <td class="email-order-details-field">Updated On</td>
  //             <td class="email-order-details-value-bold">{{declinedOn}}</td>
  //           </tr>
  //           <tr class="email-table-row-odd">
  //             <td class="email-order-details-field">Supplier Comment</td>
  //             <td class="email-order-details-value-bold">"{{reason}}"</td>
  //           </tr>
  //         </table>
  //       </div>
        
  //       <p class="email-paragraph-small">
  //         If the product was rejected or needs changes, you can revise and re-submit as needed.
  //       </p>
  //     `,
  //   },
  //   cta: {
  //     SIGNAGE_FIRM: { text: "Review Product Link Status", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
  //     SIGNAGE_TEAM_MEMBER: { text: "Review Product Link Status", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
  //     BROKERAGE_FIRM: { text: "Review Product Link Status", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
  //     BROKERAGE_TEAM_MEMBER: { text: "Review Product Link Status", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
  //     AGENT: { text: "Review Product Status", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
  //     BROKERAGE_AGENT: { text: "Review Product Status", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
  //   },
  //   footer: "RealEsy. All rights reserved."
  // },
  // "Product Unlinked": {
  //   subject: {
  //     SIGNAGE_FIRM: " Product Unlinked by Supplier",
  //     SIGNAGE_TEAM_MEMBER: " Product Unlinked by Supplier",
  //     BROKERAGE_FIRM: " Product Unlinked by Supplier",
  //     BROKERAGE_TEAM_MEMBER: " Product Unlinked by Supplier",
  //     AGENT: " Product Unlinked by {{supplierName}}",
  //     BROKERAGE_AGENT: " Product Unlinked by {{supplierName}}",
  //   },
  //   title: "Product Unlinked",
  //   logoUrl: "", // Logo is imported directly in emailTemplate.ts
  //   headerBgColor: "#ffffff",
  //   bodyLines: [], // Using bodyHtml instead
  //   bodyHtml: {
  //     BROKERAGE_FIRM: `
  //       <h2 class="email-heading-h2">
  //         Hi {{firstName}},
  //       </h2>
  //       <p class="email-paragraph">
  //         The supplier <strong>{{supplierName}}</strong> has approved and unlinked the following product from <strong>{{brokerageFirmName}}</strong>.
  //       </p>
        
  //       <h3 class="email-heading-h3">
  //         <span class="email-order-details-icon"></span>
  //         Product Unlinked
  //       </h3>
        
  //       <div class="email-order-details-card">
  //         <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
  //           <tr class="email-table-row-even">
  //             <td class="email-order-details-field-first">Product Name</td>
  //             <td class="email-order-details-value-bold-first">{{productName}}</td>
  //           </tr>
  //           <tr class="email-table-row-odd">
  //             <td class="email-order-details-field">Supplier</td>
  //             <td class="email-order-details-value-bold">{{supplierName}}</td>
  //           </tr>
  //           <tr class="email-table-row-even">
  //             <td class="email-order-details-field">Unlinked On</td>
  //             <td class="email-order-details-value-bold">{{unlinkedOn}}</td>
  //           </tr>
  //           <tr class="email-table-row-odd">
  //             <td class="email-order-details-field">Reason (if provided)</td>
  //             <td class="email-order-details-value-bold">"{{reason}}"</td>
  //           </tr>
  //         </table>
  //       </div>
        
  //       <p class="email-paragraph-small">
  //         You can review the updated product status and take further action from your inventory dashboard.
  //       </p>
  //     `,
  //     BROKERAGE_TEAM_MEMBER: `
  //       <h2 class="email-heading-h2">
  //         Hi {{firstName}},
  //       </h2>
  //       <p class="email-paragraph">
  //         The supplier <strong>{{supplierName}}</strong> has approved and unlinked the following product from <strong>{{brokerageFirmName}}</strong>.
  //       </p>
        
  //       <h3 class="email-heading-h3">
  //         <span class="email-order-details-icon"></span>
  //         Product Unlinked
  //       </h3>
        
  //       <div class="email-order-details-card">
  //         <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
  //           <tr class="email-table-row-even">
  //             <td class="email-order-details-field-first">Product Name</td>
  //             <td class="email-order-details-value-bold-first">{{productName}}</td>
  //           </tr>
  //           <tr class="email-table-row-odd">
  //             <td class="email-order-details-field">Supplier</td>
  //             <td class="email-order-details-value-bold">{{supplierName}}</td>
  //           </tr>
  //           <tr class="email-table-row-even">
  //             <td class="email-order-details-field">Unlinked On</td>
  //             <td class="email-order-details-value-bold">{{unlinkedOn}}</td>
  //           </tr>
  //           <tr class="email-table-row-odd">
  //             <td class="email-order-details-field">Reason (if provided)</td>
  //             <td class="email-order-details-value-bold">"{{reason}}"</td>
  //           </tr>
  //         </table>
  //       </div>
        
  //       <p class="email-paragraph-small">
  //         You can review the updated product status and take further action from your inventory dashboard.
  //       </p>
  //     `,
  //     SIGNAGE_TEAM_MEMBER: `
  //       <h2 class="email-heading-h2">
  //         Hi {{firstName}},
  //       </h2>
  //       <p class="email-paragraph">
  //         The supplier <strong>{{supplierName}}</strong> has approved and unlinked the following product from your signage account.
  //       </p>
        
  //       <h3 class="email-heading-h3">
  //         <span class="email-order-details-icon"></span>
  //         Product Unlinked
  //       </h3>
        
  //       <div class="email-order-details-card">
  //         <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
  //           <tr class="email-table-row-even">
  //             <td class="email-order-details-field-first">Product Name</td>
  //             <td class="email-order-details-value-bold-first">{{productName}}</td>
  //           </tr>
  //           <tr class="email-table-row-odd">
  //             <td class="email-order-details-field">Supplier</td>
  //             <td class="email-order-details-value-bold">{{supplierName}}</td>
  //           </tr>
  //           <tr class="email-table-row-even">
  //             <td class="email-order-details-field">Unlinked On</td>
  //             <td class="email-order-details-value-bold">{{unlinkedOn}}</td>
  //           </tr>
  //           <tr class="email-table-row-odd">
  //             <td class="email-order-details-field">Reason (if provided)</td>
  //             <td class="email-order-details-value-bold">"{{reason}}"</td>
  //           </tr>
  //         </table>
  //       </div>
        
  //       <p class="email-paragraph-small">
  //         You can review the updated product status and take further action from your inventory dashboard.
  //       </p>
  //     `,
  //     SIGNAGE_FIRM: `
  //       <h2 class="email-heading-h2">
  //         Hi {{firstName}},
  //       </h2>
  //       <p class="email-paragraph">
  //         The supplier <strong>{{supplierName}}</strong> has approved and unlinked the following product from your signage company.
  //       </p>
        
  //       <h3 class="email-heading-h3">
  //         <span class="email-order-details-icon"></span>
  //         Product Unlinked
  //       </h3>
        
  //       <div class="email-order-details-card">
  //         <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
  //           <tr class="email-table-row-even">
  //             <td class="email-order-details-field-first">Product Name</td>
  //             <td class="email-order-details-value-bold-first">{{productName}}</td>
  //           </tr>
  //           <tr class="email-table-row-odd">
  //             <td class="email-order-details-field">Supplier</td>
  //             <td class="email-order-details-value-bold">{{supplierName}}</td>
  //           </tr>
  //           <tr class="email-table-row-even">
  //             <td class="email-order-details-field">Unlinked On</td>
  //             <td class="email-order-details-value-bold">{{unlinkedOn}}</td>
  //           </tr>
  //           <tr class="email-table-row-odd">
  //             <td class="email-order-details-field">Reason (if provided)</td>
  //             <td class="email-order-details-value-bold">"{{reason}}"</td>
  //           </tr>
  //         </table>
  //       </div>
        
  //       <p class="email-paragraph-small">
  //         You can review the updated product status and take further action from your inventory dashboard.
  //       </p>
  //     `,
  //     BROKERAGE_AGENT: `
  //       <h2 class="email-heading-h2">
  //         Hi {{firstName}},
  //       </h2>
  //       <p class="email-paragraph">
  //         <strong>{{supplierName}}</strong> has unlinked the product <strong>{{productName}}</strong> from your account. It will no longer be available for use with that supplier.
  //       </p>
        
  //       <h3 class="email-heading-h3">
  //         <span class="email-order-details-icon"></span>
  //         Unlink Summary
  //       </h3>
        
  //       <div class="email-order-details-card">
  //         <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
  //           <tr class="email-table-row-header">
  //             <td class="email-order-details-field-first">Field Details</td>
  //             <td class="email-order-details-field-first"></td>
  //           </tr>
  //           <tr class="email-table-row-even">
  //             <td class="email-order-details-field">Product Name</td>
  //             <td class="email-order-details-value-bold">{{productName}}</td>
  //           </tr>
  //           <tr class="email-table-row-odd">
  //             <td class="email-order-details-field">Unlinked By</td>
  //             <td class="email-order-details-value-bold">{{supplierName}}</td>
  //           </tr>
  //           <tr class="email-table-row-even">
  //             <td class="email-order-details-field">Date Unlinked</td>
  //             <td class="email-order-details-value-bold">{{unlinkedOn}}</td>
  //           </tr>
  //           <tr class="email-table-row-odd">
  //             <td class="email-order-details-field">Reason (if given)</td>
  //             <td class="email-order-details-value-bold">"{{reason}}"</td>
  //           </tr>
  //         </table>
  //       </div>
        
  //       <p class="email-paragraph-small">
  //         If needed, you can re-submit the product or link it to a different supplier.
  //       </p>
  //     `,
  //     AGENT: `
  //       <h2 class="email-heading-h2">
  //         Hi {{firstName}},
  //       </h2>
  //       <p class="email-paragraph">
  //         <strong>{{supplierName}}</strong> has unlinked the product <strong>{{productName}}</strong> from your account. It will no longer be available for use with that supplier.
  //       </p>
        
  //       <h3 class="email-heading-h3">
  //         <span class="email-order-details-icon"></span>
  //         Unlink Summary
  //       </h3>
        
  //       <div class="email-order-details-card">
  //         <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
  //           <tr class="email-table-row-header">
  //             <td class="email-order-details-field-first">Field Details</td>
  //             <td class="email-order-details-field-first"></td>
  //           </tr>
  //           <tr class="email-table-row-even">
  //             <td class="email-order-details-field">Product Name</td>
  //             <td class="email-order-details-value-bold">{{productName}}</td>
  //           </tr>
  //           <tr class="email-table-row-odd">
  //             <td class="email-order-details-field">Unlinked By</td>
  //             <td class="email-order-details-value-bold">{{supplierName}}</td>
  //           </tr>
  //           <tr class="email-table-row-even">
  //             <td class="email-order-details-field">Date Unlinked</td>
  //             <td class="email-order-details-value-bold">{{unlinkedOn}}</td>
  //           </tr>
  //           <tr class="email-table-row-odd">
  //             <td class="email-order-details-field">Reason (if given)</td>
  //             <td class="email-order-details-value-bold">"{{reason}}"</td>
  //           </tr>
  //         </table>
  //       </div>
        
  //       <p class="email-paragraph-small">
  //         If needed, you can re-submit the product or link it to a different supplier.
  //       </p>
  //     `,
  //   },
  //   cta: {
  //     SIGNAGE_FIRM: { text: "View Product Status", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
  //     SIGNAGE_TEAM_MEMBER: { text: "View Product Status", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
  //     BROKERAGE_FIRM: { text: "View Product Status", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
  //     BROKERAGE_TEAM_MEMBER: { text: "View Product Status", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
  //     AGENT: { text: "Manage My Products", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
  //     BROKERAGE_AGENT: { text: "Manage My Products", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
  //   },
  //   footer: "RealEsy. All rights reserved."
  // },
  "salesorder_sendquote": {
    subject: {
      BROKERAGE_FIRM: "Quote {{orderId}} – Approval Required",
      BROKERAGE_TEAM_MEMBER: "Quote {{orderId}} – Approval Required",
      AGENT: "Quote {{orderId}} – Approval Required",
      BROKERAGE_AGENT: "Quote {{orderId}} – Approval Required",
    },
    title: "Quote {{orderId}} – Approval Required",
    logoUrl: "",
    headerBgColor: "#ffffff",
    bodyLines: [],
    bodyHtml: {
      BROKERAGE_FIRM: `
        <h2 class="email-heading-h2">Hello {{customerName}},</h2>
        <p class="email-paragraph">
          Your quote {{orderId}} has been prepared and is ready for your review.
        </p>
        <p class="email-paragraph">
          Please review the quote details by clicking the View button and approve it to proceed with your sales order.
        </p>
        <p class="email-paragraph">
          Once the quote is approved, you will also have the option to make an advance payment toward the order. <strong>Advance payments are optional and are not required for approval.</strong>
        </p>
        <h3 class="email-heading-h3">Order Summary</h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr class="email-table-row-even">
              <td class="email-order-details-field-first">Order #</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field">Total</td>
              <td class="email-order-details-value-bold">{{totalDue}}</td>
            </tr>
            <tr class="email-table-row-even">
              <td class="email-order-details-field">Already Paid</td>
              <td class="email-order-details-value-bold">{{alreadyPaid}}</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field">Remaining Balance</td>
              <td class="email-order-details-value-bold">{{remainingBalance}}</td>
            </tr>
            {{addressFieldRow}}
          </table>
        </div>
        {{lineItems}}
        <p class="email-paragraph">
          If you have any questions or would like changes to the quote, please contact us before approving it.
        </p>
      `,
      BROKERAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">Hello {{customerName}},</h2>
        <p class="email-paragraph">
          Your quote {{orderId}} has been prepared and is ready for your review.
        </p>
        <p class="email-paragraph">
          Please review the quote details by clicking the View button and approve it to proceed with your sales order. 
        </p>
        <p class="email-paragraph">
          Once the quote is approved, you will also have the option to make an advance payment toward the order. <strong>Advance payments are optional and are not required for approval.</strong>
        </p>
        <h3 class="email-heading-h3">Order Summary</h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr class="email-table-row-even">
              <td class="email-order-details-field-first">Order #</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field">Total</td>
              <td class="email-order-details-value-bold">{{totalDue}}</td>
            </tr>
            <tr class="email-table-row-even">
              <td class="email-order-details-field">Already Paid</td>
              <td class="email-order-details-value-bold">{{alreadyPaid}}</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field">Remaining Balance</td>
              <td class="email-order-details-value-bold">{{remainingBalance}}</td>
            </tr>
            {{addressFieldRow}}
          </table>
        </div>
        {{lineItems}}
        <p class="email-paragraph">
          If you have any questions or would like changes to the quote, please contact us before approving it.
        </p>
      `,
      AGENT: `
        <h2 class="email-heading-h2">Hello {{customerName}},</h2>
        <p class="email-paragraph">
          Your quote {{orderId}} has been prepared and is ready for your review. 
        </p>
        <p class="email-paragraph">
          Please review the quote details by clicking the View button and approve it to proceed with your sales order. 
        </p>
        <p class="email-paragraph">
          Once the quote is approved, you will also have the option to make an advance payment toward the order. <strong>Advance payments are optional and are not required for approval.</strong>
        </p>
        <h3 class="email-heading-h3">Order Summary</h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr class="email-table-row-even">
              <td class="email-order-details-field-first">Order #</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field">Total</td>
              <td class="email-order-details-value-bold">{{totalDue}}</td>
            </tr>
            <tr class="email-table-row-even">
              <td class="email-order-details-field">Already Paid</td>
              <td class="email-order-details-value-bold">{{alreadyPaid}}</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field">Remaining Balance</td>
              <td class="email-order-details-value-bold">{{remainingBalance}}</td>
            </tr>
            {{addressFieldRow}}
          </table>
        </div>
        {{lineItems}}
        <p class="email-paragraph">
          If you have any questions or would like changes to the quote, please contact us before approving it.
        </p>
      `,
      BROKERAGE_AGENT: `
        <h2 class="email-heading-h2">Hello {{customerName}},</h2>
        <p class="email-paragraph">
          Your quote {{orderId}} has been prepared and is ready for your review.
        </p>
        <p class="email-paragraph">
          Please review the quote details by clicking the View button and approve it to proceed with your sales order.
        </p>
        <p class="email-paragraph">
          Once the quote is approved, you will also have the option to make an advance payment toward the order. <strong>Advance payments are optional and are not required for approval.</strong>
        </p>
        <h3 class="email-heading-h3">Order Summary</h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr class="email-table-row-even">
              <td class="email-order-details-field-first">Order #</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field">Total</td>
              <td class="email-order-details-value-bold">{{totalDue}}</td>
            </tr>
            <tr class="email-table-row-even">
              <td class="email-order-details-field">Already Paid</td>
              <td class="email-order-details-value-bold">{{alreadyPaid}}</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field">Remaining Balance</td>
              <td class="email-order-details-value-bold">{{remainingBalance}}</td>
            </tr>
            {{addressFieldRow}}
          </table>
        </div>
        {{lineItems}}
        <p class="email-paragraph">
          If you have any questions or would like changes to the quote, please contact us before approving it.
        </p>
      `,
    },
    cta: {
      BROKERAGE_FIRM: { text: "View", url: "{{viewPayUrl}}" },
      BROKERAGE_TEAM_MEMBER: { text: "View", url: "{{viewPayUrl}}" },
      AGENT: { text: "View", url: "{{viewPayUrl}}" },
      BROKERAGE_AGENT: { text: "View", url: "{{viewPayUrl}}" },
    },
    footer: "RealEsy. All rights reserved.",
  },
  "installationjobs_sendquote": {
    subject: {
      BROKERAGE_FIRM: "Quote {{orderId}} – Approval Required",
      BROKERAGE_TEAM_MEMBER: "Quote {{orderId}} – Approval Required",
      AGENT: "Quote {{orderId}} – Approval Required",
      BROKERAGE_AGENT: "Quote {{orderId}} – Approval Required",
    },
    title: "Quote {{orderId}} – Approval Required",
    logoUrl: "",
    headerBgColor: "#ffffff",
    bodyLines: [],
    bodyHtml: {
      BROKERAGE_FIRM: `
        <h2 class="email-heading-h2">Hello {{customerName}},</h2>
        <p class="email-paragraph">
          Your quote {{orderId}} has been prepared and is ready for your review.
        </p>
        <p class="email-paragraph">
          Please review the quote details by clicking the View button and approve it to proceed with your installation job.
        </p>
        <p class="email-paragraph">
          Once the quote is approved, you will also have the option to make an advance payment toward the order. <strong>Advance payments are optional and are not required for approval.</strong>
        </p>
        <h3 class="email-heading-h3">Job Summary</h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr class="email-table-row-even">
              <td class="email-order-details-field-first">Job Number</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field">Total</td>
              <td class="email-order-details-value-bold">{{totalDue}}</td>
            </tr>
            <tr class="email-table-row-even">
              <td class="email-order-details-field">Already Paid</td>
              <td class="email-order-details-value-bold">{{alreadyPaid}}</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field">Remaining Balance</td>
              <td class="email-order-details-value-bold">{{remainingBalance}}</td>
            </tr>
            {{addressFieldRow}}
          </table>
        </div>
        {{lineItems}}
        <p class="email-paragraph">
          If you have any questions or would like changes to the quote, please contact us before approving it.
        </p>
      `,
      BROKERAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">Hello {{customerName}},</h2>
        <p class="email-paragraph">
          Your quote {{orderId}} has been prepared and is ready for your review.
        </p>
        <p class="email-paragraph">
          Please review the quote details by clicking the View button and approve it to proceed with your installation job.
        </p>
        <p class="email-paragraph">
          Once the quote is approved, you will also have the option to make an advance payment toward the order. <strong>Advance payments are optional and are not required for approval.</strong>
        </p>
        <h3 class="email-heading-h3">Job Summary</h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr class="email-table-row-even">
              <td class="email-order-details-field-first">Job Number</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field">Total</td>
              <td class="email-order-details-value-bold">{{totalDue}}</td>
            </tr>
            <tr class="email-table-row-even">
              <td class="email-order-details-field">Already Paid</td>
              <td class="email-order-details-value-bold">{{alreadyPaid}}</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field">Remaining Balance</td>
              <td class="email-order-details-value-bold">{{remainingBalance}}</td>
            </tr>
            {{addressFieldRow}}
          </table>
        </div>
        {{lineItems}}
        <p class="email-paragraph">
          If you have any questions or would like changes to the quote, please contact us before approving it.
        </p>
      `,
      AGENT: `
        <h2 class="email-heading-h2">Hello {{customerName}},</h2>
        <p class="email-paragraph">
          Your quote {{orderId}} has been prepared and is ready for your review. 
        </p>
        <p class="email-paragraph">
          Please review the quote details by clicking the View button and approve it to proceed with your installation job.
        </p>
        <p class="email-paragraph">
          Once the quote is approved, you will also have the option to make an advance payment toward the order. <strong>Advance payments are optional and are not required for approval.</strong>
        </p>
        <h3 class="email-heading-h3">Job Summary</h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr class="email-table-row-even">
              <td class="email-order-details-field-first">Job Number</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field">Total</td>
              <td class="email-order-details-value-bold">{{totalDue}}</td>
            </tr>
            <tr class="email-table-row-even">
              <td class="email-order-details-field">Already Paid</td>
              <td class="email-order-details-value-bold">{{alreadyPaid}}</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field">Remaining Balance</td>
              <td class="email-order-details-value-bold">{{remainingBalance}}</td>
            </tr>
            {{addressFieldRow}}
          </table>
        </div>
        {{lineItems}}
        <p class="email-paragraph">
          If you have any questions or would like changes to the quote, please contact us before approving it.
        </p>
      `,
      BROKERAGE_AGENT: `
        <h2 class="email-heading-h2">Hello {{customerName}},</h2>
        <p class="email-paragraph">
          Your quote {{orderId}} has been prepared and is ready for your review.
        </p>
        <p class="email-paragraph">
          Please review the quote details by clicking the View button and approve it to proceed with your installation job.
        </p>
        <p class="email-paragraph">
          Once the quote is approved, you will also have the option to make an advance payment toward the order. <strong>Advance payments are optional and are not required for approval.</strong>
        </p>
        <h3 class="email-heading-h3">Job Summary</h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr class="email-table-row-even">
              <td class="email-order-details-field-first">Job Number</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field">Total</td>
              <td class="email-order-details-value-bold">{{totalDue}}</td>
            </tr>
            <tr class="email-table-row-even">
              <td class="email-order-details-field">Already Paid</td>
              <td class="email-order-details-value-bold">{{alreadyPaid}}</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field">Remaining Balance</td>
              <td class="email-order-details-value-bold">{{remainingBalance}}</td>
            </tr>
            {{addressFieldRow}}
          </table>
        </div>
        {{lineItems}}
        <p class="email-paragraph">
          If you have any questions or would like changes to the quote, please contact us before approving it.
        </p>
      `,
    },
    cta: {
      BROKERAGE_FIRM: { text: "View", url: "{{viewPayUrl}}" },
      BROKERAGE_TEAM_MEMBER: { text: "View", url: "{{viewPayUrl}}" },
      AGENT: { text: "View", url: "{{viewPayUrl}}" },
      BROKERAGE_AGENT: { text: "View", url: "{{viewPayUrl}}" },
    },
    footer: "RealEsy. All rights reserved.",
  },
  "removaljobs_sendquote": {
    subject: {
      BROKERAGE_FIRM: "Quote {{orderId}} – Approval Required",
      BROKERAGE_TEAM_MEMBER: "Quote {{orderId}} – Approval Required",
      AGENT: "Quote {{orderId}} – Approval Required",
      BROKERAGE_AGENT: "Quote {{orderId}} – Approval Required",
    },
    title: "Quote {{orderId}} – Approval Required",
    logoUrl: "",
    headerBgColor: "#ffffff",
    bodyLines: [],
    bodyHtml: {
      BROKERAGE_FIRM: `
        <h2 class="email-heading-h2">Hello {{customerName}},</h2>
        <p class="email-paragraph">
          Your quote {{orderId}} has been prepared and is ready for your review. 
        </p>
        <p class="email-paragraph">
          Please review the quote details by clicking the View button and approve it to proceed with your removal job.
        </p>
        <p class="email-paragraph">
          Once the quote is approved, you will also have the option to make an advance payment toward the order. <strong>Advance payments are optional and are not required for approval.</strong>
        </p>
        <h3 class="email-heading-h3">Job Summary</h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr class="email-table-row-even">
              <td class="email-order-details-field-first">Job Number</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field">Total</td>
              <td class="email-order-details-value-bold">{{totalDue}}</td>
            </tr>
            <tr class="email-table-row-even">
              <td class="email-order-details-field">Already Paid</td>
              <td class="email-order-details-value-bold">{{alreadyPaid}}</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field">Remaining Balance</td>
              <td class="email-order-details-value-bold">{{remainingBalance}}</td>
            </tr>
            {{addressFieldRow}}
          </table>
        </div>
        <p class="email-paragraph">
          If you have any questions or would like changes to the quote, please contact us before approving it.
        </p>
      `,
      BROKERAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">Hello {{customerName}},</h2>
        <p class="email-paragraph">
          Your quote {{orderId}} has been prepared and is ready for your review.
        </p>
        <p class="email-paragraph">
          Please review the quote details by clicking the View button and approve it to proceed with your removal job.
        </p>
        <p class="email-paragraph">
          Once the quote is approved, you will also have the option to make an advance payment toward the order. <strong>Advance payments are optional and are not required for approval.</strong>
        </p>
        <h3 class="email-heading-h3">Job Summary</h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr class="email-table-row-even">
              <td class="email-order-details-field-first">Job Number</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field">Total</td>
              <td class="email-order-details-value-bold">{{totalDue}}</td>
            </tr>
            <tr class="email-table-row-even">
              <td class="email-order-details-field">Already Paid</td>
              <td class="email-order-details-value-bold">{{alreadyPaid}}</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field">Remaining Balance</td>
              <td class="email-order-details-value-bold">{{remainingBalance}}</td>
            </tr>
            {{addressFieldRow}}
          </table>
        </div>
        <p class="email-paragraph">
          If you have any questions or would like changes to the quote, please contact us before approving it.
        </p>
      `,
      AGENT: `
        <h2 class="email-heading-h2">Hello {{customerName}},</h2>
        <p class="email-paragraph">
          Your quote {{orderId}} has been prepared and is ready for your review. 
        </p>
        <p class="email-paragraph">
          Please review the quote details by clicking the View button and approve it to proceed with your removal job. 
        </p>
        <p class="email-paragraph">
          Once the quote is approved, you will also have the option to make an advance payment toward the order. <strong>Advance payments are optional and are not required for approval.</strong>
        </p>
        <h3 class="email-heading-h3">Job Summary</h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr class="email-table-row-even">
              <td class="email-order-details-field-first">Job Number</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field">Total</td>
              <td class="email-order-details-value-bold">{{totalDue}}</td>
            </tr>
            <tr class="email-table-row-even">
              <td class="email-order-details-field">Already Paid</td>
              <td class="email-order-details-value-bold">{{alreadyPaid}}</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field">Remaining Balance</td>
              <td class="email-order-details-value-bold">{{remainingBalance}}</td>
            </tr>
            {{addressFieldRow}}
          </table>
        </div>
        <p class="email-paragraph">
          If you have any questions or would like changes to the quote, please contact us before approving it.
        </p>
      `,
      BROKERAGE_AGENT: `
        <h2 class="email-heading-h2">Hello {{customerName}},</h2>
        <p class="email-paragraph">
          Your quote {{orderId}} has been prepared and is ready for your review. 
        </p>
        <p class="email-paragraph">
          Please review the quote details by clicking the View button and approve it to proceed with your removal job.
        </p>
        <p class="email-paragraph">
          Once the quote is approved, you will also have the option to make an advance payment toward the order. <strong>Advance payments are optional and are not required for approval.</strong>
        </p>
        <h3 class="email-heading-h3">Job Summary</h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr class="email-table-row-even">
              <td class="email-order-details-field-first">Job Number</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field">Total</td>
              <td class="email-order-details-value-bold">{{totalDue}}</td>
            </tr>
            <tr class="email-table-row-even">
              <td class="email-order-details-field">Already Paid</td>
              <td class="email-order-details-value-bold">{{alreadyPaid}}</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field">Remaining Balance</td>
              <td class="email-order-details-value-bold">{{remainingBalance}}</td>
            </tr>
            {{addressFieldRow}}
          </table>
        </div>
        <p class="email-paragraph">
          If you have any questions or would like changes to the quote, please contact us before approving it.
        </p>
      `,
    },
    cta: {
      BROKERAGE_FIRM: { text: "View", url: "{{viewPayUrl}}" },
      BROKERAGE_TEAM_MEMBER: { text: "View", url: "{{viewPayUrl}}" },
      AGENT: { text: "View", url: "{{viewPayUrl}}" },
      BROKERAGE_AGENT: { text: "View", url: "{{viewPayUrl}}" },
    },
    footer: "RealEsy. All rights reserved.",
  },
  "salesorder_sendquote_paymentcard": {
    subject: {
      BROKERAGE_FIRM: "Quote {{orderId}} – Ready for Review & Payment",
      BROKERAGE_TEAM_MEMBER: "Quote {{orderId}} – Ready for Review & Payment",
      AGENT: "Quote {{orderId}} – Ready for Review & Payment",
      BROKERAGE_AGENT: "Quote {{orderId}} – Ready for Review & Payment",
    },
    title: "Quote {{orderId}} – Ready for Review & Payment",
    logoUrl: "",
    headerBgColor: "#ffffff",
    bodyLines: [],
    bodyHtml: {
      BROKERAGE_FIRM: `
        <h2 class="email-heading-h2">Hello {{customerName}},</h2>
        <p class="email-paragraph">
          Your quote {{orderId}} has been prepared and is ready for your review. Please click the button below to review your quote details and complete your payment. 
        </p>
        <h3 class="email-heading-h3">Order Summary</h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr class="email-table-row-even">
              <td class="email-order-details-field-first">Order #</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field">Total</td>
              <td class="email-order-details-value-bold">{{totalDue}}</td>
            </tr>
            <tr class="email-table-row-even">
              <td class="email-order-details-field">Already Paid</td>
              <td class="email-order-details-value-bold">{{alreadyPaid}}</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field">Remaining Balance</td>
              <td class="email-order-details-value-bold">{{remainingBalance}}</td>
            </tr>
            {{addressFieldRow}}
          </table>
        </div>
        {{lineItems}}
        <p class="email-paragraph">
          If you have any questions about this quote, please contact us before submitting payment.
        </p>
      `,
      BROKERAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">Hello {{customerName}},</h2>
        <p class="email-paragraph">
          Your quote {{orderId}} has been prepared and is ready for your review. Please click the button below to review your quote details and complete your payment. 
        </p>
        <h3 class="email-heading-h3">Order Summary</h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr class="email-table-row-even">
              <td class="email-order-details-field-first">Order #</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field">Total</td>
              <td class="email-order-details-value-bold">{{totalDue}}</td>
            </tr>
            <tr class="email-table-row-even">
              <td class="email-order-details-field">Already Paid</td>
              <td class="email-order-details-value-bold">{{alreadyPaid}}</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field">Remaining Balance</td>
              <td class="email-order-details-value-bold">{{remainingBalance}}</td>
            </tr>
            {{addressFieldRow}}
          </table>
        </div>
        {{lineItems}}
        <p class="email-paragraph">
          If you have any questions about this quote, please contact us before submitting payment.
        </p>
      `,
      AGENT: `
        <h2 class="email-heading-h2">Hello {{customerName}},</h2>
        <p class="email-paragraph">
          Your quote {{orderId}} has been prepared and is ready for your review. Please click the button below to review your quote details and complete your payment. 
        </p>
        <h3 class="email-heading-h3">Order Summary</h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr class="email-table-row-even">
              <td class="email-order-details-field-first">Order #</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field">Total</td>
              <td class="email-order-details-value-bold">{{totalDue}}</td>
            </tr>
            <tr class="email-table-row-even">
              <td class="email-order-details-field">Already Paid</td>
              <td class="email-order-details-value-bold">{{alreadyPaid}}</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field">Remaining Balance</td>
              <td class="email-order-details-value-bold">{{remainingBalance}}</td>
            </tr>
            {{addressFieldRow}}
          </table>
        </div>
        {{lineItems}}
        <p class="email-paragraph">
          If you have any questions about this quote, please contact us before submitting payment.
        </p>
      `,
      BROKERAGE_AGENT: `
        <h2 class="email-heading-h2">Hello {{customerName}},</h2>
        <p class="email-paragraph">
          Your quote {{orderId}} has been prepared and is ready for your review. Please click the button below to review your quote details and complete your payment. 
        </p>
        <h3 class="email-heading-h3">Order Summary</h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr class="email-table-row-even">
              <td class="email-order-details-field-first">Order #</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field">Total</td>
              <td class="email-order-details-value-bold">{{totalDue}}</td>
            </tr>
            <tr class="email-table-row-even">
              <td class="email-order-details-field">Already Paid</td>
              <td class="email-order-details-value-bold">{{alreadyPaid}}</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field">Remaining Balance</td>
              <td class="email-order-details-value-bold">{{remainingBalance}}</td>
            </tr>
            {{addressFieldRow}}
          </table>
        </div>
        {{lineItems}}
        <p class="email-paragraph">
          If you have any questions about this quote, please contact us before submitting payment.
        </p>
      `,
    },
    cta: {
      BROKERAGE_FIRM: { text: "View", url: "{{viewPayUrl}}" },
      BROKERAGE_TEAM_MEMBER: { text: "View", url: "{{viewPayUrl}}" },
      AGENT: { text: "View", url: "{{viewPayUrl}}" },
      BROKERAGE_AGENT: { text: "View", url: "{{viewPayUrl}}" },
    },
    footer: "RealEsy. All rights reserved.",
  },

  "installationjobs_sendquote_paymentcard": {
    subject: {
      BROKERAGE_FIRM: "Quote {{orderId}} – Ready for Review & Payment",
      BROKERAGE_TEAM_MEMBER: "Quote {{orderId}} – Ready for Review & Payment",
      AGENT: "Quote {{orderId}} – Ready for Review & Payment",
      BROKERAGE_AGENT: "Quote {{orderId}} – Ready for Review & Payment",
    },
    title: "Quote {{orderId}} – Ready for Review & Payment",
    logoUrl: "",
    headerBgColor: "#ffffff",
    bodyLines: [],
    bodyHtml: {
      BROKERAGE_FIRM: `
        <h2 class="email-heading-h2">Hello {{customerName}},</h2>
        <p class="email-paragraph">
          Your quote {{orderId}} has been prepared and is ready for your review. Please click the button below to review your quote details and complete your payment. 
        </p>
        <h3 class="email-heading-h3">Job Summary</h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr class="email-table-row-even">
              <td class="email-order-details-field-first">Job Number</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field">Total</td>
              <td class="email-order-details-value-bold">{{totalDue}}</td>
            </tr>
            <tr class="email-table-row-even">
              <td class="email-order-details-field">Already Paid</td>
              <td class="email-order-details-value-bold">{{alreadyPaid}}</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field">Remaining Balance</td>
              <td class="email-order-details-value-bold">{{remainingBalance}}</td>
            </tr>
            {{addressFieldRow}}
          </table>
        </div>
        {{lineItems}}
        <p class="email-paragraph">
          If you have any questions about this quote, please contact us before submitting payment.
        </p>
      `,
      BROKERAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">Hello {{customerName}},</h2>
        <p class="email-paragraph">
          Your quote {{orderId}} has been prepared and is ready for your review. Please click the button below to review your quote details and complete your payment. 
        </p>
        <h3 class="email-heading-h3">Job Summary</h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr class="email-table-row-even">
              <td class="email-order-details-field-first">Job Number</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field">Total</td>
              <td class="email-order-details-value-bold">{{totalDue}}</td>
            </tr>
            <tr class="email-table-row-even">
              <td class="email-order-details-field">Already Paid</td>
              <td class="email-order-details-value-bold">{{alreadyPaid}}</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field">Remaining Balance</td>
              <td class="email-order-details-value-bold">{{remainingBalance}}</td>
            </tr>
            {{addressFieldRow}}
          </table>
        </div>
        {{lineItems}}
        <p class="email-paragraph">
          If you have any questions about this quote, please contact us before submitting payment.
        </p>
      `,
      AGENT: `
        <h2 class="email-heading-h2">Hello {{customerName}},</h2>
        <p class="email-paragraph">
          Your quote {{orderId}} has been prepared and is ready for your review. Please click the button below to review your quote details and complete your payment. 
        </p>
        <h3 class="email-heading-h3">Job Summary</h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr class="email-table-row-even">
              <td class="email-order-details-field-first">Job Number</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field">Total</td>
              <td class="email-order-details-value-bold">{{totalDue}}</td>
            </tr>
            <tr class="email-table-row-even">
              <td class="email-order-details-field">Already Paid</td>
              <td class="email-order-details-value-bold">{{alreadyPaid}}</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field">Remaining Balance</td>
              <td class="email-order-details-value-bold">{{remainingBalance}}</td>
            </tr>
            {{addressFieldRow}}
          </table>
        </div>
        {{lineItems}}
        <p class="email-paragraph">
          If you have any questions about this quote, please contact us before submitting payment.
        </p>
      `,
      BROKERAGE_AGENT: `
        <h2 class="email-heading-h2">Hello {{customerName}},</h2>
        <p class="email-paragraph">
          Your quote {{orderId}} has been prepared and is ready for your review. Please click the button below to review your quote details and complete your payment. 
        </p>
        <h3 class="email-heading-h3">Job Summary</h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr class="email-table-row-even">
              <td class="email-order-details-field-first">Job Number</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field">Total</td>
              <td class="email-order-details-value-bold">{{totalDue}}</td>
            </tr>
            <tr class="email-table-row-even">
              <td class="email-order-details-field">Already Paid</td>
              <td class="email-order-details-value-bold">{{alreadyPaid}}</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field">Remaining Balance</td>
              <td class="email-order-details-value-bold">{{remainingBalance}}</td>
            </tr>
            {{addressFieldRow}}
          </table>
        </div>
        {{lineItems}}
        <p class="email-paragraph">
          If you have any questions about this quote, please contact us before submitting payment.
        </p>
      `,
    },
    cta: {
      BROKERAGE_FIRM: { text: "View", url: "{{viewPayUrl}}" },
      BROKERAGE_TEAM_MEMBER: { text: "View", url: "{{viewPayUrl}}" },
      AGENT: { text: "View", url: "{{viewPayUrl}}" },
      BROKERAGE_AGENT: { text: "View", url: "{{viewPayUrl}}" },
    },
    footer: "RealEsy. All rights reserved.",
  },

  "removaljobs_sendquote_paymentcard": {
    subject: {
      BROKERAGE_FIRM: "Quote {{orderId}} – Ready for Review & Payment",
      BROKERAGE_TEAM_MEMBER: "Quote {{orderId}} – Ready for Review & Payment",
      AGENT: "Quote {{orderId}} – Ready for Review & Payment",
      BROKERAGE_AGENT: "Quote {{orderId}} – Ready for Review & Payment",
    },
    title: "Quote {{orderId}} – Ready for Review & Payment",
    logoUrl: "",
    headerBgColor: "#ffffff",
    bodyLines: [],
    bodyHtml: {
      BROKERAGE_FIRM: `
        <h2 class="email-heading-h2">Hello {{customerName}},</h2>
        <p class="email-paragraph">
          Your quote {{orderId}} has been prepared and is ready for your review. Please click the button below to review your quote details and complete your payment. 
        </p>
        <h3 class="email-heading-h3">Job Summary</h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr class="email-table-row-even">
              <td class="email-order-details-field-first">Job Number</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field">Total</td>
              <td class="email-order-details-value-bold">{{totalDue}}</td>
            </tr>
            <tr class="email-table-row-even">
              <td class="email-order-details-field">Already Paid</td>
              <td class="email-order-details-value-bold">{{alreadyPaid}}</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field">Remaining Balance</td>
              <td class="email-order-details-value-bold">{{remainingBalance}}</td>
            </tr>
            {{addressFieldRow}}
          </table>
        </div>
        <p class="email-paragraph">
          If you have any questions about this quote, please contact us before submitting payment.
        </p>
      `,
      BROKERAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">Hello {{customerName}},</h2>
        <p class="email-paragraph">
          Your quote {{orderId}} has been prepared and is ready for your review. Please click the button below to review your quote details and complete your payment. 
        </p>
        <h3 class="email-heading-h3">Job Summary</h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr class="email-table-row-even">
              <td class="email-order-details-field-first">Job Number</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field">Total</td>
              <td class="email-order-details-value-bold">{{totalDue}}</td>
            </tr>
            <tr class="email-table-row-even">
              <td class="email-order-details-field">Already Paid</td>
              <td class="email-order-details-value-bold">{{alreadyPaid}}</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field">Remaining Balance</td>
              <td class="email-order-details-value-bold">{{remainingBalance}}</td>
            </tr>
            {{addressFieldRow}}
          </table>
        </div>
        <p class="email-paragraph">
          If you have any questions about this quote, please contact us before submitting payment.
        </p>
      `,
      AGENT: `
        <h2 class="email-heading-h2">Hello {{customerName}},</h2>
        <p class="email-paragraph">
          Your quote {{orderId}} has been prepared and is ready for your review. Please click the button below to review your quote details and complete your payment. 
        </p>
        <h3 class="email-heading-h3">Job Summary</h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr class="email-table-row-even">
              <td class="email-order-details-field-first">Job Number</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field">Total</td>
              <td class="email-order-details-value-bold">{{totalDue}}</td>
            </tr>
            <tr class="email-table-row-even">
              <td class="email-order-details-field">Already Paid</td>
              <td class="email-order-details-value-bold">{{alreadyPaid}}</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field">Remaining Balance</td>
              <td class="email-order-details-value-bold">{{remainingBalance}}</td>
            </tr>
            {{addressFieldRow}}
          </table>
        </div>
        <p class="email-paragraph">
          If you have any questions about this quote, please contact us before submitting payment.
        </p>
      `,
      BROKERAGE_AGENT: `
        <h2 class="email-heading-h2">Hello {{customerName}},</h2>
        <p class="email-paragraph">
          Your quote {{orderId}} has been prepared and is ready for your review. Please click the button below to review your quote details and complete your payment. 
        </p>
        <h3 class="email-heading-h3">Job Summary</h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr class="email-table-row-even">
              <td class="email-order-details-field-first">Job Number</td>
              <td class="email-order-details-value-bold-first">{{orderId}}</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field">Total</td>
              <td class="email-order-details-value-bold">{{totalDue}}</td>
            </tr>
            <tr class="email-table-row-even">
              <td class="email-order-details-field">Already Paid</td>
              <td class="email-order-details-value-bold">{{alreadyPaid}}</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field">Remaining Balance</td>
              <td class="email-order-details-value-bold">{{remainingBalance}}</td>
            </tr>
            {{addressFieldRow}}
          </table>
        </div>
        <p class="email-paragraph">
          If you have any questions about this quote, please contact us before submitting payment.
        </p>
      `,
    },
    cta: {
      BROKERAGE_FIRM: { text: "View", url: "{{viewPayUrl}}" },
      BROKERAGE_TEAM_MEMBER: { text: "View", url: "{{viewPayUrl}}" },
      AGENT: { text: "View", url: "{{viewPayUrl}}" },
      BROKERAGE_AGENT: { text: "View", url: "{{viewPayUrl}}" },
    },
    footer: "RealEsy. All rights reserved.",
  },
  "New_project_create": {
    subject: {
      AGENT: "Your design is ready for review",
      BROKERAGE_AGENT: "Your design is ready for review",
      BROKERAGE_TEAM_MEMBER: "Your design is ready for review",
      BROKERAGE_FIRM: "Your design is ready for review",
    },
    title: "Design Ready for Review",
    logoUrl: "",
    headerBgColor: "#ffffff",
    bodyLines: [],
    bodyHtml: {
      AGENT: `
        <h2 class="email-heading-h2">Hi {{customerName}},</h2>
        <p class="email-paragraph">
          Your design for <strong>{{propertyAddress}}</strong> <strong>{{signType}}</strong> is ready for review.
        </p>
        <p class="email-paragraph">
          Please take a moment to preview the design and let us know if any changes are needed.
        </p>
        <h3 class="email-heading-h3"> What you can do:</h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr class="email-table-row-even">
              <td class="email-order-details-field-first" style="width:18px;padding-right:8px;">•</td>
              <td class="email-order-details-value-bold-first">View your design preview</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field" style="width:18px;padding-right:8px;">•</td>
              <td class="email-order-details-value-bold">Request any changes</td>
            </tr>
            <tr class="email-table-row-even">
              <td class="email-order-details-field" style="width:18px;padding-right:8px;">•</td>
              <td class="email-order-details-value-bold">Approve the design to proceed</td>
            </tr>
          </table>
        </div>
        <p class="email-paragraph-small">
          If everything looks good, you can approve the design directly from the page.
        </p>
        <p class="email-paragraph-small">
          Once approved, we will prepare and send your quotation.
        </p>
      `,
      BROKERAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">Hi {{customerName}},</h2>
        <p class="email-paragraph">
          Your design for <strong>{{propertyAddress}}</strong> <strong>{{signType}}</strong> is ready for review.
        </p>
        <p class="email-paragraph">
          Please take a moment to preview the design and let us know if any changes are needed.
        </p>
        <h3 class="email-heading-h3">What you can do:</h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr class="email-table-row-even">
              <td class="email-order-details-field-first" style="width:18px;padding-right:8px;">•</td>
              <td class="email-order-details-value-bold-first">View your design preview</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field" style="width:18px;padding-right:8px;">•</td>
              <td class="email-order-details-value-bold">Request any changes</td>
            </tr>
            <tr class="email-table-row-even">
              <td class="email-order-details-field" style="width:18px;padding-right:8px;">•</td>
              <td class="email-order-details-value-bold">Approve the design to proceed</td>
            </tr>
          </table>
        </div>
        <p class="email-paragraph-small">
          If everything looks good, you can approve the design directly from the page.
        </p>
        <p class="email-paragraph-small">
          Once approved, we will prepare and send your quotation.
        </p>
      `,
      BROKERAGE_AGENT: `
        <h2 class="email-heading-h2">Hi {{customerName}},</h2>
        <p class="email-paragraph">
          Your design for <strong>{{propertyAddress}}</strong> <strong>{{signType}}</strong> is ready for review.
        </p>
        <p class="email-paragraph">
          Please take a moment to preview the design and let us know if any changes are needed.
        </p>
        <h3 class="email-heading-h3">What you can do:</h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr class="email-table-row-even">
              <td class="email-order-details-field-first" style="width:18px;padding-right:8px;">•</td>
              <td class="email-order-details-value-bold-first">View your design preview</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field" style="width:18px;padding-right:8px;">•</td>
              <td class="email-order-details-value-bold">Request any changes</td>
            </tr>
            <tr class="email-table-row-even">
              <td class="email-order-details-field" style="width:18px;padding-right:8px;">•</td>
              <td class="email-order-details-value-bold">Approve the design to proceed</td>
            </tr>
          </table>
        </div>
        <p class="email-paragraph-small">
          If everything looks good, you can approve the design directly from the page.
        </p>
        <p class="email-paragraph-small">
          Once approved, we will prepare and send your quotation.
        </p>
      `,
      BROKERAGE_FIRM: `
        <h2 class="email-heading-h2">Hi {{customerName}},</h2>
        <p class="email-paragraph">
          Your design for <strong>{{propertyAddress}}</strong> <strong>{{signType}}</strong> is ready for review.
        </p>
        <p class="email-paragraph">
          Please take a moment to preview the design and let us know if any changes are needed.
        </p>
        <h3 class="email-heading-h3">What you can do:</h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr class="email-table-row-even">
              <td class="email-order-details-field-first" style="width:18px;padding-right:8px;">•</td>
              <td class="email-order-details-value-bold-first">View your design preview</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field" style="width:18px;padding-right:8px;">•</td>
              <td class="email-order-details-value-bold">Request any changes</td>
            </tr>
            <tr class="email-table-row-even">
              <td class="email-order-details-field" style="width:18px;padding-right:8px;">•</td>
              <td class="email-order-details-value-bold">Approve the design to proceed</td>
            </tr>
          </table>
        </div>
        <p class="email-paragraph-small">
          If everything looks good, you can approve the design directly from the page.
        </p>
        <p class="email-paragraph-small">
          Once approved, we will prepare and send your quotation.
        </p>
      `,
    },
    cta: {
      AGENT: { text: "View & Respond", url: "{{reviewUrl}}" },
      BROKERAGE_AGENT: { text: "View & Respond", url: "{{reviewUrl}}" },
      BROKERAGE_TEAM_MEMBER: { text: "View & Respond", url: "{{reviewUrl}}" },
      BROKERAGE_FIRM: { text: "View & Respond", url: "{{reviewUrl}}" },
    },
    footer: "RealEsy. All rights reserved.",
  },
  /** Sent to the project assignee when the client approves a design proof (signage quote / order next step). */
  Design_approved_quote_reminder: {
    subject:
      "Design Approved — Create Quote/Sales Order for Project #{{projectNumber}}",
    title: "Design approved",
    logoUrl: "",
    headerBgColor: "#ffffff",
    bodyLines: [],
    bodyHtml: `
        <h2 class="email-heading-h2">Hi {{assigneeName}},</h2>
        <p class="email-paragraph">
          The design for Project #{{projectNumber}} has been approved.
        </p>
        <p class="email-paragraph">
          Please proceed with creating the quote or sales order for this request.
        </p>
      `,
    cta: { text: "Open Project", url: "{{openProjectUrl}}" },
    footer: "RealEsy. All rights reserved.",
  },

  /** Signage marks proof approved → notify project customer (`customer_email`). */
  Signage_Design_approved_quote_reminder: {
    subject: "Your design has been approved — {{projectNumberSuffix}}",
    title: "Design approved",
    logoUrl: "",
    headerBgColor: "#ffffff",
    bodyLines: [],
    bodyHtml: `
        <h2 class="email-heading-h2">Hi {{firstName}},</h2>
        <p class="email-paragraph">
          Your design proof for <strong>{{projectTitle}}</strong>{{projectNumberLabel}} has been approved by your signage team.
        </p>
        <p class="email-paragraph">
          You can open your project in RealEsy anytime to view details and next steps.
        </p>
      `,
    cta: { text: "View project", url: "{{openProjectUrl}}" },
    footer: "RealEsy. All rights reserved.",
  },
  /** Signage → end user when a project conversation message is sent (unless muted in UI). */
  endusers_conversion: {
    subject: {
      AGENT: "New message on your project — {{projectTitle}}",
      BROKERAGE_AGENT: "New message on your project — {{projectTitle}}",
      BROKERAGE_TEAM_MEMBER: "New message on your project — {{projectTitle}}",
      BROKERAGE_FIRM: "New message on your project — {{projectTitle}}",
    },
    title: "New conversation message",
    logoUrl: "",
    headerBgColor: "#ffffff",
    bodyLines: [],
    bodyHtml: {
      AGENT: `
        <h2 class="email-heading-h2">Hi {{customerName}},</h2>
        <p class="email-paragraph">
          Your signage team sent you a new message regarding <strong>{{projectTitle}}</strong>{{projectNumberLabel}}.
        </p>
        <h3 class="email-heading-h3">Message</h3>
        <div class="email-order-details-card">
          <p class="email-paragraph" style="white-space:pre-wrap;">{{messageBody}}</p>
        </div>
        <h3 class="email-heading-h3">Files</h3>
        <div class="email-order-details-card">
          <p class="email-paragraph" style="white-space:pre-wrap;">{{attachmentList}}</p>
        </div>
        <p class="email-paragraph-small">
          Open your design review portal to read the full thread and reply.
        </p>
      `,
      BROKERAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">Hi {{customerName}},</h2>
        <p class="email-paragraph">
          Your signage team sent you a new message regarding <strong>{{projectTitle}}</strong>{{projectNumberLabel}}.
        </p>
        <h3 class="email-heading-h3">Message</h3>
        <div class="email-order-details-card">
          <p class="email-paragraph" style="white-space:pre-wrap;">{{messageBody}}</p>
        </div>
        <h3 class="email-heading-h3">Files</h3>
        <div class="email-order-details-card">
          <p class="email-paragraph" style="white-space:pre-wrap;">{{attachmentList}}</p>
        </div>
        <p class="email-paragraph-small">
          Open your design review portal to read the full thread and reply.
        </p>
      `,
      BROKERAGE_AGENT: `
        <h2 class="email-heading-h2">Hi {{customerName}},</h2>
        <p class="email-paragraph">
          Your signage team sent you a new message regarding <strong>{{projectTitle}}</strong>{{projectNumberLabel}}.
        </p>
        <h3 class="email-heading-h3">Message</h3>
        <div class="email-order-details-card">
          <p class="email-paragraph" style="white-space:pre-wrap;">{{messageBody}}</p>
        </div>
        <h3 class="email-heading-h3">Files</h3>
        <div class="email-order-details-card">
          <p class="email-paragraph" style="white-space:pre-wrap;">{{attachmentList}}</p>
        </div>
        <p class="email-paragraph-small">
          Open your design review portal to read the full thread and reply.
        </p>
      `,
      BROKERAGE_FIRM: `
        <h2 class="email-heading-h2">Hi {{customerName}},</h2>
        <p class="email-paragraph">
          Your signage team sent you a new message regarding <strong>{{projectTitle}}</strong>{{projectNumberLabel}}.
        </p>
        <h3 class="email-heading-h3">Message</h3>
        <div class="email-order-details-card">
          <p class="email-paragraph" style="white-space:pre-wrap;">{{messageBody}}</p>
        </div>
        <h3 class="email-heading-h3">Files</h3>
        <div class="email-order-details-card">
          <p class="email-paragraph" style="white-space:pre-wrap;">{{attachmentList}}</p>
        </div>
        <p class="email-paragraph-small">
          Open your design review portal to read the full thread and reply.
        </p>
      `,
    },
    cta: {
      AGENT: { text: "View & Respond", url: "{{reviewUrl}}" },
      BROKERAGE_AGENT: { text: "View & Respond", url: "{{reviewUrl}}" },
      BROKERAGE_TEAM_MEMBER: { text: "View & Respond", url: "{{reviewUrl}}" },
      BROKERAGE_FIRM: { text: "View & Respond", url: "{{reviewUrl}}" },
    },
    footer: "RealEsy. All rights reserved.",
  },
  /** Invoice PDF emailed from sales / installation order actions (Brokerage + team + agent). */
  Invoice_for_order: {
    subject: "Invoice for Order #{{orderId}}",
    title: "Invoice",
    logoUrl: "",
    headerBgColor: "#ffffff",
    bodyLines: [],
    bodyHtml: `
        <h2 class="email-heading-h2">Hi {{firstName}},</h2>
        <p class="email-paragraph">
          An invoice has been generated for your order <strong>#{{orderId}}</strong> by <strong>{{vendorName}}</strong>.
          Please find the invoice attached to this email.
        </p>
        <h3 class="email-heading-h3">Invoice Summary</h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr class="email-table-row-odd">
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">#{{orderId}}</td>
            </tr>
            <tr class="email-table-row-even">
              <td class="email-order-details-field">Invoice Number</td>
              <td class="email-order-details-value-bold">{{invoiceNumber}}</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field">Invoice Date</td>
              <td class="email-order-details-value-bold">{{invoiceDate}}</td>
            </tr>
            <tr class="email-table-row-even">
              <td class="email-order-details-field">Amount Due</td>
              <td class="email-order-details-value-bold">{{amountDue}}</td>
            </tr>
            <tr class="email-table-row-odd">
              <td class="email-order-details-field">Due Date</td>
              <td class="email-order-details-value-bold">{{dueDate}}</td>
            </tr>
            <tr class="email-table-row-even">
              <td class="email-order-details-field">Issued By</td>
              <td class="email-order-details-value-bold">{{vendorName}}</td>
            </tr>
          </table>
        </div>
        <p class="email-paragraph">
          Please review the attached invoice and proceed with payment as per the terms.
        </p>
      `,
    cta: {
      SIGNAGE_FIRM: { text: "View Invoice & Pay", url: "{{viewPayUrl}}" },
      SIGNAGE_TEAM_MEMBER: { text: "View Invoice & Pay", url: "{{viewPayUrl}}" },
      BROKERAGE_FIRM: { text: "View Invoice & Pay", url: "{{viewPayUrl}}" },
      BROKERAGE_TEAM_MEMBER: { text: "View Invoice & Pay", url: "{{viewPayUrl}}" },
      AGENT: { text: "View Invoice & Pay", url: "{{viewPayUrl}}" },
    },
    footer: "RealEsy. All rights reserved.",
  },

  Invoice_for_Batch_Payment: {
    subject: "Invoice {{invoice_number}} from {{company_name}}",
    title: "Invoice",
    logoUrl: "",
    headerBgColor: "#ffffff",
    bodyLines: [],
    bodyHtml: `
      <h2 class="email-heading-h2">Hello Team,</h2>

      <p class="email-paragraph">
        Your invoice <strong>{{invoice_number}}</strong> has been prepared and is ready for your review.
      </p>

      <p class="email-paragraph">
        This invoice includes the orders and jobs included in this billing statement.
      </p>

      <p class="email-paragraph">
        You can view the complete invoice and make a payment using the View &amp; Pay Invoice button.
      </p>

      <h3 class="email-heading-h3">Invoice Summary</h3>

      <div class="email-order-details-card">
        <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
          <tr class="email-table-row-odd">
            <td class="email-order-details-field-first">Invoice Number</td>
            <td class="email-order-details-value-bold-first">{{invoice_number}}</td>
          </tr>
          <tr class="email-table-row-even">
            <td class="email-order-details-field">Invoice Date</td>
            <td class="email-order-details-value-bold">{{invoice_date}}</td>
          </tr>
          <tr class="email-table-row-odd">
            <td class="email-order-details-field">Invoice Total</td>
            <td class="email-order-details-value-bold">{{invoice_total}}</td>
          </tr>
          <tr class="email-table-row-even">
            <td class="email-order-details-field">Amount Paid</td>
            <td class="email-order-details-value-bold">{{paid_amount}}</td>
          </tr>
          <tr class="email-table-row-odd">
            <td class="email-order-details-field">Balance Due</td>
            <td class="email-order-details-value-bold">{{remaining_balance}}</td>
          </tr>
        </table>
      </div>

      <p class="email-paragraph">
        If you have any questions regarding this invoice or any of the included orders or jobs, please contact us.
      </p>
    `,
     cta: {
    SIGNAGE_FIRM: { text: "View & Pay Invoice", url: "{{viewPayUrl}}" },
    SIGNAGE_TEAM_MEMBER: { text: "View & Pay Invoice", url: "{{viewPayUrl}}" },
    BROKERAGE_FIRM: { text: "View & Pay Invoice", url: "{{viewPayUrl}}" },
    BROKERAGE_TEAM_MEMBER: { text: "View & Pay Invoice", url: "{{viewPayUrl}}" },
    AGENT: { text: "View & Pay Invoice", url: "{{viewPayUrl}}" },
    BROKERAGE_AGENT: { text: "View & Pay Invoice", url: "{{viewPayUrl}}" },
  },
    footer: "RealEsy. All rights reserved.",
  },


//   <p class="email-paragraph">
//   Thank you,<br />
//   {{company_name}}
// </p>
  // Please review the invoice summary below.
  // cta: {
  //   SIGNAGE_FIRM: {
  //     text: "View Invoice & Pay",
  //     url: "{{viewPayUrl}}"
  //   },
  //   SIGNAGE_TEAM_MEMBER: {
  //     text: "View Invoice & Pay",
  //     url: "{{viewPayUrl}}"
  //   },
  //   BROKERAGE_FIRM: {
  //     text: "View Invoice & Pay",
  //     url: "{{viewPayUrl}}"
  //   },
  //   BROKERAGE_TEAM_MEMBER: {
  //     text: "View Invoice & Pay",
  //     url: "{{viewPayUrl}}"
  //   },
  //   AGENT: {
  //     text: "View Invoice & Pay",
  //     url: "{{viewPayUrl}}"
  //   },
  // },
  // Merge realtor email triggers
  ...(() => {
    try {
      // Dynamic import to avoid circular dependencies
      const { realtorEmailTriggers } = require("./RealtorEmailConfig");
      return realtorEmailTriggers || {};
    } catch {
      return {};
    }
  })(),
  // Merge agent email triggers
  ...(() => {
    try {
      // Dynamic import to avoid circular dependencies
      const { agentEmailTriggers } = require("./agentsemailconfig");
      return agentEmailTriggers || {};
    } catch {
      return {};
    }
  })(),
};




// export const triggerConfig: Record<string, TriggerConfig> = {
//   // … your other triggers …

//   "User Registration": {
//     subject: " Welcome to Rea!",
//     title: "Thanks for signing up !",
//     logoUrl: "https://example.com/logo.png",
//     bodyLines: [
//       "Hi {{userName}},",
//       "Your account has been created successfully.",
//       "Click below to start exploring:"
//     ],
//     cta: { text: "Go to Dashboard", url: "https://app.example.com/dashboard" },
//     footer: "© 2025 MyApp Inc. All rights reserved."
//   },
// };
